// import removed (duplicate)
// --- IMPORTS ---
import { generateSampleSalarySlip } from './sample_salary_slip';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  getAuth,
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  User
} from 'firebase/auth';
 
import {
  getFirestore,
  collection,
  addDoc,
  query,
  onSnapshot,
  orderBy,
  serverTimestamp,
  limit,
  doc,
  updateDoc,
  where,
  getDocs,
  deleteDoc,
  setDoc,
  getDoc,
  writeBatch
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';

import {
  MapPin,
  Users,
  Building,
  FileText,
  LogOut,
  Upload,
  Plus,
  RefreshCw,
  CheckCircle,
  Clock,
  Menu,
  X,
  Download,
  UserPlus,
  Map as MapIcon,
  Link,
  ExternalLink,
  Calendar,
  FileSpreadsheet,
  Bell,
  Camera,
  CheckSquare,
  AlertTriangle,
  Pencil,
  Lock,
  Mail,
  Fingerprint,
  RotateCcw,
  XCircle,
  Search,
  FileCheck,
  Truck,
  UserCheck
} from 'lucide-react';

import { MapContainer, TileLayer, CircleMarker, Popup, Polyline, useMap } from 'react-leaflet';
import type { LatLngTuple } from 'leaflet';


// SAMPLE SALARY SLIP BUTTON COMPONENT
function SampleSalarySlipButton() {
  return (
    <div className="bg-white p-6 rounded-xl shadow border max-w-lg mx-auto flex flex-col items-center">
      <h2 className="text-lg font-bold mb-2">Preview Sample Salary Slip</h2>
      <button
        onClick={generateSampleSalarySlip}
        className="bg-indigo-600 text-white px-4 py-2 rounded font-bold hover:bg-indigo-700 transition"
      >
        Download Sample PDF
      </button>
      <div className="text-xs text-slate-500 mt-2">This is a preview using your provided pay heads and layout.</div>
    </div>
  );
}

// --- PLACEHOLDER COMPONENTS (remove if real ones exist) ---
function AdminLocationTrails({ profile }: { profile: UserProfile }) {
  type TrailPoint = {
    id: string;
    at: Date;
    lat: number;
    lng: number;
    locationText?: string;
    type?: string;
    deviceType?: string;
  };

  const toDateSafe = (value: any): Date | null => {
    try {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value?.toDate === 'function') return value.toDate();
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  const [empId, setEmpId] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [points, setPoints] = useState<TrailPoint[]>([]);

  const FitToPoints = ({ pts }: { pts: TrailPoint[] }) => {
    const map = useMap();
    useEffect(() => {
      const coords = pts
        .map((p) => [p.lat, p.lng] as LatLngTuple)
        .filter((c) => Number.isFinite(c[0]) && Number.isFinite(c[1]));
      if (coords.length === 0) return;
      if (coords.length === 1) {
        map.setView(coords[0], 15);
        return;
      }
      map.fitBounds(coords, { padding: [24, 24] });
    }, [map, pts]);
    return null;
  };

  const loadTrail = useCallback(async () => {
    setError('');
    setLoading(true);
    setPoints([]);
    try {
      const normalizedEmpId = String(empId || '').trim().toUpperCase();
      if (!normalizedEmpId) {
        setError('Please enter an Employee ID (EmpID).');
        return;
      }

      const start = new Date(`${date}T00:00:00.000`);
      const end = new Date(`${date}T23:59:59.999`);

      const userSnap = await getDocs(
        query(
          collection(db, 'artifacts', appId, 'public', 'data', 'users'),
          where('empId', '==', normalizedEmpId),
          limit(1)
        )
      );

      if (userSnap.empty) {
        setError(`No user found for EmpID: ${normalizedEmpId}`);
        return;
      }

      const userId = userSnap.docs[0].id;
      const qAtt = query(
        collection(db, 'artifacts', appId, 'public', 'data', 'attendance'),
        where('userId', '==', userId),
        where('timestamp', '>=', start),
        where('timestamp', '<=', end),
        orderBy('timestamp', 'asc')
      );
      const attSnap = await getDocs(qAtt);
      const raw = attSnap.docs.map((d) => ({ id: d.id, ...d.data() } as any));

      const pts: TrailPoint[] = raw
        .map((r) => {
          const at = toDateSafe(r.timestamp);
          const lat = Number(r.latitude);
          const lng = Number(r.longitude);
          if (!at || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          if (lat === 0 && lng === 0) return null;
          return {
            id: r.id,
            at,
            lat,
            lng,
            locationText: r.locationText,
            type: r.type,
            deviceType: r.deviceType,
          } as TrailPoint;
        })
        .filter(Boolean) as TrailPoint[];

      setPoints(pts);
      if (pts.length === 0) {
        setError('No GPS trail points found for the selected date (only points with valid lat/lng are shown).');
      }
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Failed to load location trail.');
    } finally {
      setLoading(false);
    }
  }, [date, empId]);

  return (
    <div className="bg-white p-6 rounded-xl shadow border border-slate-200">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Location Trails</h2>
          <p className="text-xs text-slate-500">Loads GPS points from Attendance records for the selected employee and date.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="px-3 py-2 border rounded-lg text-sm"
            placeholder="EmpID (e.g., AI1001)"
            value={empId}
            onChange={(e) => setEmpId(e.target.value)}
          />
          <input
            className="px-3 py-2 border rounded-lg text-sm"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <button
            onClick={loadTrail}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Load'}
          </button>
        </div>
      </div>

      {error && <div className="mb-4 bg-amber-50 border border-amber-100 text-amber-800 text-sm p-3 rounded-lg">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div className="h-[420px] rounded-xl overflow-hidden border border-slate-200">
            <MapContainer center={[20.5937, 78.9629]} zoom={5} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />
              <FitToPoints pts={points} />
              {points.length >= 2 && (
                <Polyline
                  positions={points.map((p) => [p.lat, p.lng] as LatLngTuple)}
                  pathOptions={{ color: '#4f46e5', weight: 4, opacity: 0.8 }}
                />
              )}
              {points.map((p, idx) => (
                <CircleMarker
                  key={p.id}
                  center={[p.lat, p.lng]}
                  radius={idx === 0 || idx === points.length - 1 ? 8 : 5}
                  pathOptions={{ color: idx === 0 ? '#16a34a' : idx === points.length - 1 ? '#dc2626' : '#2563eb' }}
                >
                  <Popup>
                    <div className="text-sm">
                      <div className="font-semibold">{p.at.toLocaleString()}</div>
                      <div className="text-xs text-slate-600">{p.locationText || `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        {p.type ? `Punch: ${p.type}` : ''} {p.deviceType ? ` · ${p.deviceType}` : ''}
                      </div>
                      <a
                        className="text-indigo-600 text-xs underline"
                        href={`https://www.google.com/maps?q=${p.lat},${p.lng}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open in Google Maps
                      </a>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
          <div className="text-xs text-slate-400 mt-2">Green = first point, Red = last point.</div>
        </div>

        <div className="bg-slate-50 rounded-xl border border-slate-200 p-3 overflow-auto max-h-[420px]">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-slate-800 text-sm">Trail Points</div>
            <div className="text-xs text-slate-500">{points.length} points</div>
          </div>
          {points.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-10">No points loaded.</div>
          ) : (
            <div className="space-y-2">
              {points.map((p, idx) => (
                <div key={p.id} className="bg-white rounded-lg border border-slate-200 p-2">
                  <div className="flex justify-between gap-2">
                    <div className="text-xs font-semibold text-slate-800">#{idx + 1}</div>
                    <div className="text-[11px] text-slate-500">{p.at.toLocaleTimeString()}</div>
                  </div>
                  <div className="text-[11px] text-slate-600 mt-1">{p.locationText || '—'}</div>
                  <div className="text-[11px] text-slate-500 font-mono mt-1">{p.lat.toFixed(5)}, {p.lng.toFixed(5)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
function AdminTravelStops({ profile }: { profile: UserProfile }) {
  type TrailPoint = {
    id: string;
    at: Date;
    lat: number;
    lng: number;
    locationText?: string;
  };
  type Stop = {
    start: Date;
    end: Date;
    minutes: number;
    lat: number;
    lng: number;
    locationText?: string;
    pointsCount: number;
  };

  const toDateSafe = (value: any): Date | null => {
    try {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value?.toDate === 'function') return value.toDate();
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  const haversineMeters = useCallback((a: TrailPoint, b: TrailPoint) => {
    const R = 6371000;
    const toRad = (x: number) => (x * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    return R * c;
  }, []);

  const computeStops = useCallback((pts: TrailPoint[], radiusMeters: number, minStopMinutes: number): Stop[] => {
    if (pts.length < 2) return [];
    const stops: Stop[] = [];
    let bucket: TrailPoint[] = [pts[0]];

    const flush = () => {
      if (bucket.length < 2) {
        bucket = [];
        return;
      }
      const start = bucket[0].at;
      const end = bucket[bucket.length - 1].at;
      const minutes = (end.getTime() - start.getTime()) / 60000;
      if (minutes >= minStopMinutes) {
        const mid = bucket[Math.floor(bucket.length / 2)];
        stops.push({
          start,
          end,
          minutes: Math.round(minutes),
          lat: mid.lat,
          lng: mid.lng,
          locationText: mid.locationText,
          pointsCount: bucket.length,
        });
      }
      bucket = [];
    };

    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1];
      const cur = pts[i];
      const dist = haversineMeters(prev, cur);
      if (dist <= radiusMeters) {
        if (bucket.length === 0) bucket.push(prev);
        bucket.push(cur);
      } else {
        flush();
      }
    }
    flush();
    return stops;
  }, [haversineMeters]);

  const [empId, setEmpId] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [radiusMeters, setRadiusMeters] = useState(120);
  const [minMinutes, setMinMinutes] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stops, setStops] = useState<Stop[]>([]);

  const loadStops = useCallback(async () => {
    setError('');
    setStops([]);
    setLoading(true);
    try {
      const normalizedEmpId = String(empId || '').trim().toUpperCase();
      if (!normalizedEmpId) {
        setError('Please enter an Employee ID (EmpID).');
        return;
      }

      const start = new Date(`${date}T00:00:00.000`);
      const end = new Date(`${date}T23:59:59.999`);

      const userSnap = await getDocs(
        query(
          collection(db, 'artifacts', appId, 'public', 'data', 'users'),
          where('empId', '==', normalizedEmpId),
          limit(1)
        )
      );
      if (userSnap.empty) {
        setError(`No user found for EmpID: ${normalizedEmpId}`);
        return;
      }

      const userId = userSnap.docs[0].id;
      const qAtt = query(
        collection(db, 'artifacts', appId, 'public', 'data', 'attendance'),
        where('userId', '==', userId),
        where('timestamp', '>=', start),
        where('timestamp', '<=', end),
        orderBy('timestamp', 'asc')
      );
      const attSnap = await getDocs(qAtt);
      const pts: TrailPoint[] = attSnap.docs
        .map((d) => ({ id: d.id, ...d.data() } as any))
        .map((r) => {
          const at = toDateSafe(r.timestamp);
          const lat = Number(r.latitude);
          const lng = Number(r.longitude);
          if (!at || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          if (lat === 0 && lng === 0) return null;
          return {
            id: r.id,
            at,
            lat,
            lng,
            locationText: r.locationText,
          } as TrailPoint;
        })
        .filter(Boolean) as TrailPoint[];

      const computed = computeStops(pts, Number(radiusMeters) || 120, Number(minMinutes) || 10);
      setStops(computed);
      if (pts.length === 0) {
        setError('No GPS points found for this date; cannot compute stops.');
      } else if (computed.length === 0) {
        setError('No stops detected with the current thresholds. Try reducing the minimum minutes or increasing radius.');
      }
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Failed to load travel stops.');
    } finally {
      setLoading(false);
    }
  }, [date, empId, minMinutes, radiusMeters, computeStops]);

  return (
    <div className="bg-white p-6 rounded-xl shadow border border-slate-200">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Travel Stops</h2>
          <p className="text-xs text-slate-500">Detects stops by grouping nearby GPS points from Attendance records.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="px-3 py-2 border rounded-lg text-sm"
            placeholder="EmpID (e.g., AI1001)"
            value={empId}
            onChange={(e) => setEmpId(e.target.value)}
          />
          <input
            className="px-3 py-2 border rounded-lg text-sm"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <input
            className="px-3 py-2 border rounded-lg text-sm w-24"
            type="number"
            min={20}
            step={10}
            value={radiusMeters}
            onChange={(e) => setRadiusMeters(Number(e.target.value))}
            title="Radius (meters)"
          />
          <input
            className="px-3 py-2 border rounded-lg text-sm w-24"
            type="number"
            min={1}
            step={1}
            value={minMinutes}
            onChange={(e) => setMinMinutes(Number(e.target.value))}
            title="Minimum stop duration (minutes)"
          />
          <button
            onClick={loadStops}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Detect'}
          </button>
        </div>
      </div>

      {error && <div className="mb-4 bg-amber-50 border border-amber-100 text-amber-800 text-sm p-3 rounded-lg">{error}</div>}

      {stops.length === 0 ? (
        <div className="text-sm text-slate-400 text-center py-10">No stops to show.</div>
      ) : (
        <div className="overflow-auto border border-slate-200 rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Stop</th>
                <th className="text-left px-3 py-2">Duration</th>
                <th className="text-left px-3 py-2">From</th>
                <th className="text-left px-3 py-2">To</th>
                <th className="text-left px-3 py-2">Location</th>
                <th className="text-left px-3 py-2">Map</th>
              </tr>
            </thead>
            <tbody>
              {stops.map((s, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-semibold text-slate-800">#{idx + 1}</td>
                  <td className="px-3 py-2">{s.minutes} min ({s.pointsCount} pts)</td>
                  <td className="px-3 py-2 text-slate-600">{s.start.toLocaleTimeString()}</td>
                  <td className="px-3 py-2 text-slate-600">{s.end.toLocaleTimeString()}</td>
                  <td className="px-3 py-2 text-slate-600">
                    <div className="text-xs">{s.locationText || '—'}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{s.lat.toFixed(5)}, {s.lng.toFixed(5)}</div>
                  </td>
                  <td className="px-3 py-2">
                    <a
                      href={`https://www.google.com/maps?q=${s.lat},${s.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 text-xs underline"
                    >
                      Open
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const normalizeEmpIdForSlip = (value: string) => String(value ?? '').trim().toUpperCase();

const monthLabelFromKeyForSlip = (key: string) => {
  const [y, m] = String(key ?? '').split('-');
  const year = Number(y);
  const monthIndex = Number(m) - 1;
  const d = new Date(year, monthIndex, 1);
  const monthName = d.toLocaleString('en-US', { month: 'long' });
  return `${monthName}-${year}`;
};

const extractEmpIdFromFilenameForSlip = (fileName: string) => {
  const base = String(fileName ?? '').replace(/\.pdf$/i, '');
  const firstToken = base.trim().split(/\s+/)[0] || '';
  const monthNames = new Set([
    'jan',
    'january',
    'feb',
    'february',
    'mar',
    'march',
    'apr',
    'april',
    'may',
    'jun',
    'june',
    'jul',
    'july',
    'aug',
    'august',
    'sep',
    'sept',
    'september',
    'oct',
    'october',
    'nov',
    'november',
    'dec',
    'december',
  ]);

  const stripCommonMonthSuffix = (token: string) => {
    const t = String(token || '').trim();
    if (!t) return '';

    // Pattern: EMP001_Oct-2025, A1798_NOV-2025
    let m = t.match(/^(.+?)[_-]([A-Za-z]{3,9})[-_](\d{4})$/);
    if (m && monthNames.has(String(m[2]).toLowerCase())) return m[1];

    // Pattern: EMP001_2025-11
    m = t.match(/^(.+?)[_-](\d{4})[-_](\d{1,2})$/);
    if (m) {
      const mm = Number(m[3]);
      const yy = Number(m[2]);
      if (yy >= 2000 && yy <= 2100 && mm >= 1 && mm <= 12) return m[1];
    }

    // Pattern: EMP001_11-2025
    m = t.match(/^(.+?)[_-](\d{1,2})[-_](\d{4})$/);
    if (m) {
      const mm = Number(m[2]);
      const yy = Number(m[3]);
      if (yy >= 2000 && yy <= 2100 && mm >= 1 && mm <= 12) return m[1];
    }

    return t;
  };

  const token = stripCommonMonthSuffix(firstToken);

  // Accept common empId formats like EMP-001, TL_12, 1001
  const match = token.match(/^[A-Za-z0-9][A-Za-z0-9_-]*/);
  return normalizeEmpIdForSlip(match?.[0] ?? '');
};

function AdminSalarySlipBulkPdfUpload({ profile }: { profile: UserProfile }) {
  const [monthKey, setMonthKey] = useState('');
  const [maxPdfKb, setMaxPdfKb] = useState(400);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [tableLoading, setTableLoading] = useState(false);
  const [previewRows, setPreviewRows] = useState<any[]>([]);
  const [uploadedRows, setUploadedRows] = useState<any[]>([]);

  const isAdmin = profile.role === 'admin';

  // Payroll months: allow back months only (exclude the current month).
  // Example: if today is Feb-2026, allow Jan-2026 back to Aug-2025 (6 months).
  const getAllowedMonthKeys = (count: number) => {
    const now = new Date();
    const base = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const keys: string[] = [];
    for (let i = 0; i < count; i++) {
      const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      keys.push(`${y}-${m}`);
    }
    return keys;
  };

  const allowedMonthKeys = getAllowedMonthKeys(6);
  const isAllowedMonth = monthKey ? allowedMonthKeys.includes(monthKey) : false;

  const getEmpIdCandidatesFromFilenameForSlip = (fileName: string) => {
    const base = String(fileName ?? '').replace(/\.pdf$/i, '').trim();
    const firstToken = base.split(/\s+/)[0] || '';

    const candidates = new Set<string>();
    const primary = extractEmpIdFromFilenameForSlip(fileName);
    if (primary) candidates.add(primary);

    const normalizedFirstToken = normalizeEmpIdForSlip(firstToken);
    if (normalizedFirstToken) candidates.add(normalizedFirstToken);

    // Common patterns: EMPID_OCT-2025_anything.pdf => EMPID
    if (normalizedFirstToken.includes('_')) {
      const c = normalizeEmpIdForSlip(normalizedFirstToken.split('_')[0]);
      if (c) candidates.add(c);
    }
    // Common patterns: EMPID-OCT-2025-anything.pdf => EMPID
    if (normalizedFirstToken.includes('-')) {
      const c = normalizeEmpIdForSlip(normalizedFirstToken.split('-')[0]);
      if (c) candidates.add(c);
    }

    return Array.from(candidates);
  };


  const handlePickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage('');
    const files = Array.from(e.target.files ?? []);
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    setMessage('');
    if (!monthKey) {
      setMessage('Please select the Month (e.g., 2025-10).');
      return;
    }
    if (!isAllowedMonth) {
      setMessage(`Only last 6 months are allowed: ${allowedMonthKeys.join(', ')}.`);
      return;
    }
    if (selectedFiles.length === 0) {
      setMessage('Please select one or more PDF files.');
      return;
    }

    const monthLabel = monthLabelFromKeyForSlip(monthKey);
    const files = selectedFiles.slice(0, 50);

    if (selectedFiles.length > 50) {
      setMessage('Selected more than 50 PDFs. Only the first 50 will be uploaded.');
    }

    setUploading(true);
    try {
      const db = getFirestore();
      const usersCol = collection(db, 'artifacts', appId, 'public', 'data', 'users');
      const salarySlipsCol = collection(db, 'artifacts', appId, 'public', 'data', 'salarySlips');
      const storage = getStorage();
      const uploadedByUid = getAuth().currentUser?.uid || null;

      // Build empId -> userId map (so employees can see slips by auth uid)
      const usersSnap = await getDocs(usersCol);
      const empIdToUserId = new Map<string, string>();
      usersSnap.forEach((d) => {
        const empId = normalizeEmpIdForSlip(String((d.data() as any)?.empId ?? ''));
        if (empId) empIdToUserId.set(empId, d.id);
      });

      const maxBytes = Math.max(1, Number(maxPdfKb) || 0) * 1024;

      let uploadedCount = 0;
      let tooLargeCount = 0;
      let invalidNameCount = 0;
      let missingUserCount = 0;
      let nonPdfCount = 0;
      let errorCount = 0;

      for (const f of files) {
        try {
          if (!/\.pdf$/i.test(f.name)) {
            nonPdfCount++;
            continue;
          }
          if (f.size > maxBytes) {
            tooLargeCount++;
            continue;
          }

          const empIdCandidates = getEmpIdCandidatesFromFilenameForSlip(f.name);
          if (empIdCandidates.length === 0) {
            invalidNameCount++;
            continue;
          }

          const empId = empIdCandidates.find((c) => empIdToUserId.has(c)) || empIdCandidates[0];
          const userId = empIdToUserId.get(empId);
          if (!userId) {
            missingUserCount++;
            continue;
          }

          const filePath = `salary_slips/${monthKey}/${empId}.pdf`;
          const fileRef = ref(storage, filePath);
          await uploadBytes(fileRef, f);
          const fileUrl = await getDownloadURL(fileRef);

          const slipDocId = `${userId}_${monthKey}`;
          await setDoc(
            doc(salarySlipsCol, slipDocId),
            {
              userId,
              empId,
              month: monthLabel,
              monthKey,
              filePath,
              fileUrl,
              createdAt: serverTimestamp(),
              source: 'web-bulk-pdf',
              uploadedByUid,
            },
            { merge: true }
          );

          uploadedCount++;
        } catch {
          errorCount++;
        }
      }

      const parts = [
        `Uploaded ${uploadedCount} salary slip(s) for ${monthLabel}.`,
        missingUserCount ? `Missing user mapping (empId not found in users): ${missingUserCount}.` : '',
        invalidNameCount ? `Invalid file name (could not extract empId): ${invalidNameCount}.` : '',
        tooLargeCount ? `Too large (> ${maxPdfKb} KB): ${tooLargeCount}.` : '',
        nonPdfCount ? `Non-PDF skipped: ${nonPdfCount}.` : '',
        errorCount ? `Errors: ${errorCount}.` : '',
      ].filter(Boolean);

      setMessage(parts.join(' '));

      // Refresh table after upload
      try {
        await loadSalarySlipTables(monthKey, selectedFiles);
      } catch {
        // ignore refresh errors
      }
    } catch {
      setMessage('Failed to upload salary slips.');
    } finally {
      setUploading(false);
    }
  };

  const loadSalarySlipTables = useCallback(async (mk: string, files: File[]) => {
    if (!mk) {
      setPreviewRows([]);
      setUploadedRows([]);
      return;
    }

    setTableLoading(true);
    try {
      const db = getFirestore();
      const usersCol = collection(db, 'artifacts', appId, 'public', 'data', 'users');
      const salarySlipsCol = collection(db, 'artifacts', appId, 'public', 'data', 'salarySlips');

      const usersSnap = await getDocs(usersCol);
      const empIdToUser = new Map<string, { uid: string; name: string; empId: string }>();
      const uidToUser = new Map<string, { uid: string; name: string; empId: string }>();
      usersSnap.forEach((d) => {
        const raw = d.data() as any;
        const empId = normalizeEmpIdForSlip(String(raw?.empId ?? ''));
        const name = String(raw?.name ?? '').trim();
        const user = { uid: d.id, name, empId };
        uidToUser.set(d.id, user);
        if (empId) empIdToUser.set(empId, user);
      });

      // Existing slips for this month (no orderBy to avoid composite index requirement)
      const slipsSnap = await getDocs(query(salarySlipsCol, where('monthKey', '==', mk)));
      const existingDocIds = new Set<string>(slipsSnap.docs.map(d => d.id));

      // Uploaded slips table rows
      const uploaded = slipsSnap.docs
        .map((d) => ({ id: d.id, ...(d.data() as any) }))
        .map((s: any) => {
          const user = uidToUser.get(String(s.userId ?? ''));
          const empId = normalizeEmpIdForSlip(String(s.empId ?? user?.empId ?? ''));
          const employeeName = String(user?.name ?? '').trim();
          const month = String(s.month ?? monthLabelFromKeyForSlip(mk));
          const fp = String(s.filePath ?? '');
          const slipName = fp ? fp.split('/').pop() : '';
          const createdAtMs = s.createdAt?.toDate?.()?.getTime?.() ?? 0;
          return {
            month,
            empId,
            employeeName,
            slipName: slipName || `${empId || 'Slip'}.pdf`,
            uploaded: true,
            createdAtMs,
          };
        })
        .sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));

      // Preview rows (selected PDFs)
      const month = monthLabelFromKeyForSlip(mk);
      const maxBytes = Math.max(1, Number(maxPdfKb) || 0) * 1024;
      const preview = (files || []).map((f) => {
        const slipName = f.name;
        if (!/\.pdf$/i.test(f.name)) {
          return { month, empId: '', employeeName: '', slipName, uploaded: false, issue: 'Not a PDF' };
        }

        if (f.size > maxBytes) {
          return {
            month,
            empId: '',
            employeeName: '',
            slipName,
            uploaded: false,
            issue: `Too large (> ${maxPdfKb} KB)`,
          };
        }

        const empIdCandidates = getEmpIdCandidatesFromFilenameForSlip(f.name);
        if (empIdCandidates.length === 0) {
          return { month, empId: '', employeeName: '', slipName, uploaded: false, issue: 'Invalid filename' };
        }

        const resolvedEmpId = empIdCandidates.find((c) => empIdToUser.has(c)) || empIdCandidates[0];
        const user = empIdToUser.get(resolvedEmpId);
        if (!user) {
          return { month, empId: resolvedEmpId, employeeName: '', slipName, uploaded: false, issue: 'Employee not found (empId not in users)' };
        }

        const docId = `${user.uid}_${mk}`;
        const alreadyUploaded = existingDocIds.has(docId);
        return {
          month,
          empId: resolvedEmpId,
          employeeName: user.name,
          slipName,
          uploaded: alreadyUploaded,
          issue: alreadyUploaded ? '' : '',
        };
      });

      setUploadedRows(uploaded);
      setPreviewRows(preview);
    } finally {
      setTableLoading(false);
    }
  }, [maxPdfKb]);

  useEffect(() => {
    if (!isAdmin) return;
    if (!monthKey) {
      setPreviewRows([]);
      setUploadedRows([]);
      return;
    }
    // Load table when month changes; also updates when file selection changes.
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadSalarySlipTables(monthKey, selectedFiles);
  }, [isAdmin, monthKey, selectedFiles, loadSalarySlipTables]);

  if (!isAdmin) {
    return (
      <div className="bg-white p-6 rounded-xl shadow border max-w-2xl mx-auto">
        <h2 className="text-xl font-bold">Bulk Upload Salary Slips (PDF)</h2>
        <div className="text-slate-500 text-sm mt-2">Only Admin/HR can upload salary slips.</div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-xl shadow border max-w-2xl mx-auto mt-8">
      <div className="flex items-center gap-2 mb-2">
        <Upload className="w-6 h-6 text-indigo-600" />
        <h2 className="text-xl font-bold">Bulk Upload Salary Slips (PDF)</h2>
      </div>
      <div className="text-xs text-slate-500 mb-4">
        Upload up to 50 PDFs per batch. Month is shown to employees as <span className="font-semibold">October-2025</span>.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Month (last 6 only)</label>
          <input
            type="month"
            className="w-full border rounded p-2"
            value={monthKey}
            onChange={(e) => setMonthKey(e.target.value)}
            disabled={uploading}
            max={allowedMonthKeys[0]}
            min={allowedMonthKeys[allowedMonthKeys.length - 1]}
          />
          <div className="text-[11px] text-slate-400 mt-1">Allowed: {allowedMonthKeys.join(', ')}</div>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Max PDF size (KB)</label>
          <input
            type="number"
            min={1}
            className="w-full border rounded p-2"
            value={maxPdfKb}
            onChange={(e) => setMaxPdfKb(Number(e.target.value) || 0)}
            disabled={uploading}
          />
          <div className="text-[11px] text-slate-400 mt-1">Default is 400 KB</div>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1">PDF Files</label>
          <input
            type="file"
            accept="application/pdf,.pdf"
            multiple
            onChange={handlePickFiles}
            disabled={uploading}
            className="w-full"
          />
          <div className="text-[11px] text-slate-400 mt-1">Selected: {selectedFiles.length}</div>
        </div>
      </div>

      {!isAllowedMonth && monthKey && (
        <div className="mt-3 text-sm text-red-600">
          Only last 6 months are allowed.
        </div>
      )}

      <div className="mt-4 flex gap-3">
        <button
          onClick={handleUpload}
          disabled={uploading || !isAllowedMonth || !monthKey || selectedFiles.length === 0}
          className="bg-indigo-600 text-white px-4 py-2 rounded font-bold hover:bg-indigo-700 transition disabled:opacity-50"
        >
          {uploading ? 'Uploading...' : 'Upload PDFs'}
        </button>
        <button
          onClick={() => {
            setSelectedFiles([]);
            setMessage('');
          }}
          disabled={uploading}
          className="border px-4 py-2 rounded font-bold hover:bg-slate-50 transition disabled:opacity-50"
        >
          Clear
        </button>
      </div>

      {message && <div className="mt-3 text-sm text-slate-700">{message}</div>}

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-700">Salary Slips Table</h3>
          <div className="text-xs text-slate-400">
            {tableLoading ? 'Loading…' : monthKey ? `Month: ${monthLabelFromKeyForSlip(monthKey)}` : 'Select a month to view'}
          </div>
        </div>

        {/* Preview table for selected PDFs */}
        {monthKey && selectedFiles.length > 0 && (
          <div className="mt-3">
            <div className="text-xs font-semibold text-slate-600 mb-2">Selected PDFs</div>
            <div className="overflow-auto border rounded">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Month</th>
                    <th className="px-3 py-2 text-left">Employee Id</th>
                    <th className="px-3 py-2 text-left">Employee Name</th>
                    <th className="px-3 py-2 text-left">Salary Slip Name</th>
                    <th className="px-3 py-2 text-left">Uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((r: any, idx: number) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2">{r.month || '-'}</td>
                      <td className="px-3 py-2">{r.empId || '-'}</td>
                      <td className="px-3 py-2">{r.employeeName || '-'}</td>
                      <td className="px-3 py-2">{r.slipName || '-'}</td>
                      <td className="px-3 py-2">
                        {r.issue ? (
                          <span className="text-amber-700">No — {r.issue}</span>
                        ) : r.uploaded ? (
                          <span className="text-green-700 font-semibold">Yes</span>
                        ) : (
                          <span className="text-red-700 font-semibold">No</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Uploaded slips for selected month */}
        {monthKey && (
          <div className="mt-5">
            <div className="text-xs font-semibold text-slate-600 mb-2">Uploaded Slips (Selected Month)</div>
            {uploadedRows.length === 0 ? (
              <div className="text-xs text-slate-500">No uploaded salary slips found for this month.</div>
            ) : (
              <div className="overflow-auto border rounded">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Month</th>
                      <th className="px-3 py-2 text-left">Employee Id</th>
                      <th className="px-3 py-2 text-left">Employee Name</th>
                      <th className="px-3 py-2 text-left">Salary Slip Name</th>
                      <th className="px-3 py-2 text-left">Uploaded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedRows.map((r: any, idx: number) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2">{r.month || '-'}</td>
                        <td className="px-3 py-2">{r.empId || '-'}</td>
                        <td className="px-3 py-2">{r.employeeName || '-'}</td>
                        <td className="px-3 py-2">{r.slipName || '-'}</td>
                        <td className="px-3 py-2"><span className="text-green-700 font-semibold">Yes</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 text-xs text-slate-500">
        File naming: empId should be the first token in filename (e.g., <span className="font-mono">EMP001.pdf</span> or <span className="font-mono">EMP001_Oct-2025.pdf</span>).
      </div>
    </div>
  );
}

// Remove all import statements that appear after this block throughout the file.
// Ensure all components are defined only once and before use.
// ...existing code (interfaces, types, and components) at the top level, not nested, and in correct order...
// LIVE LOCATION TYPES
interface LiveLocation {
  id: string;
  userId: string;
  userName: string;
  latitude: number;
  longitude: number;
  updatedAt: any;
}

// ADMIN/MANAGER LIVE LOCATIONS VIEW COMPONENT
function AdminLiveLocations({ profile }: { profile: UserProfile }) {
  const [locations, setLocations] = useState<LiveLocation[]>([]);

  useEffect(() => {
    const q = collection(db, 'artifacts', appId, 'public', 'data', 'live_locations');
    const unsub = onSnapshot(q, (snap) => {
      setLocations(snap.docs.map(d => ({ id: d.id, ...d.data() })) as LiveLocation[]);
    });
    return () => unsub();
  }, []);

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow border max-w-3xl mx-auto">
        <h2 className="text-xl font-bold mb-4">Live Locations</h2>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2">Employee</th>
              <th className="px-3 py-2">Latitude</th>
              <th className="px-3 py-2">Longitude</th>
              <th className="px-3 py-2">Last Updated</th>
              <th className="px-3 py-2">Map</th>
            </tr>
          </thead>
          <tbody>
            {locations.map(loc => (
              <tr key={loc.id} className="border-b">
                <td className="px-3 py-2">{loc.userName}</td>
                <td className="px-3 py-2">{loc.latitude}</td>
                <td className="px-3 py-2">{loc.longitude}</td>
                <td className="px-3 py-2">{loc.updatedAt?.toDate?.().toLocaleString?.() || 'N/A'}</td>
                <td className="px-3 py-2">
                  <a href={`https://maps.google.com/?q=${loc.latitude},${loc.longitude}`} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 underline">View</a>
                </td>
              </tr>
            ))}
            {locations.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-4">No live locations found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
// DUTY STATE TYPES
interface DutyState {
  id: string;
  userId: string;
  userName: string;
  state: string;
  updatedAt: any;
}

// ADMIN/MANAGER DUTY STATE VIEW COMPONENT
function AdminDutyStates({ profile }: { profile: UserProfile }) {
  const [dutyStates, setDutyStates] = useState<DutyState[]>([]);
  const [userProfiles, setUserProfiles] = useState<Record<string, UserProfile>>({});
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    const q = collection(db, 'artifacts', appId, 'public', 'data', 'duty_state');
    const unsub = onSnapshot(q, (snap) => {
      setDutyStates(snap.docs.map(d => ({ id: d.id, ...d.data() })) as DutyState[]);
    });
    const unsubUsers = onSnapshot(
      collection(db, 'artifacts', appId, 'public', 'data', 'users'),
      (snap) => {
        const map: Record<string, UserProfile> = {};
        snap.docs.forEach((d) => {
          map[d.id] = { id: d.id, ...(d.data() as any) } as UserProfile;
        });
        setUserProfiles(map);
      }
    );
    return () => {
      unsub();
      unsubUsers();
    };
  }, []);

  const managerTeamUserIds = useMemo(() => {
    if (profile?.role !== 'manager') return null;
    return buildManagerTeamUserIdSet(profile, userProfiles);
  }, [profile, userProfiles]);

  const rows = useMemo(() => {
    const toUpper = (v: any) => String(v ?? '').trim().toUpperCase();
    const search = toUpper(searchText);

    const allEmployees = Object.values(userProfiles || {}).filter((u) => {
      if (u.status && u.status !== 'Active') return false;
      if (profile?.role === 'manager') {
        if (!managerTeamUserIds) return false;
        return managerTeamUserIds.has(u.id);
      }
      return true;
    });

    // Map duty_state by userId (docId is usually the userId)
    const dsByUserId = new Map<string, DutyState>();
    (dutyStates || []).forEach((ds) => {
      const uid = String(ds.userId || ds.id || '').trim();
      if (!uid) return;
      // Keep latest updatedAt if duplicates exist
      const existing = dsByUserId.get(uid);
      const tNew = ds.updatedAt?.toDate?.()?.getTime?.() ?? 0;
      const tOld = existing?.updatedAt?.toDate?.()?.getTime?.() ?? 0;
      if (!existing || tNew >= tOld) dsByUserId.set(uid, ds);
    });

    const merged = allEmployees.map((u) => {
      const ds = dsByUserId.get(u.id);
      const stateRaw = String(ds?.state ?? '').trim();
      // Normalize states coming from mobile
      const state = stateRaw ? stateRaw.toLowerCase() : '';

      const status: 'On Duty' | 'Off Duty' | 'Not Started' =
        state.includes('on') || state.includes('start')
          ? 'On Duty'
          : state.includes('off') || state.includes('end')
            ? 'Off Duty'
            : 'Not Started';

      const updatedAt = ds?.updatedAt?.toDate?.() ?? null;
      return {
        userId: u.id,
        empId: String(u.empId ?? '').trim() || '-',
        name: String(u.name ?? '').trim() || ds?.userName || '-',
        department: String(u.department ?? '').trim() || '-',
        status,
        rawState: stateRaw || '-',
        updatedAt,
      };
    });

    const filtered = merged.filter((r) => {
      if (!search) return true;
      const hay = `${toUpper(r.empId)} ${toUpper(r.name)} ${toUpper(r.department)} ${toUpper(r.status)} ${toUpper(r.rawState)}`;
      return hay.includes(search);
    });

    filtered.sort((a, b) => {
      const ta = a.updatedAt?.getTime?.() ?? 0;
      const tb = b.updatedAt?.getTime?.() ?? 0;
      return tb - ta;
    });

    return filtered;
  }, [dutyStates, userProfiles, searchText, profile, managerTeamUserIds]);

  const stats = useMemo(() => {
    const onDuty = rows.filter((r) => r.status === 'On Duty').length;
    const offDuty = rows.filter((r) => r.status === 'Off Duty').length;
    const notStarted = rows.filter((r) => r.status === 'Not Started').length;
    return { total: rows.length, onDuty, offDuty, notStarted };
  }, [rows]);

  const badgeClass = (status: string) => {
    if (status === 'On Duty') return 'bg-green-50 text-green-700 border-green-100';
    if (status === 'Off Duty') return 'bg-red-50 text-red-700 border-red-100';
    return 'bg-slate-50 text-slate-700 border-slate-200';
  };

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow border max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-bold">All Employees Duty State</h2>
            <div className="text-xs text-slate-500 mt-1">
              Total: <b>{stats.total}</b> • On Duty: <b className="text-green-700">{stats.onDuty}</b> • Off Duty:{' '}
              <b className="text-red-700">{stats.offDuty}</b> • Not Started: <b>{stats.notStarted}</b>
            </div>
          </div>
          <div className="w-full md:w-80">
            <input
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              placeholder="Search name / EmpID / dept"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">EmpID</th>
                <th className="px-4 py-3 font-medium">Employee</th>
                <th className="px-4 py-3 font-medium">Department</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Last Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.userId} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-slate-700">{r.empId}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
                  <td className="px-4 py-3 text-slate-600">{r.department}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${badgeClass(r.status)}`}>
                      {r.status}
                    </span>
                    <span className="ml-2 text-xs text-slate-400">({r.rawState})</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.updatedAt ? r.updatedAt.toLocaleString() : '-'}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-slate-400 py-6">
                    No employees found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
// TRAVEL REQUEST TYPES
interface TravelRequest {
  id: string;
  userId: string;
  userName: string;
  from: string;
  to: string;
  date: string;
  reason: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  createdAt: any;
}

// EMPLOYEE TRAVEL REQUESTS COMPONENT
function EmployeeTravelRequests({ profile }: { profile: UserProfile }) {
  const [travelRequests, setTravelRequests] = useState<TravelRequest[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'travelRequests'),
      where('userId', '==', profile.id),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setTravelRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })) as TravelRequest[]);
    });
    return () => unsub();
  }, [profile.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!from || !to || !date || !reason) {
      setError('All fields are required.');
      return;
    }
    setLoading(true);
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'travelRequests'), {
        userId: profile.id,
        userName: profile.name,
        from,
        to,
        date,
        reason,
        status: 'Pending',
        createdAt: serverTimestamp(),
      });
      setFrom('');
      setTo('');
      setDate('');
      setReason('');
    } catch (err: any) {
      setError('Failed to submit travel request.');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white p-6 rounded-xl shadow border">
        <h2 className="text-xl font-bold mb-4">Submit Travel Request</h2>
        {error && <div className="bg-red-50 text-red-600 p-2 rounded mb-2 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">From</label>
            <input type="text" className="w-full border rounded p-2" value={from} onChange={e => setFrom(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">To</label>
            <input type="text" className="w-full border rounded p-2" value={to} onChange={e => setTo(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Date</label>
            <input type="date" className="w-full border rounded p-2" value={date} onChange={e => setDate(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Reason</label>
            <textarea className="w-full border rounded p-2" value={reason} onChange={e => setReason(e.target.value)} required />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-indigo-600 text-white py-2 rounded font-bold hover:bg-indigo-700 transition disabled:opacity-50">
            {loading ? 'Submitting...' : 'Submit Travel Request'}
          </button>
        </form>
      </div>
      <div className="bg-white p-6 rounded-xl shadow border">
        <h2 className="text-xl font-bold mb-4">My Travel Requests</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2">From</th>
                <th className="px-3 py-2">To</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {travelRequests.map(tr => (
                <tr key={tr.id} className="border-b">
                  <td className="px-3 py-2">{tr.from}</td>
                  <td className="px-3 py-2">{tr.to}</td>
                  <td className="px-3 py-2">{tr.date}</td>
                  <td className="px-3 py-2">{tr.reason}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${tr.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' : tr.status === 'Approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{tr.status}</span>
                  </td>
                </tr>
              ))}
              {travelRequests.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-4">No travel requests found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </div>
  );
}

// ADMIN/MANAGER TRAVEL APPROVALS COMPONENT
function AdminTravelApprovals({ profile }: { profile: UserProfile }) {
  const [travelRequests, setTravelRequests] = useState<TravelRequest[]>([]);
  const [userProfiles, setUserProfiles] = useState<{ [id: string]: any }>({});

  const getEmployeeName = (tr: TravelRequest) => {
    const fromProfile = (userProfiles as any)?.[tr.userId]?.name;
    const fromDoc = (tr as any)?.userName || (tr as any)?.name;
    return (fromDoc || fromProfile || '-').toString();
  };

  const teamUserIds = useMemo(() => {
    if (profile.role !== 'manager') return null;
    return buildManagerTeamUserIdSet(profile, userProfiles);
  }, [profile, userProfiles]);

  const visibleTravelRequests = useMemo(() => {
    if (profile.role !== 'manager' || !teamUserIds) return travelRequests;
    return travelRequests.filter((tr) => teamUserIds.has(tr.userId));
  }, [profile.role, travelRequests, teamUserIds]);

  const sortedTravelRequests = [...visibleTravelRequests].sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'travelRequests'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setTravelRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })) as TravelRequest[]);
    });
    const unsubUsers = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'users'), (snap) => {
      const profiles: { [id: string]: any } = {};
      snap.docs.forEach(d => { profiles[d.id] = d.data(); });
      setUserProfiles(profiles);
    });
    return () => {
      unsub();
      unsubUsers();
    };
  }, []);

  const handleApprove = async (id: string) => {
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'travelRequests', id), { status: 'Approved' });
  };
  const handleReject = async (id: string) => {
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'travelRequests', id), { status: 'Rejected' });
  };

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow border max-w-3xl mx-auto">
        <h2 className="text-xl font-bold mb-4">Travel Request Approvals</h2>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2">Date (Descending)</th>
              <th className="px-3 py-2">Employee Id</th>
              <th className="px-3 py-2">Employee Name</th>
              <th className="px-3 py-2">From</th>
              <th className="px-3 py-2">To</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedTravelRequests.map(tr => (
              <tr key={tr.id} className="border-b">
                <td className="px-3 py-2">{tr.date}</td>
                <td className="px-3 py-2">{userProfiles[tr.userId]?.empId || '-'}</td>
                <td className="px-3 py-2">{getEmployeeName(tr)}</td>
                <td className="px-3 py-2">{tr.from}</td>
                <td className="px-3 py-2">{tr.to}</td>
                <td className="px-3 py-2">{tr.reason}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-1 rounded text-xs font-bold ${tr.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' : tr.status === 'Approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{tr.status}</span>
                </td>
                <td className="px-3 py-2 flex gap-2">
                  {tr.status === 'Pending' && <>
                    <button onClick={() => handleApprove(tr.id)} className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700">Approve</button>
                    <button onClick={() => handleReject(tr.id)} className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700">Reject</button>
                  </>}
                </td>
              </tr>
            ))}
            {sortedTravelRequests.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-4">No travel requests found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
// EXPENSE TYPES
interface Expense {
  id: string;
  userId: string;
  userName: string;
  amount: number;
  description: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  createdAt: any;
}

type FundRequestStage = 'HOD_PENDING' | 'FINANCE_PENDING' | 'BOSS_PENDING' | 'CEO_PENDING' | 'APPROVED' | 'REJECTED';

interface FundRequest {
  id: string;
  userId: string;
  userName: string;
  empId?: string;
  department?: string;
  designation?: string;

  amount: number;
  purpose: string;
  neededBy?: string;
  currency?: string;

  status: 'Pending' | 'Approved' | 'Rejected';
  stage: FundRequestStage;

  assignedToUserId?: string;
  assignedToRole?: 'hod' | 'finance' | 'boss' | 'ceo' | string;

  hodUserId?: string;
  hodEmpId?: string;
  financeUserId?: string;
  financeEmpId?: string;
  bossUserId?: string;
  bossEmpId?: string;
  ceoUserId?: string;
  ceoEmpId?: string;

  financeRemark?: string;
  watcherUserIds?: string[];

  createdAt: any;
  updatedAt?: any;
}

// EMPLOYEE EXPENSES COMPONENT
function EmployeeExpenses({ profile }: { profile: UserProfile }) {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'expenses'),
      where('userId', '==', profile.id),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() })) as Expense[]);
    });
    return () => unsub();
  }, [profile.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!amount || !description) {
      setError('All fields are required.');
      return;
    }
    if (isNaN(Number(amount)) || Number(amount) <= 0) {
      setError('Amount must be a positive number.');
      return;
    }
    setLoading(true);
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'expenses'), {
        userId: profile.id,
        userName: profile.name,
        amount: Number(amount),
        description,
        status: 'Pending',
        createdAt: serverTimestamp(),
      });
      setAmount('');
      setDescription('');
    } catch (err: any) {
      setError('Failed to submit expense.');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white p-6 rounded-xl shadow border">
        <div className="flex items-center gap-2 mb-4">
          <Upload className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-bold">Submit Expense</h2>
        </div>
        {error && <div className="bg-red-50 text-red-600 p-2 rounded mb-2 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Amount</label>
            <input type="number" min="0" step="0.01" className="w-full border rounded p-2" value={amount} onChange={e => setAmount(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Description</label>
            <textarea className="w-full border rounded p-2" value={description} onChange={e => setDescription(e.target.value)} required />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-indigo-600 text-white py-2 rounded font-bold hover:bg-indigo-700 transition disabled:opacity-50">
            {loading ? 'Submitting...' : 'Submit Expense'}
          </button>
        </form>
      </div>
      <div className="bg-white p-6 rounded-xl shadow border">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-bold">My Expenses</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2">Employee Code</th>
                <th className="px-3 py-2">Employee Name</th>
                <th className="px-3 py-2">Department</th>
                <th className="px-3 py-2">Designation</th>
                <th className="px-3 py-2">Amount</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map(exp => (
                <tr key={exp.id} className="border-b">
                  <td className="px-3 py-2">{profile.empId || '-'}</td>
                  <td className="px-3 py-2">{profile.name || '-'}</td>
                  <td className="px-3 py-2">{profile.department || '-'}</td>
                  <td className="px-3 py-2">{profile.designation || '-'}</td>
                  <td className="px-3 py-2">₹{exp.amount.toFixed(2)}</td>
                  <td className="px-3 py-2">{exp.description}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${exp.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' : exp.status === 'Approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{exp.status}</span>
                  </td>
                  <td className="px-3 py-2">-</td>
                </tr>
              ))}
              {expenses.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-4">No expenses found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </div>
  );
}

function EmployeeFundRequests({ profile }: { profile: UserProfile }) {
  const [items, setItems] = useState<FundRequest[]>([]);
  const [allUsers, setAllUsers] = useState<Record<string, any>>({});
  const [departments, setDepartments] = useState<Department[]>([]);
  const [matrix, setMatrix] = useState<any>({});

  const [amount, setAmount] = useState('');
  const [purpose, setPurpose] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [financeUserId, setFinanceUserId] = useState('');
  const [ceoUserId, setCeoUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'fundRequests'),
      where('userId', '==', profile.id),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })) as FundRequest[]);
    });
    return () => unsub();
  }, [profile.id]);

  useEffect(() => {
    const unsubUsers = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'users'), (snap) => {
      const profiles: Record<string, any> = {};
      snap.docs.forEach(d => {
        profiles[d.id] = d.data();
      });
      setAllUsers(profiles);
    });
    return () => unsubUsers();
  }, []);

  useEffect(() => {
    const unsubDepts = onSnapshot(
      query(collection(db, 'artifacts', appId, 'public', 'data', 'departments'), orderBy('name', 'asc')),
      (snap) => {
        setDepartments(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Department[]);
      }
    );
    const unsubMatrix = onSnapshot(
      doc(db, 'artifacts', appId, 'public', 'data', 'approvalMatrix', 'fundRequests'),
      (snap) => {
        setMatrix((snap.data() as any) || {});
      }
    );
    return () => {
      unsubDepts();
      unsubMatrix();
    };
  }, []);

  const financeCandidates = useMemo(() => {
    return Object.entries(allUsers)
      .filter(([, u]) => {
        const dept = String((u as any)?.department ?? '').trim().toLowerCase();
        const role = String((u as any)?.role ?? '').trim().toLowerCase();
        return dept.includes('finance') && (role.includes('manager') || role.includes('admin'));
      })
      .map(([uid, u]) => ({ uid, name: String((u as any)?.name ?? uid), empId: String((u as any)?.empId ?? '') }));
  }, [allUsers]);

  const ceoCandidates = useMemo(() => {
    return Object.entries(allUsers)
      .filter(([, u]) => {
        const role = String((u as any)?.role ?? '').trim().toLowerCase();
        const desig = String((u as any)?.designation ?? '').trim().toLowerCase();
        return role.includes('admin') || desig.includes('ceo');
      })
      .map(([uid, u]) => ({ uid, name: String((u as any)?.name ?? uid), empId: String((u as any)?.empId ?? '') }));
  }, [allUsers]);

  useEffect(() => {
    const matrixFinance = String((matrix as any)?.financeUserId ?? '').trim();
    if (matrixFinance) {
      setFinanceUserId(matrixFinance);
      return;
    }
    if (!financeUserId && financeCandidates.length > 0) setFinanceUserId(financeCandidates[0].uid);
  }, [financeUserId, financeCandidates, matrix]);

  useEffect(() => {
    const matrixCeo = String((matrix as any)?.ceoUserId ?? '').trim();
    if (matrixCeo) {
      setCeoUserId(matrixCeo);
      return;
    }
    if (!ceoUserId && ceoCandidates.length > 0) setCeoUserId(ceoCandidates[0].uid);
  }, [ceoUserId, ceoCandidates, matrix]);

  const resolveUserIdByEmpIdOrName = (empId: string, name: string) => {
    const empNeedle = String(empId ?? '').trim();
    if (empNeedle) {
      const byEmp = Object.entries(allUsers).find(([, u]) => String((u as any)?.empId ?? '').trim() === empNeedle);
      if (byEmp) return byEmp[0];
    }
    const nameNeedle = String(name ?? '').trim().toLowerCase();
    if (nameNeedle) {
      const byName = Object.entries(allUsers).find(([, u]) => String((u as any)?.name ?? '').trim().toLowerCase() === nameNeedle);
      if (byName) return byName[0];
    }
    return '';
  };

  const stageLabel = (stage?: string) => {
    switch (stage) {
      case 'HOD_PENDING':
        return 'Pending HOD';
      case 'FINANCE_PENDING':
        return 'Pending Finance';
      case 'BOSS_PENDING':
        return 'Pending Boss';
      case 'CEO_PENDING':
        return 'Pending CEO';
      case 'APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      default:
        return stage || '-';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const amt = Number(amount);
    if (!amount || Number.isNaN(amt) || amt <= 0) {
      setError('Amount must be a positive number.');
      return;
    }
    if (!purpose.trim()) {
      setError('Purpose is required.');
      return;
    }

    const useDeptHod = Boolean((matrix as any)?.useDepartmentHod ?? true);
    const deptName = String(profile.department || '').trim();
    const deptHodUserId = useDeptHod
      ? String(
          (departments.find((d) => String(d.name || '').trim().toLowerCase() === deptName.toLowerCase()) as any)
            ?.hodUserId || ''
        ).trim()
      : '';

    const hodUserId = deptHodUserId || resolveUserIdByEmpIdOrName(profile.reportingToEmpId || '', profile.reportingTo || '');
    if (!hodUserId) {
      setError('Reporting manager (HOD) is not configured for your profile. Please ask Admin to set Reporting To EmpId / Name.');
      return;
    }
    if (!financeUserId) {
      setError('No Finance approver found. Please ask Admin to create a Finance manager (department contains "Finance").');
      return;
    }
    if (!ceoUserId) {
      setError('No CEO/admin found. Please ask Admin to ensure at least one admin user exists.');
      return;
    }

    const hodEmpId = String((allUsers as any)?.[hodUserId]?.empId ?? '');
    const hodBossUserId = resolveUserIdByEmpIdOrName(
      String((allUsers as any)?.[hodUserId]?.reportingToEmpId ?? ''),
      String((allUsers as any)?.[hodUserId]?.reportingTo ?? '')
    );
    const bossEmpId = hodBossUserId ? String((allUsers as any)?.[hodBossUserId]?.empId ?? '') : '';

    setLoading(true);
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'fundRequests'), {
        userId: profile.id,
        userName: profile.name,
        empId: profile.empId,
        department: profile.department || '',
        designation: profile.designation || '',

        amount: amt,
        purpose: purpose.trim(),
        neededBy: neededBy || '',
        currency: 'INR',

        status: 'Pending',
        stage: 'HOD_PENDING',
        assignedToUserId: hodUserId,
        assignedToRole: 'hod',

        hodUserId,
        hodEmpId,
        financeUserId,
        financeEmpId: String((allUsers as any)?.[financeUserId]?.empId ?? ''),
        bossUserId: hodBossUserId,
        bossEmpId,
        ceoUserId,
        ceoEmpId: String((allUsers as any)?.[ceoUserId]?.empId ?? ''),

        watcherUserIds: Array.from(new Set([financeUserId, ceoUserId].filter(Boolean))),

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        source: 'web',
      });

      setAmount('');
      setPurpose('');
      setNeededBy('');
    } catch (err: any) {
      setError('Failed to submit fund request.');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow border">
          <div className="flex items-center gap-2 mb-4">
            <Upload className="w-6 h-6 text-indigo-600" />
            <h2 className="text-xl font-bold">Request for Funds</h2>
          </div>
          {error && <div className="bg-red-50 text-red-600 p-2 rounded mb-2 text-sm">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full border rounded p-2"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Purpose</label>
              <textarea
                className="w-full border rounded p-2"
                value={purpose}
                onChange={e => setPurpose(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Needed By (optional)</label>
              <input
                type="date"
                className="w-full border rounded p-2"
                value={neededBy}
                onChange={e => setNeededBy(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Finance Reviewer</label>
                <select
                  className="w-full border rounded p-2"
                  value={financeUserId}
                  onChange={(e) => setFinanceUserId(e.target.value)}
                  disabled={financeCandidates.length === 0}
                >
                  {financeCandidates.length === 0 && <option value="">No Finance approver found</option>}
                  {financeCandidates.map((u) => (
                    <option key={u.uid} value={u.uid}>
                      {u.name}{u.empId ? ` (${u.empId})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Final Approver (CEO/Admin)</label>
                <select
                  className="w-full border rounded p-2"
                  value={ceoUserId}
                  onChange={(e) => setCeoUserId(e.target.value)}
                  disabled={ceoCandidates.length === 0}
                >
                  {ceoCandidates.length === 0 && <option value="">No admin/CEO found</option>}
                  {ceoCandidates.map((u) => (
                    <option key={u.uid} value={u.uid}>
                      {u.name}{u.empId ? ` (${u.empId})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-2 rounded font-bold hover:bg-indigo-700 transition disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Fund Request'}
            </button>
          </form>
        </div>

        <div className="bg-white p-6 rounded-xl shadow border">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-6 h-6 text-indigo-600" />
            <h2 className="text-xl font-bold">My Fund Requests</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Purpose</th>
                  <th className="px-3 py-2">Needed By</th>
                  <th className="px-3 py-2">Stage</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((fr) => (
                  <tr key={fr.id} className="border-b">
                    <td className="px-3 py-2">₹{Number(fr.amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2">{fr.purpose}</td>
                    <td className="px-3 py-2">{fr.neededBy || '-'}</td>
                    <td className="px-3 py-2">{stageLabel(fr.stage)}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${fr.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' : fr.status === 'Approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{fr.status}</span>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-slate-400 py-4">No fund requests found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// EMPLOYEE SALARY SLIPS (DOWNLOAD)
function EmployeeSalarySlips({ profile }: { profile: UserProfile }) {
  const [slips, setSlips] = useState<any[]>([]);

  useEffect(() => {
    const empId = String((profile as any)?.empId ?? '').trim().toUpperCase();
    const base = collection(db, 'artifacts', appId, 'public', 'data', 'salarySlips');

    // Query by empId so slips still show even if the employee auth uid differs
    // from the uid that was used during admin upload mapping.
    const q = empId
      ? query(base, where('empId', '==', empId))
      : query(base, where('userId', '==', profile.id));

    const unsub = onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() })) as any[];
      const sorted = all.sort((a, b) => {
        const aMs = a?.createdAt?.toDate?.()?.getTime?.() ?? 0;
        const bMs = b?.createdAt?.toDate?.()?.getTime?.() ?? 0;
        return bMs - aMs;
      });
      setSlips(sorted.slice(0, 12));
    });
    return () => unsub();
  }, [profile]);

  return (
    <div className="bg-white p-6 rounded-xl shadow border max-w-3xl mx-auto">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-6 h-6 text-indigo-600" />
        <h2 className="text-xl font-bold">Salary Slips</h2>
      </div>
      <div className="text-xs text-slate-500 mb-4">Showing your latest uploaded slips.</div>

      {slips.length === 0 ? (
        <div className="text-slate-500 text-sm">No salary slips found.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left">Month</th>
              <th className="px-3 py-2 text-left">Uploaded</th>
              <th className="px-3 py-2 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {slips.map(s => (
              <tr key={s.id} className="border-b">
                <td className="px-3 py-2">{String(s.month ?? s.monthKey ?? 'Salary Slip')}</td>
                <td className="px-3 py-2">{s.createdAt?.toDate?.()?.toLocaleDateString?.() || '-'}</td>
                <td className="px-3 py-2">
                  {s.fileUrl ? (
                    <a
                      href={String(s.fileUrl)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-indigo-600 hover:underline"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </a>
                  ) : (
                    <span className="text-slate-400">No file</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// EMPLOYEE LOCAL CONVEYANCE (SUBMIT + HISTORY)
function EmployeeLocalConveyance({ profile }: { profile: UserProfile }) {
  const [conveyanceDate, setConveyanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [totalKm, setTotalKm] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState<any[]>([]);

  const [filterStatus, setFilterStatus] = useState<'All' | 'Pending' | 'Approved' | 'Rejected'>('All');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'localConveyance'),
      where('userId', '==', profile.id),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })) as any[]);
    });
    return () => unsub();
  }, [profile.id]);

  const filteredItems = useMemo(() => {
    const from = filterFrom ? new Date(filterFrom) : null;
    const to = filterTo ? new Date(filterTo) : null;
    if (to) {
      to.setHours(23, 59, 59, 999);
    }

    const toDateObj = (ts: any): Date | null => {
      try {
        if (!ts) return null;
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        if (Number.isNaN(d.getTime())) return null;
        return d;
      } catch {
        return null;
      }
    };

    return items.filter((it) => {
      const status = String(it.status || 'Pending');
      if (filterStatus !== 'All' && status !== filterStatus) return false;

      if (from || to) {
        const d = toDateObj(it.conveyanceDate);
        if (!d) return false;
        if (from && d < from) return false;
        if (to && d > to) return false;
      }
      return true;
    });
  }, [items, filterStatus, filterFrom, filterTo]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const km = Number(totalKm);
    if (!Number.isFinite(km) || km <= 0) {
      setError('Please enter a valid Total Km.');
      return;
    }
    setSubmitting(true);
    try {
      const storage = getStorage();
      let attachmentData: any = null;
      if (attachment) {
        const path = `conveyance-attachments/${profile.id}/${Date.now()}_${attachment.name}`;
        const r = ref(storage, path);
        await uploadBytes(r, attachment);
        const url = await getDownloadURL(r);
        attachmentData = {
          url,
          storagePath: path,
          fileType: attachment.type || 'file',
          uploadedAt: serverTimestamp(),
        };
      }

      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'localConveyance'), {
        userId: profile.id,
        empId: profile.empId,
        name: profile.name,
        managerEmpId: (profile as any).managerEmpId || null,
        managerName: (profile as any).managerName || null,
        managerEmail: (profile as any).managerEmail || null,
        conveyanceDate: new Date(conveyanceDate),
        totalKm: km,
        attachment: attachmentData,
        status: 'Pending',
        createdAt: serverTimestamp(),
        source: 'web-employee',
      });

      setTotalKm('');
      setAttachment(null);
    } catch {
      setError('Failed to submit local conveyance.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white p-6 rounded-xl shadow border">
        <div className="flex items-center gap-2 mb-3">
          <Truck className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-bold">Local Conveyance</h2>
        </div>
        {error && <div className="bg-red-50 text-red-600 p-2 rounded mb-2 text-sm">{error}</div>}
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Date</label>
            <input type="date" className="w-full border rounded p-2" value={conveyanceDate} onChange={e => setConveyanceDate(e.target.value)} disabled={submitting} />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Total Km</label>
            <input type="number" min={0} step="0.01" className="w-full border rounded p-2" value={totalKm} onChange={e => setTotalKm(e.target.value)} disabled={submitting} />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Attachment (optional)</label>
            <input type="file" accept="image/*,application/pdf" onChange={e => setAttachment((e.target.files && e.target.files[0]) || null)} disabled={submitting} />
            {attachment && <div className="text-[11px] text-slate-500 mt-1">Selected: {attachment.name}</div>}
          </div>
          <div className="md:col-span-3">
            <button type="submit" disabled={submitting} className="w-full bg-indigo-600 text-white py-2 rounded-lg font-bold hover:bg-indigo-700 transition disabled:opacity-50">
              {submitting ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white p-6 rounded-xl shadow border">
        <h3 className="text-lg font-bold mb-3">My Submissions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">From</label>
            <input type="date" className="w-full border rounded p-2 text-sm" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">To</label>
            <input type="date" className="w-full border rounded p-2 text-sm" value={filterTo} onChange={e => setFilterTo(e.target.value)} />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Status</label>
            <select className="w-full border rounded p-2 text-sm" value={filterStatus} onChange={e => setFilterStatus(e.target.value as any)}>
              <option value="All">All</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
        </div>
        {items.length === 0 ? (
          <div className="text-slate-500 text-sm">No conveyance submissions found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Total Km</th>
                <th className="px-3 py-2 text-left">Attachment</th>
                <th className="px-3 py-2 text-left">Report PDF</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(it => (
                <tr key={it.id} className="border-b">
                  <td className="px-3 py-2">{it.conveyanceDate?.toDate?.()?.toLocaleDateString?.() || (it.conveyanceDate ? new Date(it.conveyanceDate).toLocaleDateString() : '-')}</td>
                  <td className="px-3 py-2">{String(it.totalKm ?? '-')}</td>
                  <td className="px-3 py-2">
                    {it.attachment?.url ? (
                      <a href={String(it.attachment.url)} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1">
                        <ExternalLink className="w-4 h-4" /> View
                      </a>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {(it as any)?.reportPdf?.url ? (
                      <a href={String((it as any).reportPdf.url)} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1">
                        <ExternalLink className="w-4 h-4" /> PDF
                      </a>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${String(it.status || 'Pending') === 'Pending' ? 'bg-yellow-100 text-yellow-700' : String(it.status || 'Pending') === 'Approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {String(it.status || 'Pending')}
                    </span>
                  </td>
                </tr>
              ))}
              {filteredItems.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-slate-400 py-4">No submissions match the filter.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
    </div>
  );
}

// ADMIN/MANAGER EXPENSE APPROVALS COMPONENT
function AdminExpenseApprovals({ profile }: { profile: UserProfile }) {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [userProfiles, setUserProfiles] = useState<{ [id: string]: any }>({});

  const getEmployeeName = (exp: Expense) => {
    const fromProfile = (userProfiles as any)?.[exp.userId]?.name;
    const fromDoc = (exp as any)?.userName || (exp as any)?.name;
    return (fromDoc || fromProfile || '-').toString();
  };

  const teamUserIds = useMemo(() => {
    if (profile.role !== 'manager') return null;
    return buildManagerTeamUserIdSet(profile, userProfiles);
  }, [profile, userProfiles]);

  const visibleExpenses = useMemo(() => {
    if (profile.role !== 'manager' || !teamUserIds) return expenses;
    return expenses.filter((e) => teamUserIds.has(e.userId));
  }, [profile.role, expenses, teamUserIds]);

  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'expenses'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() })) as Expense[]);
    });
    const unsubUsers = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'users'), (snap) => {
      const profiles: { [id: string]: any } = {};
      snap.docs.forEach(d => { profiles[d.id] = d.data(); });
      setUserProfiles(profiles);
    });
    return () => {
      unsub();
      unsubUsers();
    };
  }, []);

  const handleApprove = async (id: string) => {
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'expenses', id), { status: 'Approved' });
  };
  const handleReject = async (id: string) => {
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'expenses', id), { status: 'Rejected' });
  };

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow border max-w-3xl mx-auto">
        <h2 className="text-xl font-bold mb-4">Expense Approvals</h2>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2">Employee Code</th>
              <th className="px-3 py-2">Employee Name</th>
              <th className="px-3 py-2">Department</th>
              <th className="px-3 py-2">Designation</th>
              <th className="px-3 py-2">Amount</th>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {visibleExpenses.map(exp => (
              <tr key={exp.id} className="border-b">
                <td className="px-3 py-2">{userProfiles[exp.userId]?.empId || '-'}</td>
                <td className="px-3 py-2">{getEmployeeName(exp)}</td>
                <td className="px-3 py-2">{userProfiles[exp.userId]?.department || '-'}</td>
                <td className="px-3 py-2">{userProfiles[exp.userId]?.designation || '-'}</td>
                <td className="px-3 py-2">₹{exp.amount.toFixed(2)}</td>
                <td className="px-3 py-2">{exp.description}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-1 rounded text-xs font-bold ${exp.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' : exp.status === 'Approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{exp.status}</span>
                </td>
                <td className="px-3 py-2 flex gap-2">
                  {exp.status === 'Pending' && <>
                    <button onClick={() => handleApprove(exp.id)} className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700">Approve</button>
                    <button onClick={() => handleReject(exp.id)} className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700">Reject</button>
                  </>}
                </td>
              </tr>
            ))}
            {visibleExpenses.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-4">No expenses found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>

  );
}

function AdminFundApprovals({ profile }: { profile: UserProfile }) {
  const [items, setItems] = useState<FundRequest[]>([]);
  const [userProfiles, setUserProfiles] = useState<Record<string, any>>({});
  const [financeRemarks, setFinanceRemarks] = useState<Record<string, string>>({});

  useEffect(() => {
    const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'fundRequests'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })) as FundRequest[]);
    });
    const unsubUsers = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'users'), (snap) => {
      const profiles: Record<string, any> = {};
      snap.docs.forEach(d => {
        profiles[d.id] = d.data();
      });
      setUserProfiles(profiles);
    });
    return () => {
      unsub();
      unsubUsers();
    };
  }, []);

  const stageLabel = (stage?: string) => {
    switch (stage) {
      case 'HOD_PENDING':
        return 'Pending HOD';
      case 'FINANCE_PENDING':
        return 'Pending Finance';
      case 'BOSS_PENDING':
        return 'Pending Boss';
      case 'CEO_PENDING':
        return 'Pending CEO';
      case 'APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      default:
        return stage || '-';
    }
  };

  const getEmployeeName = (fr: FundRequest) => {
    const fromProfile = (userProfiles as any)?.[fr.userId]?.name;
    const fromDoc = (fr as any)?.userName || (fr as any)?.name;
    return (fromDoc || fromProfile || '-').toString();
  };

  const visibleItems = useMemo(() => {
    if (profile.role === 'admin') return items;
    const myUid = profile.id;
    return items.filter((it) => {
      if ((it as any)?.assignedToUserId === myUid) return true;
      const watchers = Array.isArray((it as any)?.watcherUserIds) ? (it as any).watcherUserIds : [];
      return watchers.includes(myUid);
    });
  }, [items, profile]);

  const handleHodApprove = async (it: FundRequest) => {
    if (!it.financeUserId) {
      alert('Finance reviewer is missing on this request.');
      return;
    }
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'fundRequests', it.id), {
      stage: 'FINANCE_PENDING',
      status: 'Pending',
      assignedToUserId: it.financeUserId,
      assignedToRole: 'finance',
      updatedAt: serverTimestamp(),
    });
  };

  const handleReject = async (it: FundRequest) => {
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'fundRequests', it.id), {
      stage: 'REJECTED',
      status: 'Rejected',
      assignedToUserId: '',
      assignedToRole: '',
      updatedAt: serverTimestamp(),
    });
  };

  const handleFinanceReviewed = async (it: FundRequest) => {
    const remark = (financeRemarks[it.id] || '').trim();
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'fundRequests', it.id), {
      financeRemark: remark,
      financeReviewedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };

  const handleFinanceForward = async (it: FundRequest) => {
    if (!it.bossUserId) {
      alert('Boss is not configured for this request (HOD has no Reporting To).');
      return;
    }
    const remark = (financeRemarks[it.id] || '').trim();
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'fundRequests', it.id), {
      stage: 'BOSS_PENDING',
      status: 'Pending',
      assignedToUserId: it.bossUserId,
      assignedToRole: 'boss',
      financeRemark: remark,
      updatedAt: serverTimestamp(),
    });
  };

  const handleBossApprove = async (it: FundRequest) => {
    if (!it.ceoUserId) {
      alert('CEO/admin is missing on this request.');
      return;
    }
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'fundRequests', it.id), {
      stage: 'CEO_PENDING',
      status: 'Pending',
      assignedToUserId: it.ceoUserId,
      assignedToRole: 'ceo',
      updatedAt: serverTimestamp(),
    });
  };

  const handleCeoApprove = async (it: FundRequest) => {
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'fundRequests', it.id), {
      stage: 'APPROVED',
      status: 'Approved',
      assignedToUserId: '',
      assignedToRole: '',
      updatedAt: serverTimestamp(),
    });
  };

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow border max-w-6xl mx-auto">
        <h2 className="text-xl font-bold mb-4">Fund Request Approvals</h2>
        <div className="text-xs text-slate-500 mb-3">Workflow: HOD → Finance (remarks + forward) → Boss → CEO</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2">Employee Code</th>
                <th className="px-3 py-2">Employee Name</th>
                <th className="px-3 py-2">Department</th>
                <th className="px-3 py-2">Designation</th>
                <th className="px-3 py-2">Amount</th>
                <th className="px-3 py-2">Purpose</th>
                <th className="px-3 py-2">Stage</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((it) => {
                const employee = userProfiles[it.userId] || {};
                const stage = String((it as any)?.stage || 'HOD_PENDING');
                const assignedTo = String((it as any)?.assignedToUserId || '');
                const isMine = assignedTo && assignedTo === profile.id;
                const canAct = profile.role === 'admin' ? true : isMine;

                return (
                  <tr key={it.id} className="border-b align-top">
                    <td className="px-3 py-2">{employee.empId || it.empId || '-'}</td>
                    <td className="px-3 py-2">{getEmployeeName(it)}</td>
                    <td className="px-3 py-2">{employee.department || it.department || '-'}</td>
                    <td className="px-3 py-2">{employee.designation || it.designation || '-'}</td>
                    <td className="px-3 py-2">₹{Number(it.amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2">{it.purpose || '-'}</td>
                    <td className="px-3 py-2">{stageLabel(stage)}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${it.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' : it.status === 'Approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{it.status}</span>
                    </td>
                    <td className="px-3 py-2">
                      {it.status !== 'Pending' && <span className="text-slate-400">-</span>}

                      {it.status === 'Pending' && stage === 'HOD_PENDING' && (
                        <div className="flex gap-2">
                          <button
                            disabled={!canAct}
                            onClick={() => handleHodApprove(it)}
                            className={`px-3 py-1 rounded text-xs ${canAct ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                          >
                            Approve
                          </button>
                          <button
                            disabled={!canAct}
                            onClick={() => handleReject(it)}
                            className={`px-3 py-1 rounded text-xs ${canAct ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                          >
                            Reject
                          </button>
                        </div>
                      )}

                      {it.status === 'Pending' && stage === 'FINANCE_PENDING' && (
                        <div className="space-y-2">
                          <textarea
                            className="w-full border rounded p-2 text-xs"
                            placeholder="Finance remarks (optional)"
                            value={financeRemarks[it.id] ?? String((it as any)?.financeRemark ?? '')}
                            onChange={(e) => setFinanceRemarks((p) => ({ ...p, [it.id]: e.target.value }))}
                            disabled={!canAct}
                          />
                          <div className="flex flex-wrap gap-2">
                            <button
                              disabled={!canAct}
                              onClick={() => handleFinanceReviewed(it)}
                              className={`px-3 py-1 rounded text-xs ${canAct ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                            >
                              Reviewed / No Issues
                            </button>
                            <button
                              disabled={!canAct}
                              onClick={() => handleFinanceForward(it)}
                              className={`px-3 py-1 rounded text-xs ${canAct ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                            >
                              Forward to Boss
                            </button>
                          </div>
                          <div className="text-[11px] text-slate-500">Finance cannot reject; only remarks + forward.</div>
                        </div>
                      )}

                      {it.status === 'Pending' && stage === 'BOSS_PENDING' && (
                        <div className="flex gap-2">
                          <button
                            disabled={!canAct}
                            onClick={() => handleBossApprove(it)}
                            className={`px-3 py-1 rounded text-xs ${canAct ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                          >
                            Approve
                          </button>
                          <button
                            disabled={!canAct}
                            onClick={() => handleReject(it)}
                            className={`px-3 py-1 rounded text-xs ${canAct ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                          >
                            Reject
                          </button>
                        </div>
                      )}

                      {it.status === 'Pending' && stage === 'CEO_PENDING' && (
                        <div className="flex gap-2">
                          <button
                            disabled={!canAct}
                            onClick={() => handleCeoApprove(it)}
                            className={`px-3 py-1 rounded text-xs ${canAct ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                          >
                            Approve
                          </button>
                          <button
                            disabled={!canAct}
                            onClick={() => handleReject(it)}
                            className={`px-3 py-1 rounded text-xs ${canAct ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {visibleItems.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-slate-400 py-4">No fund requests found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// LOCAL CONVEYANCE TYPES
interface LocalConveyance {
  id: string;
  userId: string;
  empId?: string;
  name?: string;
  conveyanceDate?: any;
  totalKm?: number;
  trackedKm?: number;
  trackedPoints?: number;
  attachment?: { url?: string } | null;
  reportPdf?: { url?: string } | null;
  status?: 'Pending' | 'Approved' | 'Rejected' | string;
  createdAt?: any;
}

// ADMIN/MANAGER LOCAL CONVEYANCE APPROVALS COMPONENT
function AdminLocalConveyanceApprovals({ profile }: { profile: UserProfile }) {
  const [items, setItems] = useState<LocalConveyance[]>([]);
  const [userProfiles, setUserProfiles] = useState<{ [id: string]: any }>({});

  const [filterEmpId, setFilterEmpId] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterStatus, setFilterStatus] = useState<'All' | 'Pending' | 'Approved' | 'Rejected'>('All');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const teamUserIds = useMemo(() => {
    if (profile.role !== 'manager') return null;
    return buildManagerTeamUserIdSet(profile, userProfiles);
  }, [profile, userProfiles]);

  const visibleItems = useMemo(() => {
    if (profile.role !== 'manager' || !teamUserIds) return items;
    return items.filter((it) => !!it.userId && teamUserIds.has(it.userId));
  }, [profile.role, items, teamUserIds]);

  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'localConveyance'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })) as LocalConveyance[]);
    });
    const unsubUsers = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'users'), (snap) => {
      const profiles: { [id: string]: any } = {};
      snap.docs.forEach((d) => {
        profiles[d.id] = d.data();
      });
      setUserProfiles(profiles);
    });
    return () => {
      unsub();
      unsubUsers();
    };
  }, []);

  const handleApprove = async (id: string) => {
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'localConveyance', id), {
      status: 'Approved',
    });
  };

  const handleReject = async (id: string) => {
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'localConveyance', id), {
      status: 'Rejected',
    });
  };

  const fmtDate = (ts: any) => {
    try {
      if (!ts) return '-';
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString();
    } catch {
      return '-';
    }
  };

  const toDateObj = (ts: any): Date | null => {
    try {
      if (!ts) return null;
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      if (Number.isNaN(d.getTime())) return null;
      return d;
    } catch {
      return null;
    }
  };

  const filteredItems = useMemo(() => {
    const empNeedle = filterEmpId.trim().toLowerCase();
    const nameNeedle = filterName.trim().toLowerCase();
    const from = filterFrom ? new Date(filterFrom) : null;
    const to = filterTo ? new Date(filterTo) : null;
    if (to) {
      to.setHours(23, 59, 59, 999);
    }

    return visibleItems.filter((it) => {
      const status = String(it.status || 'Pending');
      if (filterStatus !== 'All' && status !== filterStatus) return false;

      if (empNeedle) {
        const emp = String(it.empId || '').toLowerCase();
        if (!emp.includes(empNeedle)) return false;
      }

      if (nameNeedle) {
        const nm = String(it.name || '').toLowerCase();
        if (!nm.includes(nameNeedle)) return false;
      }

      if (from || to) {
        const d = toDateObj(it.conveyanceDate);
        if (!d) return false;
        if (from && d < from) return false;
        if (to && d > to) return false;
      }

      return true;
    });
  }, [visibleItems, filterEmpId, filterName, filterStatus, filterFrom, filterTo]);

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow border max-w-5xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <Truck className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-bold">Local Conveyance</h2>
        </div>
        <div className="text-sm text-slate-500 mb-4">Date | Employee Id | Employee Name | Submitted Km | Tracked Km | Diff | Attachment | Report PDF | Approval Status</div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Emp Id</label>
            <input
              className="w-full border rounded p-2 text-sm"
              value={filterEmpId}
              onChange={(e) => setFilterEmpId(e.target.value)}
              placeholder="Search EmpId"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Name</label>
            <input
              className="w-full border rounded p-2 text-sm"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="Search name"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">From</label>
            <input
              type="date"
              className="w-full border rounded p-2 text-sm"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">To</label>
            <input
              type="date"
              className="w-full border rounded p-2 text-sm"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Status</label>
            <select
              className="w-full border rounded p-2 text-sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
            >
              <option value="All">All</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Employee Id</th>
              <th className="px-3 py-2">Employee Name</th>
              <th className="px-3 py-2">Submitted Km</th>
              <th className="px-3 py-2">Tracked Km</th>
              <th className="px-3 py-2">Diff</th>
              <th className="px-3 py-2">Attachment</th>
              <th className="px-3 py-2">Report PDF</th>
              <th className="px-3 py-2">Approval Status</th>
              <th className="px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map(it => {
              const status = (it.status || 'Pending').toString();
              const attachmentUrl = (it.attachment as any)?.url || '';
              const reportUrl = (it.reportPdf as any)?.url || (it as any)?.reportPdf?.url || '';
              const submittedKm = typeof it.totalKm === 'number' ? it.totalKm : (it.totalKm as any);
              const trackedKm = typeof it.trackedKm === 'number' ? it.trackedKm : (it as any)?.trackedKm;
              const kmDiff = (typeof submittedKm === 'number' && typeof trackedKm === 'number')
                ? Math.abs(submittedKm - trackedKm)
                : null;
              const badgeClass = status === 'Approved'
                ? 'bg-green-100 text-green-700'
                : status === 'Rejected'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-yellow-100 text-yellow-700';

              return (
                <tr key={it.id} className="border-b">
                  <td className="px-3 py-2">{fmtDate(it.conveyanceDate)}</td>
                  <td className="px-3 py-2">{it.empId || '-'}</td>
                  <td className="px-3 py-2">{it.name || '-'}</td>
                  <td className="px-3 py-2">{typeof submittedKm === 'number' ? submittedKm.toFixed(2) : (submittedKm ?? '-')}</td>
                  <td className="px-3 py-2">{typeof trackedKm === 'number' ? trackedKm.toFixed(2) : (trackedKm ?? '-')}</td>
                  <td className="px-3 py-2">{typeof kmDiff === 'number' ? kmDiff.toFixed(2) : '-'}</td>
                  <td className="px-3 py-2">
                    {attachmentUrl ? (
                      <a
                        href={attachmentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 underline text-xs"
                      >
                        View
                      </a>
                    ) : (
                      <span className="text-slate-400 text-xs">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {reportUrl ? (
                      <a
                        href={reportUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 underline text-xs"
                      >
                        PDF
                      </a>
                    ) : (
                      <span className="text-slate-400 text-xs">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${badgeClass}`}>{status}</span>
                  </td>
                  <td className="px-3 py-2 flex gap-2">
                    {status === 'Pending' && (
                      <>
                        <button onClick={() => handleApprove(it.id)} className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700">Approve</button>
                        <button onClick={() => handleReject(it.id)} className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700">Reject</button>
                      </>
                    )}
                    {status !== 'Pending' && <span className="text-slate-400 text-xs">-</span>}
                  </td>
                </tr>
              );
            })}
            {filteredItems.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center text-slate-400 py-4">No submissions found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// --- Fix for TypeScript Build Errors ---
declare const __firebase_config: any;
declare const __app_id: any;

// --- Firebase Configuration & Initialization ---
let firebaseConfig: any;

try {
  if (typeof __firebase_config !== 'undefined') {
    firebaseConfig = JSON.parse(__firebase_config);
  } 
} catch (e) {}

if (!firebaseConfig) {
  firebaseConfig = {
    // Instructions: Paste your specific keys inside the quotes below.
    apiKey: "AIzaSyCaj2CvxEyPRrjS97JlAff2DhOnJRsgQX8", 
    authDomain: "tap-log-56fdc.firebaseapp.com",
    projectId: "tap-log-56fdc",
    storageBucket: "tap-log-56fdc.firebasestorage.app", 
  };
}

const app = initializeApp(firebaseConfig.apiKey ? firebaseConfig : { apiKey: "demo", projectId: "demo" });
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const appId = typeof __app_id !== 'undefined' ? __app_id : "tap-log"; 

const FUNCTIONS_REGION = 'asia-south1';
const functions = getFunctions(app, FUNCTIONS_REGION);
const requestSessionCallable = httpsCallable(functions, 'requestSession');
const adminResetDeviceCallable = httpsCallable(functions, 'adminResetDevice');
const adminSimulateGeoFenceOutCallable = httpsCallable(functions, 'adminSimulateGeoFenceOut');

// Secondary Firebase app used only for creating employee/vendor accounts
const existingAdminApp = getApps().find(a => a.name === 'adminApp');
const adminApp = existingAdminApp ?? initializeApp(
  firebaseConfig.apiKey ? firebaseConfig : { apiKey: 'demo', projectId: 'demo' },
  'adminApp'
);

const adminAuth = getAuth(adminApp);



// --- Utils ---
const getOrCreateWebDeviceId = () => {
  const key = 'tap_log_device_id';
  try {
    const existing = window.localStorage.getItem(key);
    if (existing && existing.trim()) return existing.trim();
    const id =
      (typeof window.crypto !== 'undefined' && typeof (window.crypto as any).randomUUID === 'function')
        ? (window.crypto as any).randomUUID()
        : `web_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
    window.localStorage.setItem(key, id);
    return id;
  } catch {
    return `web_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
  }
};

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

const callErrorToUserMessage = (err: any) => {
  const details = err?.details || {};
  const code = String(details?.code || err?.code || '').trim();
  if (code.includes('DEVICE_NOT_AUTHORIZED')) {
    return 'This account is registered on another device. Please contact Admin to Reset Device access.';
  }
  if (code.includes('SESSION_EXPIRED') || String(err?.code || '').includes('failed-precondition')) {
    return 'Daily session expired (11:59 PM IST). Please login again.';
  }
  const msg = String(err?.message || '').trim();
  if (msg) return msg;
  return 'Login blocked by session policy. Please try again.';
};

const downloadCSV = (filename: string, headers: string[], rows: any[]) => {
    const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map((cell: any) => `"${cell || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};

const toFiniteNumber = (value: any): number | null => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

const haversineMeters = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const resolveSiteForPoint = (
  lat: any,
  lng: any,
  sites: Site[]
): { site: Site; distanceMeters: number } | null => {
  const la = toFiniteNumber(lat);
  const lo = toFiniteNumber(lng);
  if (la === null || lo === null) return null;
  if (!Array.isArray(sites) || sites.length === 0) return null;

  let best: { site: Site; distanceMeters: number } | null = null;
  for (const s of sites) {
    const sLat = toFiniteNumber((s as any).latitude);
    const sLng = toFiniteNumber((s as any).longitude);
    if (sLat === null || sLng === null) continue;
    const radius = toFiniteNumber((s as any).radius) ?? 0;
    const dist = haversineMeters(la, lo, sLat, sLng);

    // Must be within the site's radius to match.
    if (radius > 0 && dist > radius) continue;

    if (!best || dist < best.distanceMeters) {
      best = { site: s, distanceMeters: dist };
    }
  }

  return best;
};

const isReporteeOfManager = (employee: any, manager: UserProfile) => {
  const employeeReportingToEmpId = String(employee?.reportingToEmpId ?? '').trim();
  const managerEmpId = String(manager?.empId ?? '').trim();
  if (employeeReportingToEmpId && managerEmpId && employeeReportingToEmpId === managerEmpId) return true;

  const employeeReportingToName = String(employee?.reportingTo ?? '').trim().toLowerCase();
  const managerName = String(manager?.name ?? '').trim().toLowerCase();
  if (employeeReportingToName && managerName && employeeReportingToName === managerName) return true;

  return false;
};

const buildManagerTeamUserIdSet = (manager: UserProfile, userProfilesMap: Record<string, any>) => {
  const set = new Set<string>();
  Object.entries(userProfilesMap || {}).forEach(([uid, employee]) => {
    if (isReporteeOfManager(employee, manager)) set.add(uid);
  });
  return set;
};

// --- Types ---
interface UserProfile {
  id: string; 
  empId: string; 
  name: string; 
  email: string;
  phone: string;
  role: 'admin' | 'manager' | 'employee'; 
  
  // Employee Fields
  department?: string;
  designation?: string;
  reportingTo?: string; 
  reportingToEmpId?: string;
  hrManager?: string; 
  hrManagerEmpId?: string;
  allocatedSiteIds?: string[];
  employeeCategory: 'Direct' | 'Indirect' | 'Contract' | 'Vendor'; 
  dutyType: 'Field' | 'Static';
  clientName?: string; 
  
  // Vendor Specific Fields
  ownerName?: string;
  address?: string;
  gstNo?: string;
  panNo?: string;
  bankName?: string;
  bankAccount?: string;
  ifscCode?: string;
  
  // Common
  status: 'Active' | 'Inactive';
}

interface Site {
  id: string;
  siteCode: string;
  name: string;
  clientName: string; 
  latitude: number;
  longitude: number;
  radius: number; 
  address: string;
}

interface AttendanceRecord {
  id: string;
  userId: string;
  empId?: string;
  userName: string;
  type: 'in' | 'out' | 'movement';
  timestamp: any;
  latitude: number;
  longitude: number;
  locationText?: string;    // 👈 add this line
  isOfflineSync?: boolean;
  siteName?: string;
  siteId?: string;
  onSite?: boolean;
  distanceToSiteMeters?: number;
  requiresHrApproval?: boolean;
  status: 'Verified' | 'Pending Manager' | 'Pending HR' | 'Rejected'; 
  managerApproved?: boolean;
  hrApproved?: boolean;
}

interface Task {
  id: string;
  title: string;
  description: string;
  assignedTo: string; 
  assignedToName: string;
  assignedBy: string; 
  assignedByName: string;
  targetSiteIds?: string[]; 
  targetSiteNames?: string; // Corrected: Plural to match usage
  status: 'Pending' | 'In Progress' | 'Completed' | 'Verified';
  dueDate: string;
  proofUrl?: string; 
  proofUrls?: string[]; 
  proofPath?: string;
  proofType?: 'image' | 'video' | 'multi';
  proofTimestamp?: any;
  proofLocation?: string;
  proofRemarks?: string;
  createdAt: any;
}

interface Notification {
  id: string;
  userId: string; 
  targetRole?: 'admin' | 'manager'; 
  message: string;
  type: 'alert' | 'info' | 'success';
  read: boolean;
  timestamp: any;
}

// --- Main Application Component ---
export default function TrackingApp() {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [configError, setConfigError] = useState(false);
  const [authGateMessage, setAuthGateMessage] = useState('');

  const roleTokensFromValue = (value: any) => {
    const raw = String(value ?? '').trim().toLowerCase();
    const tokens = raw.split(/[^a-z]+/).filter(Boolean);
    return new Set(tokens);
  };

  const primaryRoleFromTokens = (tokens: Set<string>): UserProfile['role'] => {
    if (tokens.has('admin')) return 'admin';
    if (tokens.has('manager')) return 'manager';
    return 'employee';
  };

  useEffect(() => {
    if (!firebaseConfig.apiKey || firebaseConfig.apiKey === "demo" || firebaseConfig.apiKey === "") {
        setConfigError(true);
        setLoading(false);
        return;
    }

    let expiryTimer: any = null;

    const clearExpiryTimer = () => {
      if (expiryTimer) {
        clearTimeout(expiryTimer);
        expiryTimer = null;
      }
    };

    const enforceSession = async (u: User) => {
      const deviceId = getOrCreateWebDeviceId();
      const deviceLabel = typeof navigator !== 'undefined' ? String(navigator.userAgent || '').slice(0, 120) : '';
      const res: any = await requestSessionCallable({
        appId,
        deviceId,
        platform: 'web',
        deviceLabel,
      });
      const data = (res?.data || {}) as any;
      const expiresAtUtcMs = Number(data?.expiresAtUtcMs || 0);
      if (expiresAtUtcMs > 0) {
        const ms = Math.max(0, expiresAtUtcMs - Date.now());
        clearExpiryTimer();
        expiryTimer = setTimeout(async () => {
          try {
            setAuthGateMessage('Daily session expired (11:59 PM IST). Please login again.');
            await signOut(auth);
          } catch {
            // ignore
          }
        }, ms);
      }
      // Pick up any new custom claims.
      try {
        await u.getIdToken(true);
      } catch {
        // ignore
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
          try {
            setAuthGateMessage('');
            await enforceSession(u);
            const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'users', u.uid);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
          const raw = docSnap.data() as any;

          // Roles in Firestore can be inconsistent (e.g. "Employee, Manager"), so normalize to a primary role.
          const tokens = roleTokensFromValue(raw?.role ?? 'employee');
          let primaryRole = primaryRoleFromTokens(tokens);

          // If role isn't explicitly manager/admin, infer manager by checking if anyone reports to them.
          if (primaryRole === 'employee') {
            const empId = String(raw?.empId ?? '').trim();
            const name = String(raw?.name ?? '').trim();
            const usersCol = collection(db, 'artifacts', appId, 'public', 'data', 'users');

            try {
              if (empId) {
                const snap = await getDocs(query(usersCol, where('reportingToEmpId', '==', empId), limit(1)));
                if (!snap.empty) primaryRole = 'manager';
              }

              if (primaryRole === 'employee' && name) {
                const snap = await getDocs(query(usersCol, where('reportingTo', '==', name), limit(1)));
                if (!snap.empty) primaryRole = 'manager';
              }
            } catch (e) {
              // Ignore inference failures; fall back to role field.
              console.warn('Manager inference failed', e);
            }
          }

          setProfile({ id: docSnap.id, ...raw, role: primaryRole } as UserProfile);
            } else {
                console.warn("User authenticated but no profile found");
            }
          } catch (e) {
              console.error("Error fetching profile", e);
              setAuthGateMessage(callErrorToUserMessage(e));
              try {
                await signOut(auth);
              } catch {
                // ignore
              }
              setProfile(null);
          }
      } else {
          clearExpiryTimer();
          setProfile(null);
      }
      setLoading(false);
    });

    return () => {
      clearExpiryTimer();
      unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    setAuthGateMessage('');
    await signOut(auth);
    setProfile(null);
  };

  // Heartbeat (web): updates a single Firestore doc per user so admins can detect stale sessions.
  useEffect(() => {
    if (!user || !profile) return;

    const hbRef = doc(db, 'artifacts', appId, 'public', 'data', 'heartbeats', user.uid);
    const deviceId = getOrCreateWebDeviceId();

    const getAppState = () => {
      try {
        return document.visibilityState === 'visible' ? 'foreground' : 'background';
      } catch {
        return 'unknown';
      }
    };

    const writeHeartbeat = async (reason: string) => {
      try {
        await setDoc(
          hbRef,
          {
            userId: user.uid,
            empId: (profile as any)?.empId || '',
            userName: (profile as any)?.name || '',
            role: (profile as any)?.role || '',
            employeeCategory: (profile as any)?.employeeCategory || '',
            allocatedSiteIds: Array.isArray((profile as any)?.allocatedSiteIds)
              ? (profile as any)?.allocatedSiteIds
              : [],
            platform: 'web',
            deviceId,
            appState: getAppState(),
            reason,
            clientTimeUtcMs: Date.now(),
            lastSeenAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch {
        // ignore heartbeat failures
      }
    };

    // Immediate write on mount
    writeHeartbeat('auth_state_changed');

    const interval = window.setInterval(() => {
      writeHeartbeat('interval');
    }, HEARTBEAT_INTERVAL_MS);

    const onVis = () => {
      writeHeartbeat('visibility_change');
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user, profile]);

  if (configError) {
      return (
          <div className="flex flex-col h-screen items-center justify-center p-8 text-center bg-slate-50">
              <div className="bg-white p-8 rounded-xl shadow-lg border border-red-100 max-w-md">
                  <h2 className="text-xl font-bold text-red-600 mb-2">Configuration Missing</h2>
                  <p className="text-slate-600 mb-4">The app cannot connect to Firebase because the API Key is missing.</p>
                  <p className="text-sm text-slate-500">Please paste your keys from the Firebase Console into the code.</p>
              </div>
          </div>
      );
  }

  if (loading) return <div className="flex h-screen items-center justify-center text-slate-500">Loading Alert Intell...</div>;

  if (!user || !profile) {
    return <LoginScreen initialError={authGateMessage} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      {profile.role === 'admin' ? (
        <AdminPortal profile={profile} onLogout={handleLogout} />
      ) : (
        <div>
          <EmployeeMobileApp profile={profile} onLogout={handleLogout} />
        </div>
      )}
    </div>
  );
}

// --- Login Screen ---
function LoginScreen({ initialError }: { initialError?: string }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);

  useEffect(() => {
    if (initialError && initialError.trim()) setError(initialError);
  }, [initialError]);

  const handleLogin = async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');
      setLoading(true);
      try {
          await signInWithEmailAndPassword(auth, email, password);
      } catch (err: any) {
          setError("Invalid Email or Password. Please try again.");
      }
      setLoading(false);
  };

  const handleActivate = async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');
      setLoading(true);
      
      if (email.toLowerCase() === 'admin@tap-log.com') {
          try {
              const userCredential = await createUserWithEmailAndPassword(auth, email, password);
              await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', userCredential.user.uid), {
                  empId: 'ADM001',
                  name: 'System Administrator',
                  email: email,
                  phone: '0000000000',
                  role: 'admin',
                  department: 'Management',
                  designation: 'Super Admin',
                  employeeCategory: 'Direct',
                  dutyType: 'Static',
                  status: 'Active',
                  allocatedSiteIds: []
              });
              alert("Super Admin Account Created! You can now login.");
              setIsRegistering(false);
          } catch (err: any) {
              setError(err.message);
          }
          setLoading(false);
          return;
      }

      const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'users'), where('email', '==', email));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
          setError("This email is not registered by Admin. Please contact HR.");
          setLoading(false);
          return;
      }

      const userDoc = querySnapshot.docs[0];
      const existingData = userDoc.data();

      try {
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', userCredential.user.uid), existingData);
          await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', userDoc.id));
          
          alert("Account Activated! You can now login.");
          setIsRegistering(false); 
      } catch (err: any) {
          if(err.code === 'auth/email-already-in-use') {
              setError("Account already active. Please Login.");
          } else {
              setError(err.message);
          }
      }
      setLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');
      if (!email) { setError("Please enter your email address."); return; }
      setLoading(true);
      try {
          await sendPasswordResetEmail(auth, email);
          alert("Password reset email sent! Check your inbox.");
          setResetMode(false);
      } catch (err: any) {
          setError(err.message);
      } finally {
        setLoading(false);
      }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-indigo-900 to-slate-900 p-6">
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full">
        <div className="mb-6 flex justify-center">
          <div className="bg-indigo-100 p-4 rounded-full">
            <img
              src={`${process.env.PUBLIC_URL}/Company_logo.png`}
              alt="Alert Intell"
              className="w-10 h-10 object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-slate-800 text-center mb-1">Alert Intell</h1>
        <p className="text-slate-500 text-center mb-6 text-sm">Field Force Management</p>

        {error && <div className="bg-red-50 text-red-600 p-3 rounded text-sm mb-4">{error}</div>}

        {resetMode ? (
             <form onSubmit={handleResetPassword} className="space-y-4">
                 <p className="text-sm text-slate-600">Enter your email to receive a password reset link.</p>
                 <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Email</label>
                    <div className="flex items-center border rounded-lg p-2 bg-slate-50">
                        <Mail className="w-5 h-5 text-slate-400 mr-2" />
                        <input type="email" required className="bg-transparent w-full outline-none" placeholder="name@company.com" value={email} onChange={e=>setEmail(e.target.value)} />
                    </div>
                </div>
                <button type="submit" disabled={loading} className="w-full bg-indigo-600 text-white py-3 rounded-lg font-bold hover:bg-indigo-700 transition disabled:opacity-50">
                    {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button type="button" onClick={() => {setResetMode(false); setError('');}} className="w-full text-slate-500 text-sm py-2">Back to Login</button>
             </form>
        ) : (
            <form onSubmit={isRegistering ? handleActivate : handleLogin} className="space-y-4">
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Email</label>
                    <div className="flex items-center border rounded-lg p-2 bg-slate-50">
                        <Mail className="w-5 h-5 text-slate-400 mr-2" />
                        <input type="email" required className="bg-transparent w-full outline-none" placeholder="name@company.com" value={email} onChange={e=>setEmail(e.target.value)} />
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Password</label>
                    <div className="flex items-center border rounded-lg p-2 bg-slate-50">
                        <Lock className="w-5 h-5 text-slate-400 mr-2" />
                        <input type="password" required className="bg-transparent w-full outline-none" placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)} />
                    </div>
                    {!isRegistering && (
                        <div className="text-right mt-1">
                            <button type="button" onClick={() => setResetMode(true)} className="text-xs text-indigo-600 hover:underline">Forgot Password?</button>
                        </div>
                    )}
                </div>

                <button type="submit" disabled={loading} className="w-full bg-indigo-600 text-white py-3 rounded-lg font-bold hover:bg-indigo-700 transition disabled:opacity-50">
                    {loading ? 'Processing...' : (isRegistering ? 'Activate Account' : 'Login')}
                </button>

                <div className="text-center pt-4 border-t border-slate-100">
                    {isRegistering ? (
                        <p className="text-sm text-slate-600">Already have an account? <button type="button" onClick={() => setIsRegistering(false)} className="text-indigo-600 font-bold hover:underline">Login</button></p>
                    ) : (
                        <p className="text-sm text-slate-600">First time here? <button type="button" onClick={() => setIsRegistering(true)} className="text-indigo-600 font-bold hover:underline">Activate Account</button></p>
                    )}
                </div>
            </form>
        )}
      </div>
    </div>
  );
}

// --- Admin/Manager Portal ---
function AdminPortal({ profile, onLogout }: { profile: UserProfile, onLogout: () => void }) {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'map' | 'employees' | 'departments' | 'approval_matrix' | 'sites' | 'tasks' | 'allocation' | 'reports' | 'approvals' | 'conveyance' | 'attendance_settings' | 'my_attendance'>('dashboard');
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotif, setShowNotif] = useState(false);
  // ...existing code...

  useEffect(() => {
      // Query notifications
      const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'notifications'), orderBy('timestamp', 'desc'), limit(20));
      const unsub = onSnapshot(q, (snap) => {
          const all = snap.docs.map(d => ({id: d.id, ...d.data()})) as Notification[];
          const myNotifs = all.filter(n => n.userId === profile.id || (profile.role === 'admin' && n.targetRole === 'admin') || (profile.role === 'manager' && n.targetRole === 'manager'));
          setNotifications(myNotifs);
      });
      return () => unsub();
  }, [profile]);

  const NavItem = ({ id, icon: Icon, label }: any) => (
    <button onClick={() => { setActiveTab(id); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === id ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}><Icon className="w-5 h-5" />{label}</button>
  );

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <div className="flex items-center gap-2 font-bold text-xl text-slate-800">
            <img
              src={`${process.env.PUBLIC_URL}/Company_logo.png`}
              alt="Alert Intell"
              className="w-7 h-7 object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
            <span>Alert Intell</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="md:hidden"><X className="w-6 h-6 text-slate-400" /></button>
        </div>
        <nav className="p-4 space-y-1">
          <div className="mb-2 text-xs font-semibold text-slate-400 uppercase px-4">Management</div>
          <NavItem id="dashboard" icon={CheckCircle} label="Dashboard" />
          <NavItem id="map" icon={MapIcon} label="Live Map" />
          {profile.role === 'admin' && <NavItem id="employees" icon={Users} label="Employees & Vendors" />}
          {profile.role === 'admin' && <NavItem id="departments" icon={Building} label="Departments" />}
          {profile.role === 'admin' && <NavItem id="approval_matrix" icon={FileSpreadsheet} label="Approval Matrix" />}
          <NavItem id="tasks" icon={CheckSquare} label="Tasks & Updates" />
          <NavItem id="approvals" icon={FileCheck} label="Approvals" />
          <NavItem id="conveyance" icon={Truck} label="Local Conveyance" />
          {profile.role === 'admin' && <NavItem id="sites" icon={Building} label="Manage Sites" />}
          {profile.role === 'admin' && <NavItem id="allocation" icon={Link} label="Allocation" />}
          {profile.role === 'admin' && <NavItem id="attendance_settings" icon={Calendar} label="Attendance Settings" />}
          <NavItem id="reports" icon={FileText} label="Reports" />

          <div className="mt-6 mb-2 text-xs font-semibold text-slate-400 uppercase px-4">Personal</div>
          <NavItem id="my_attendance" icon={Fingerprint} label="My Attendance" />
        </nav>
        <div className="absolute bottom-0 w-full p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">{profile.name[0]}</div>
            <div className="text-sm"><p className="font-medium text-slate-900">{profile.name}</p><p className="text-slate-500 text-xs capitalize">{profile.role}</p></div>
          </div>
          <button onClick={onLogout} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"><LogOut className="w-4 h-4" /> Sign Out</button>
        </div>
      </aside>
      <main className="flex-1 flex flex-col h-full overflow-hidden">
        <header className="bg-white border-b border-slate-200 p-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(true)} className="md:hidden"><Menu className="w-6 h-6 text-slate-600" /></button>
            <h1 className="font-semibold text-slate-800 capitalize">{activeTab.replace('_', ' ')}</h1>
          </div>
          <div className="relative">
              <button onClick={() => setShowNotif(!showNotif)} className="relative p-2 rounded-full hover:bg-slate-100">
                  <Bell className="w-6 h-6 text-slate-600" />
                  {notifications.filter(n => !n.read).length > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>}
              </button>
              {showNotif && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-100 z-50 overflow-hidden">
                      <div className="p-3 border-b border-slate-50 bg-slate-50 flex justify-between items-center">
                          <h3 className="font-bold text-sm">Notifications</h3>
                          <button onClick={() => setShowNotif(false)}><X className="w-4 h-4 text-slate-400" /></button>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                          {notifications.map(n => (
                              <div key={n.id} className="p-3 border-b border-slate-50 hover:bg-slate-50 text-sm">
                                  <div className="flex gap-2">
                                      {n.type === 'alert' ? <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" /> : <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />}
                                      <div>
                                          <p className="text-slate-800">{n.message}</p>
                                          <p className="text-xs text-slate-400 mt-1">{n.timestamp?.toDate().toLocaleTimeString()}</p>
                                      </div>
                                  </div>
                              </div>
                          ))}
                          {notifications.length === 0 && <div className="p-4 text-center text-slate-400 text-xs">No notifications</div>}
                      </div>
                  </div>
              )}
          </div>
        </header>
        <div className="flex-1 overflow-auto p-4 md:p-8">
          {activeTab === 'dashboard' && <AdminDashboard />}
          {activeTab === 'dashboard' && <div className="mt-8"><AdminDutyStates profile={profile} /></div>}
          {activeTab === 'map' && <>
            <AdminLiveMap />
            <div className="mt-8">
              <AdminLiveLocations profile={profile} />
            </div>
            <div className="mt-8">
              <AdminLocationTrails profile={profile} />
            </div>
            <div className="mt-8">
              <AdminTravelStops profile={profile} />
            </div>
          </>}
          {activeTab === 'employees' && profile.role === 'admin' && <AdminEmployees profile={profile} />}
          {activeTab === 'departments' && profile.role === 'admin' && <AdminDepartments profile={profile} />}
          {activeTab === 'approval_matrix' && profile.role === 'admin' && <AdminApprovalMatrix profile={profile} />}
          {activeTab === 'sites' && <AdminSites />}
          {activeTab === 'tasks' && <AdminTasks profile={profile} />}
          {activeTab === 'allocation' && <AdminAllocation />}
          {activeTab === 'reports' && <>
            <AdminReports />
            {profile.role === 'admin' && <div className="mt-8"><AdminSalarySlipBulkPdfUpload profile={profile} /></div>}
            <div className="mt-8">
              <SampleSalarySlipButton />
            </div>
          </>}
          {activeTab === 'approvals' && <>
            <AdminApprovals profile={profile} />
            <div className="mt-8">
              <AdminLeaveApprovals profile={profile} />
            </div>
            <div className="mt-8">
              <AdminExpenseApprovals profile={profile} />
            </div>
            <div className="mt-8">
              <AdminTravelApprovals profile={profile} />
            </div>
          </>}
          {activeTab === 'conveyance' && <AdminLocalConveyanceApprovals profile={profile} />}
          {activeTab === 'attendance_settings' && <AdminAttendanceSettings profile={profile} />}
          {/* Re-use the Employee App Logic for the Manager/Admin's own attendance */}
          {activeTab === 'my_attendance' && <EmployeeMobileApp profile={profile} onLogout={() => {}} />}
        </div>
      </main>
    </div>
  );
}

interface Department {
  id: string;
  name: string;
  hodUserId?: string;
  hodEmpId?: string;
  hodName?: string;
  isActive?: boolean;
  createdAt?: any;
  updatedAt?: any;
}

function AdminDepartments({ profile }: { profile: UserProfile }) {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [users, setUsers] = useState<Record<string, any>>({});

  const [name, setName] = useState('');
  const [hodUserId, setHodUserId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'artifacts', appId, 'public', 'data', 'departments'), orderBy('name', 'asc')),
      (snap) => {
        setDepartments(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Department[]);
      }
    );
    const unsubUsers = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'users'), (snap) => {
      const map: Record<string, any> = {};
      snap.docs.forEach((d) => {
        map[d.id] = d.data();
      });
      setUsers(map);
    });
    return () => {
      unsub();
      unsubUsers();
    };
  }, []);

  const userOptions = useMemo(() => {
    return Object.entries(users)
      .map(([uid, u]) => ({
        uid,
        empId: String((u as any)?.empId ?? ''),
        name: String((u as any)?.name ?? ''),
        role: String((u as any)?.role ?? ''),
        department: String((u as any)?.department ?? ''),
      }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [users]);

  const resetForm = () => {
    setName('');
    setHodUserId('');
    setIsActive(true);
  };

  const createDepartment = async () => {
    if (profile.role !== 'admin') return;
    setError('');
    const deptName = name.trim();
    if (!deptName) {
      setError('Department name is required.');
      return;
    }

    setSaving(true);
    try {
      const hod = hodUserId ? users[hodUserId] : null;
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'departments'), {
        name: deptName,
        hodUserId: hodUserId || '',
        hodEmpId: hod ? String((hod as any)?.empId ?? '') : '',
        hodName: hod ? String((hod as any)?.name ?? '') : '',
        isActive,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: profile.id,
        updatedByName: profile.name,
      });
      resetForm();
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const updateDepartment = async (deptId: string, patch: Partial<Department>) => {
    if (profile.role !== 'admin') return;
    setError('');
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'departments', deptId), {
        ...patch,
        updatedAt: serverTimestamp(),
        updatedBy: profile.id,
        updatedByName: profile.name,
      } as any);
    } catch (e: any) {
      setError(String(e?.message || e));
    }
  };

  const removeDepartment = async (deptId: string) => {
    if (profile.role !== 'admin') return;
    const ok = window.confirm('Delete this department?');
    if (!ok) return;
    setError('');
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'departments', deptId));
    } catch (e: any) {
      setError(String(e?.message || e));
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-2xl font-bold text-slate-800">Departments</h2>
        <p className="text-sm text-slate-500 mt-1">Create departments and assign a HOD for routing approvals.</p>

        {error && <div className="mt-4 bg-red-50 text-red-600 p-3 rounded text-sm">{error}</div>}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Department Name</label>
            <input className="w-full border rounded p-2 text-sm" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Operations" />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">HOD (optional)</label>
            <select className="w-full border rounded p-2 text-sm" value={hodUserId} onChange={(e) => setHodUserId(e.target.value)}>
              <option value="">-- Select HOD --</option>
              {userOptions.map((u) => (
                <option key={u.uid} value={u.uid}>
                  {u.name}{u.empId ? ` (${u.empId})` : ''}{u.department ? ` • ${u.department}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              Active
            </label>
            <button onClick={createDepartment} disabled={saving} className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-indigo-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Add'}
            </button>
          </div>
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2">Department</th>
                <th className="px-3 py-2">HOD</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {departments.map((d) => (
                <tr key={d.id} className="border-b">
                  <td className="px-3 py-2 font-medium text-slate-800">{d.name}</td>
                  <td className="px-3 py-2">
                    <select
                      className="border rounded p-2 text-xs"
                      value={String(d.hodUserId || '')}
                      onChange={(e) => {
                        const nextUid = e.target.value;
                        const hod = nextUid ? users[nextUid] : null;
                        updateDepartment(d.id, {
                          hodUserId: nextUid,
                          hodEmpId: hod ? String((hod as any)?.empId ?? '') : '',
                          hodName: hod ? String((hod as any)?.name ?? '') : '',
                        });
                      }}
                    >
                      <option value="">-- None --</option>
                      {userOptions.map((u) => (
                        <option key={u.uid} value={u.uid}>
                          {u.name}{u.empId ? ` (${u.empId})` : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => updateDepartment(d.id, { isActive: !d.isActive })}
                      className={`px-2 py-1 rounded text-xs font-bold ${d.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}
                    >
                      {d.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => removeDepartment(d.id)} className="text-xs text-red-600 font-bold hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
              {departments.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-slate-400 py-4">No departments created yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AdminApprovalMatrix({ profile }: { profile: UserProfile }) {
  const matrixRef = useMemo(() => doc(db, 'artifacts', appId, 'public', 'data', 'approvalMatrix', 'fundRequests'), []);
  const [users, setUsers] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [financeUserId, setFinanceUserId] = useState('');
  const [ceoUserId, setCeoUserId] = useState('');
  const [useDepartmentHod, setUseDepartmentHod] = useState(true);

  useEffect(() => {
    const unsubUsers = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'users'), (snap) => {
      const map: Record<string, any> = {};
      snap.docs.forEach((d) => {
        map[d.id] = d.data();
      });
      setUsers(map);
    });
    const unsubMatrix = onSnapshot(
      matrixRef,
      (snap) => {
        const data = (snap.data() as any) || {};
        setFinanceUserId(String(data.financeUserId || ''));
        setCeoUserId(String(data.ceoUserId || ''));
        setUseDepartmentHod(Boolean(data.useDepartmentHod ?? true));
        setLoading(false);
      },
      (err) => {
        setError(String(err?.message || err));
        setLoading(false);
      }
    );
    return () => {
      unsubUsers();
      unsubMatrix();
    };
  }, [matrixRef]);

  const userOptions = useMemo(() => {
    return Object.entries(users)
      .map(([uid, u]) => ({ uid, name: String((u as any)?.name ?? ''), empId: String((u as any)?.empId ?? ''), department: String((u as any)?.department ?? ''), role: String((u as any)?.role ?? '') }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [users]);

  const save = async () => {
    if (profile.role !== 'admin') return;
    setError('');
    setSaving(true);
    try {
      await setDoc(
        matrixRef,
        {
          financeUserId: financeUserId || '',
          financeEmpId: financeUserId ? String((users as any)?.[financeUserId]?.empId ?? '') : '',
          ceoUserId: ceoUserId || '',
          ceoEmpId: ceoUserId ? String((users as any)?.[ceoUserId]?.empId ?? '') : '',
          useDepartmentHod,
          updatedAt: serverTimestamp(),
          updatedBy: profile.id,
          updatedByName: profile.name,
        },
        { merge: true }
      );
      alert('Approval Matrix saved.');
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-2xl font-bold text-slate-800">Approval Matrix</h2>
        <p className="text-sm text-slate-500 mt-1">Configure approval routing defaults (currently used for Fund Requests).</p>

        {error && <div className="mt-4 bg-red-50 text-red-600 p-3 rounded text-sm">{error}</div>}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Finance Reviewer</label>
            <select className="w-full border rounded p-2 text-sm" value={financeUserId} onChange={(e) => setFinanceUserId(e.target.value)}>
              <option value="">-- Select Finance --</option>
              {userOptions.map((u) => (
                <option key={u.uid} value={u.uid}>
                  {u.name}{u.empId ? ` (${u.empId})` : ''}{u.department ? ` • ${u.department}` : ''}
                </option>
              ))}
            </select>
            <div className="text-[11px] text-slate-400 mt-1">Finance cannot reject (remarks + forward only).</div>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Final Approver (CEO/Admin)</label>
            <select className="w-full border rounded p-2 text-sm" value={ceoUserId} onChange={(e) => setCeoUserId(e.target.value)}>
              <option value="">-- Select CEO/Admin --</option>
              {userOptions.map((u) => (
                <option key={u.uid} value={u.uid}>
                  {u.name}{u.empId ? ` (${u.empId})` : ''}{u.role ? ` • ${u.role}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={useDepartmentHod} onChange={(e) => setUseDepartmentHod(e.target.checked)} />
            Use Department HOD as first approver (fallback to Reporting Manager if not set)
          </label>
        </div>

        <div className="mt-8 flex gap-3">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="bg-green-600 text-white px-5 py-2 rounded-lg font-bold hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Matrix'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminAttendanceSettings({ profile }: { profile: UserProfile }) {
  const settingsRef = useMemo(
    () => doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'attendance'),
    []
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  const [weekendDays, setWeekendDays] = useState<number[]>([0]); // 0=Sun..6=Sat
  const [holidayDate, setHolidayDate] = useState<string>('');
  const [holidays, setHolidays] = useState<string[]>([]); // YYYY-MM-DD

  const days = [
    { id: 0, label: 'Sun' },
    { id: 1, label: 'Mon' },
    { id: 2, label: 'Tue' },
    { id: 3, label: 'Wed' },
    { id: 4, label: 'Thu' },
    { id: 5, label: 'Fri' },
    { id: 6, label: 'Sat' },
  ];

  useEffect(() => {
    const unsub = onSnapshot(
      settingsRef,
      (snap) => {
        const data = snap.data() as any;
        const wdRaw: any[] = Array.isArray(data?.weekendDays) ? data.weekendDays : [0];
        const wd = wdRaw
          .map((n: any) => Number(n))
          .filter((n: number): n is number => Number.isFinite(n) && n >= 0 && n <= 6);
        const uniqueWd = Array.from(new Set<number>(wd)).sort((a, b) => a - b);

        const hsRaw: any[] = Array.isArray(data?.holidays) ? data.holidays : [];
        const hs = hsRaw.map((s: any) => String(s)).filter((s: string) => s.trim().length > 0);
        const uniqueHs = Array.from(new Set<string>(hs)).sort();

        setWeekendDays(uniqueWd.length > 0 ? uniqueWd : [0]);
        setHolidays(uniqueHs);
        setLoading(false);
      },
      (err) => {
        setError(String(err?.message || err));
        setLoading(false);
      }
    );
    return () => unsub();
  }, [settingsRef]);

  const toggleWeekend = (dayId: number) => {
    setWeekendDays((prev) => {
      const next = prev.includes(dayId) ? prev.filter((d) => d !== dayId) : [...prev, dayId];
      return next.sort((a, b) => a - b);
    });
  };

  const addHoliday = () => {
    const v = holidayDate.trim();
    if (!v) return;
    setHolidays((prev) => Array.from(new Set([...prev, v])).sort());
    setHolidayDate('');
  };

  const removeHoliday = (dateKey: string) => {
    setHolidays((prev) => prev.filter((d) => d !== dateKey));
  };

  const save = async () => {
    if (profile.role !== 'admin') return;
    setError('');
    setSaving(true);
    try {
      await setDoc(
        settingsRef,
        {
          weekendDays: weekendDays,
          holidays: holidays,
          updatedAt: serverTimestamp(),
          updatedBy: profile.id,
          updatedByName: profile.name,
        },
        { merge: true }
      );
      alert('Attendance settings saved.');
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-2xl font-bold text-slate-800">Attendance Settings</h2>
        <p className="text-sm text-slate-500 mt-1">
          Configure weekend days and holiday dates. These are used for month attendance calculations.
        </p>

        {error && <div className="mt-4 bg-red-50 text-red-600 p-3 rounded text-sm">{error}</div>}

        <div className="mt-6">
          <div className="text-xs font-bold text-slate-500 uppercase mb-2">Weekend Days</div>
          <div className="flex flex-wrap gap-2">
            {days.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => toggleWeekend(d.id)}
                className={`px-3 py-2 rounded-lg border text-sm transition ${
                  weekendDays.includes(d.id)
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <div className="text-xs text-slate-400 mt-2">Tip: select multiple days if your company has 2-day weekends.</div>
        </div>

        <div className="mt-8">
          <div className="text-xs font-bold text-slate-500 uppercase mb-2">Holidays (YYYY-MM-DD)</div>
          <div className="flex flex-col md:flex-row gap-2">
            <input
              type="date"
              value={holidayDate}
              onChange={(e) => setHolidayDate(e.target.value)}
              className="border rounded-lg px-3 py-2 bg-slate-50"
            />
            <button
              type="button"
              onClick={addHoliday}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-indigo-700"
            >
              Add Holiday
            </button>
          </div>

          <div className="mt-4">
            {holidays.length === 0 ? (
              <div className="text-sm text-slate-400">No holidays added.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {holidays.map((h) => (
                  <div key={h} className="flex items-center justify-between gap-2 border border-slate-200 rounded-lg px-3 py-2 bg-white">
                    <div className="text-sm text-slate-700 font-mono">{h}</div>
                    <button
                      type="button"
                      onClick={() => removeHoliday(h)}
                      className="text-xs text-red-600 font-bold hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="bg-green-600 text-white px-5 py-2 rounded-lg font-bold hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- MODULE COMPONENTS ---

// LEAVE REQUEST TYPES
interface LeaveRequest {
  id: string;
  userId: string;
  userName: string;
  startDate: any;
  endDate: any;
  reason: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  createdAt: any;
}

const formatLeaveDate = (value: any): string => {
  try {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value?.toDate === 'function') return value.toDate().toLocaleDateString();
    if (typeof value === 'number' && Number.isFinite(value)) return new Date(value).toLocaleDateString();
    if (typeof value === 'object') {
      const seconds = Number((value as any).seconds);
      const nanos = Number((value as any).nanoseconds);
      if (Number.isFinite(seconds)) {
        const ms = seconds * 1000 + (Number.isFinite(nanos) ? Math.floor(nanos / 1e6) : 0);
        return new Date(ms).toLocaleDateString();
      }
    }
    return String(value);
  } catch {
    return '';
  }
};

const pickLeaveDate = (lr: any, keys: string[]): string => {
  for (const k of keys) {
    const v = lr?.[k];
    const s = formatLeaveDate(v);
    if (s) return s;
  }
  return '-';
};

// EMPLOYEE LEAVE REQUESTS COMPONENT
function EmployeeLeaveRequests({ profile }: { profile: UserProfile }) {
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'leaveRequests'),
      where('userId', '==', profile.id),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setLeaveRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })) as LeaveRequest[]);
    });
    return () => unsub();
  }, [profile.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!startDate || !endDate || !reason) {
      setError('All fields are required.');
      return;
    }
    setLoading(true);
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'leaveRequests'), {
        userId: profile.id,
        userName: profile.name,
        startDate,
        endDate,
        reason,
        status: 'Pending',
        createdAt: serverTimestamp(),
      });
      setStartDate('');
      setEndDate('');
      setReason('');
    } catch (err: any) {
      setError('Failed to submit leave request.');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white p-6 rounded-xl shadow border">
        <h2 className="text-xl font-bold mb-4">Apply for Leave</h2>
        {error && <div className="bg-red-50 text-red-600 p-2 rounded mb-2 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Start Date</label>
            <input type="date" className="w-full border rounded p-2" value={startDate} onChange={e => setStartDate(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">End Date</label>
            <input type="date" className="w-full border rounded p-2" value={endDate} onChange={e => setEndDate(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Reason</label>
            <textarea className="w-full border rounded p-2" value={reason} onChange={e => setReason(e.target.value)} required />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-indigo-600 text-white py-2 rounded font-bold hover:bg-indigo-700 transition disabled:opacity-50">
            {loading ? 'Submitting...' : 'Submit Leave Request'}
          </button>
        </form>
      </div>
      <div className="bg-white p-6 rounded-xl shadow border">
        <h2 className="text-xl font-bold mb-4">My Leave Requests</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2">Start</th>
                <th className="px-3 py-2">End</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {leaveRequests.map(lr => (
                <tr key={lr.id} className="border-b">
                  <td className="px-3 py-2">{pickLeaveDate(lr as any, ['startDate', 'fromDate', 'start', 'from', 'leaveFrom', 'dateFrom'])}</td>
                  <td className="px-3 py-2">{pickLeaveDate(lr as any, ['endDate', 'toDate', 'end', 'to', 'leaveTo', 'dateTo'])}</td>
                  <td className="px-3 py-2">{lr.reason}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${lr.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' : lr.status === 'Approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{lr.status}</span>
                  </td>
                </tr>
              ))}
              {leaveRequests.length === 0 && <tr><td colSpan={4} className="text-center text-slate-400 py-4">No leave requests found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </div>
  );
}

// ADMIN/MANAGER LEAVE APPROVALS COMPONENT
function AdminLeaveApprovals({ profile }: { profile: UserProfile }) {
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [userProfiles, setUserProfiles] = useState<{ [id: string]: any }>({});

  const teamUserIds = useMemo(() => {
    if (profile.role !== 'manager') return null;
    return buildManagerTeamUserIdSet(profile, userProfiles);
  }, [profile, userProfiles]);

  const visibleLeaveRequests = useMemo(() => {
    if (profile.role !== 'manager' || !teamUserIds) return leaveRequests;
    return leaveRequests.filter((lr) => teamUserIds.has(lr.userId));
  }, [profile.role, leaveRequests, teamUserIds]);

  const getEmployeeName = (lr: LeaveRequest) => {
    const fromProfile = (userProfiles as any)?.[lr.userId]?.name;
    const fromDoc = (lr as any)?.userName || (lr as any)?.name;
    return (fromDoc || fromProfile || '-').toString();
  };

  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'leaveRequests'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setLeaveRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })) as LeaveRequest[]);
    });
    const unsubUsers = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'users'), (snap) => {
      const profiles: { [id: string]: any } = {};
      snap.docs.forEach(d => { profiles[d.id] = d.data(); });
      setUserProfiles(profiles);
    });
    return () => {
      unsub();
      unsubUsers();
    };
  }, []);

  const handleApprove = async (id: string) => {
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'leaveRequests', id), { status: 'Approved' });
  };
  const handleReject = async (id: string) => {
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'leaveRequests', id), { status: 'Rejected' });
  };

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow border max-w-3xl mx-auto">
        <h2 className="text-xl font-bold mb-4">Leave Requests</h2>
        {/* <button onClick={downloadLeaveExcel} className="mb-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">Download Excel</button> */}
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2">Employee Name</th>
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Department</th>
              <th className="px-3 py-2">Designation</th>
              <th className="px-3 py-2">Start</th>
              <th className="px-3 py-2">End</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {visibleLeaveRequests.map(lr => (
              <tr key={lr.id} className="border-b">
                <td className="px-3 py-2">{getEmployeeName(lr)}</td>
                <td className="px-3 py-2">{userProfiles[lr.userId]?.empId || '-'}</td>
                <td className="px-3 py-2">{userProfiles[lr.userId]?.department || '-'}</td>
                <td className="px-3 py-2">{userProfiles[lr.userId]?.designation || '-'}</td>
                <td className="px-3 py-2">{pickLeaveDate(lr as any, ['startDate', 'fromDate', 'start', 'from', 'leaveFrom', 'dateFrom'])}</td>
                <td className="px-3 py-2">{pickLeaveDate(lr as any, ['endDate', 'toDate', 'end', 'to', 'leaveTo', 'dateTo'])}</td>
                <td className="px-3 py-2">{lr.reason}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-1 rounded text-xs font-bold ${lr.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' : lr.status === 'Approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{lr.status}</span>
                </td>
                <td className="px-3 py-2 flex gap-2">
                  {lr.status === 'Pending' && <>
                    <button onClick={() => handleApprove(lr.id)} className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700">Approve</button>
                    <button onClick={() => handleReject(lr.id)} className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700">Reject</button>
                  </>}
                </td>
              </tr>
            ))}
            {visibleLeaveRequests.length === 0 && <tr><td colSpan={9} className="text-center text-slate-400 py-4">No leave requests found.</td></tr>}
          </tbody>
        </table>
        {/* ...existing code... */}
      </div>
    </div>
  );
}

function AdminApprovals({ profile }: { profile: UserProfile }) {
    const [pendingRequests, setPendingRequests] = useState<AttendanceRecord[]>([]);
    const [userProfiles, setUserProfiles] = useState<Record<string, any>>({});

    const teamUserIds = useMemo(() => {
      if (profile.role !== 'manager') return null;
      return buildManagerTeamUserIdSet(profile, userProfiles);
    }, [profile, userProfiles]);

    const visiblePendingRequests = useMemo(() => {
      if (profile.role !== 'manager' || !teamUserIds) return pendingRequests;
      return pendingRequests.filter((r) => teamUserIds.has(r.userId));
    }, [profile.role, pendingRequests, teamUserIds]);

    useEffect(() => {
        // Filter requests based on role
        let statusFilter = [];
        if (profile.role === 'manager') statusFilter = ['Pending Manager'];
        else statusFilter = ['Pending HR', 'Pending Manager']; // Admin/HR sees all

        const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'attendance'), 
            where('status', 'in', statusFilter), 
            orderBy('timestamp', 'desc'));
        
        const unsub = onSnapshot(q, (snap) => {
            setPendingRequests(snap.docs.map(d => ({id: d.id, ...d.data()})) as AttendanceRecord[]);
        });

        const unsubUsers = onSnapshot(
          collection(db, 'artifacts', appId, 'public', 'data', 'users'),
          (snap) => {
            const profiles: Record<string, any> = {};
            snap.docs.forEach((d) => {
              profiles[d.id] = d.data();
            });
            setUserProfiles(profiles);
          }
        );

        return () => {
          unsub();
          unsubUsers();
        };
    }, [profile.role]);

    const handleApprove = async (record: AttendanceRecord) => {
        const ref = doc(db, 'artifacts', appId, 'public', 'data', 'attendance', record.id);
        
        if (record.status === 'Pending Manager') {
            const requiresHr = (record as any).requiresHrApproval === true;

            if (requiresHr) {
              // Move to HR
              await updateDoc(ref, { 
                  status: 'Pending HR',
                  managerApproved: true,
              });
              alert("Approved! Forwarded to HR.");
            } else {
              // Manager verification is final (off-site duty approval)
              await updateDoc(ref, {
                status: 'Verified',
                managerApproved: true,
                approvedAt: serverTimestamp(),
              });
              alert("Attendance Verified Successfully!");

              await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'notifications'), {
                userId: record.userId,
                message: `Your attendance for ${record.timestamp?.toDate().toLocaleDateString()} has been APPROVED by manager.`,
                type: 'success',
                read: false,
                timestamp: serverTimestamp()
              });
            }

        } else if (record.status === 'Pending HR') {
            // Verify
            await updateDoc(ref, { 
                status: 'Verified',
                hrApproved: true,
            });
            alert("Attendance Verified Successfully!");

            // Notify Employee
            await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'notifications'), {
                userId: record.userId,
                message: `Your attendance for ${record.timestamp?.toDate().toLocaleDateString()} has been APPROVED.`,
                type: 'success',
                read: false,
                timestamp: serverTimestamp()
            });
        }
    };

    const handleReject = async (id: string) => {
        if(window.confirm("Reject this attendance request?")) {
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'attendance', id), { status: 'Rejected' });
        }
    };

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-800">Attendance Approvals</h2>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {visiblePendingRequests.length === 0 ? (
                    <div className="p-12 text-center text-slate-400 flex flex-col items-center">
                        <UserCheck className="w-12 h-12 mb-4 text-slate-200" />
                        <p>No pending approvals found.</p>
                    </div>
                ) : (
                    <table className="w-full text-sm text-left">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                <th className="px-6 py-3">Time</th>
                                <th className="px-6 py-3">Employee</th>
                                <th className="px-6 py-3">Site / Location</th>
                                <th className="px-6 py-3">Current Status</th>
                                <th className="px-6 py-3">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {visiblePendingRequests.map((req) => (
                                <tr key={req.id} className="hover:bg-slate-50">
                                    <td className="px-6 py-4">{req.timestamp?.toDate().toLocaleString()}</td>
                                    <td className="px-6 py-4 font-medium">{req.userName}</td>
                                    <td className="px-6 py-4 text-slate-500">
                                        <div className="flex flex-col gap-1">
                                          <span className={`text-xs px-2 py-1 rounded inline-block w-fit ${(req.onSite === true) ? 'bg-green-50 text-green-700' : (req.onSite === false) ? 'bg-amber-50 text-amber-700' : 'bg-slate-50 text-slate-600'}`}>
                                            {(req.onSite === true) ? 'On Site' : (req.onSite === false) ? 'Off Site' : 'Unknown'}
                                            {req.siteName ? ` • ${req.siteName}` : ''}
                                            {(typeof req.distanceToSiteMeters === 'number') ? ` • ${Math.round(req.distanceToSiteMeters)}m` : ''}
                                          </span>
                                          <span className="text-xs text-slate-400">Lat: {req.latitude}, Lng: {req.longitude}</span>
                                        </div>
                                        <br/>
                                        <a href={`https://maps.google.com/?q=${req.latitude},${req.longitude}`} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 underline mt-1 inline-block">View on Google Maps</a>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded text-xs font-bold ${req.status === 'Pending Manager' ? 'bg-yellow-100 text-yellow-700' : 'bg-purple-100 text-purple-700'}`}>{req.status}</span>
                                    </td>
                                    <td className="px-6 py-4 flex gap-2">
                                        <button onClick={() => handleApprove(req)} className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700">Approve</button>
                                        <button onClick={() => handleReject(req.id)} className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700">Reject</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function AdminDashboard() {
  const [stats, setStats] = useState({ active: 0, checkedIn: 0, sites: 0 });
  const [recentActivity, setRecentActivity] = useState<AttendanceRecord[]>([]);
  const [userProfiles, setUserProfiles] = useState<Record<string, UserProfile>>({});
  const [sites, setSites] = useState<Site[]>([]);
  const [heartbeats, setHeartbeats] = useState<Record<string, any>>({});
  const [selectedHbLocation, setSelectedHbLocation] = useState<
    | null
    | {
        uid: string;
        name: string;
        empId: string;
        lat: number;
        lng: number;
        accuracyM?: number;
        atText: string;
      }
  >(null);

  const [geoFenceTestEmpId, setGeoFenceTestEmpId] = useState('AI1877');
  const [geoFenceTestDistanceM, setGeoFenceTestDistanceM] = useState<number>(250);
  const [geoFenceTestBusy, setGeoFenceTestBusy] = useState(false);
  const [geoFenceTestResult, setGeoFenceTestResult] = useState<any>(null);
  const [geoFenceTestError, setGeoFenceTestError] = useState<string | null>(null);

  const runGeoFenceTest = async () => {
    const empId = geoFenceTestEmpId.trim();
    if (!empId) {
      setGeoFenceTestError('Enter EmpID');
      return;
    }

    setGeoFenceTestBusy(true);
    setGeoFenceTestError(null);
    setGeoFenceTestResult(null);
    try {
      const res: any = await adminSimulateGeoFenceOutCallable({
        appId,
        empId,
        distanceM: geoFenceTestDistanceM,
      });
      setGeoFenceTestResult(res?.data ?? res);
    } catch (e: any) {
      const msg = String(e?.message || e);
      setGeoFenceTestError(msg);
    } finally {
      setGeoFenceTestBusy(false);
    }
  };

  const toDateSafe = (value: any): Date | null => {
    try {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value?.toDate === 'function') return value.toDate();
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  const trackingComplianceRows = useMemo(() => {
    const now = Date.now();
    const WARN_MS = 10 * 60 * 1000;
    const STALE_MS = 30 * 60 * 1000;

    const isTrackingDisabled = (hb: any) => {
      const gpsEnabled = hb?.gpsEnabled;
      if (gpsEnabled === false) return true;
      const perm = String(hb?.permission || '').toLowerCase();
      if (!perm) return false;
      if (perm.includes('denied')) return true;
      if (perm.includes('restricted')) return true;
      if (perm.includes('disabled')) return true;
      return false;
    };

    const isEmployeeProfile = (p: any) => {
      if (!p) return false;
      if (String(p?.employeeCategory || '').toLowerCase() === 'vendor') return false;
      const role = String(p?.role || '').toLowerCase();
      return !role.includes('admin');
    };

    const rows = Object.entries(userProfiles)
      .filter(([, p]) => isEmployeeProfile(p))
      .map(([uid, p]) => {
        const hb = heartbeats[uid];
        const lastSeen = toDateSafe(hb?.lastSeenAt);
        const ageMs = lastSeen ? now - lastSeen.getTime() : Number.POSITIVE_INFINITY;
        const trackingOff = isTrackingDisabled(hb);

        const locLatRaw = hb?.locationLat;
        const locLngRaw = hb?.locationLng;
        const locationLat = typeof locLatRaw === 'number' ? locLatRaw : null;
        const locationLng = typeof locLngRaw === 'number' ? locLngRaw : null;
        const locationAccuracyM = typeof hb?.locationAccuracyM === 'number' ? hb.locationAccuracyM : null;
        const locClientAt =
          typeof hb?.locationClientAtUtcMs === 'number'
            ? new Date(hb.locationClientAtUtcMs)
            : toDateSafe(hb?.locationClientAtUtcMs);
        const locationAtText = locClientAt ? locClientAt.toLocaleString() : '';

        let status: 'GREEN' | 'YELLOW' | 'RED' = 'GREEN';
        if (trackingOff) status = 'RED';
        else if (!isFinite(ageMs) || ageMs > STALE_MS) status = 'RED';
        else if (ageMs > WARN_MS) status = 'YELLOW';

        const ageMin = isFinite(ageMs) ? Math.round(ageMs / 60000) : null;

        return {
          uid,
          empId: String((p as any)?.empId || '-'),
          name: String((p as any)?.name || uid),
          department: String((p as any)?.department || ''),
          allocatedSites: Array.isArray((p as any)?.allocatedSiteIds) ? (p as any)?.allocatedSiteIds.length : 0,
          status,
          ageMin,
          lastSeenText: lastSeen ? lastSeen.toLocaleString() : '—',
          appState: String(hb?.appState || ''),
          platform: String(hb?.platform || ''),
          permission: String(hb?.permission || ''),
          gpsEnabled: hb?.gpsEnabled,
          locationLat,
          locationLng,
          locationAccuracyM,
          locationAtText,
          reason:
            trackingOff
              ? 'Tracking disabled'
              : !lastSeen
                ? 'No heartbeat yet'
                : ageMs > STALE_MS
                  ? 'No heartbeat > 30 min'
                  : ageMs > WARN_MS
                    ? 'Stale heartbeat'
                    : 'OK',
        };
      })
      .sort((a, b) => {
        const rank = (s: string) => (s === 'RED' ? 0 : s === 'YELLOW' ? 1 : 2);
        const dr = rank(a.status) - rank(b.status);
        if (dr !== 0) return dr;
        const aa = a.ageMin ?? 999999;
        const bb = b.ageMin ?? 999999;
        return bb - aa;
      });

    return rows;
  }, [userProfiles, heartbeats]);

  const dateKeyLocal = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const todaysSummary = useMemo(() => {
    const todayKey = dateKeyLocal(new Date());
    const byUser = new Map<
      string,
      {
        userId: string;
        userName: string;
        empId: string;
        firstInAt: Date | null;
        lastOutAt: Date | null;
        lastPunchAt: Date | null;
        lastPunchType: AttendanceRecord['type'] | null;
        lastLatitude: number | null;
        lastLongitude: number | null;
        lastSiteName?: string;
        lastLocationText?: string;
      }
    >();

    (recentActivity || []).forEach((r) => {
      const ts = r.timestamp?.toDate?.();
      if (!ts) return;
      if (dateKeyLocal(ts) !== todayKey) return;

      const empId =
        String((r.empId || '').toString().trim() || (userProfiles[r.userId]?.empId || '').toString().trim() || '-') ||
        '-';

      const lat = toFiniteNumber((r as any).latitude);
      const lng = toFiniteNumber((r as any).longitude);

      const curr = byUser.get(r.userId) || {
        userId: r.userId,
        userName: r.userName,
        empId,
        firstInAt: null,
        lastOutAt: null,
        lastPunchAt: null,
        lastPunchType: null,
        lastLatitude: null,
        lastLongitude: null,
        lastSiteName: undefined,
        lastLocationText: undefined,
      };

      // first IN of day
      if (r.type === 'in') {
        if (!curr.firstInAt || ts.getTime() < curr.firstInAt.getTime()) curr.firstInAt = ts;
      }

      // last OUT of day
      if (r.type === 'out') {
        if (!curr.lastOutAt || ts.getTime() > curr.lastOutAt.getTime()) curr.lastOutAt = ts;
      }

      // last punch (for status + location)
      if (!curr.lastPunchAt || ts.getTime() > curr.lastPunchAt.getTime()) {
        curr.lastPunchAt = ts;
        curr.lastPunchType = r.type;
        curr.lastLatitude = lat;
        curr.lastLongitude = lng;
        curr.lastSiteName = r.siteName;
        curr.lastLocationText = r.locationText;
        curr.userName = r.userName || curr.userName;
        curr.empId = empId;
      }

      byUser.set(r.userId, curr);
    });

    const list = Array.from(byUser.values()).sort((a, b) => {
      const ta = a.lastPunchAt?.getTime?.() ?? 0;
      const tb = b.lastPunchAt?.getTime?.() ?? 0;
      return tb - ta;
    });

    return list;
  }, [recentActivity, userProfiles]);

  useEffect(() => {
    const unsubUsers = onSnapshot(
      collection(db, 'artifacts', appId, 'public', 'data', 'users'),
      (snap) => {
        const map: Record<string, UserProfile> = {};
        snap.docs.forEach((d) => {
          map[d.id] = { id: d.id, ...(d.data() as any) } as UserProfile;
        });
        setUserProfiles(map);
      }
    );

    const unsubSites = onSnapshot(
      collection(db, 'artifacts', appId, 'public', 'data', 'sites'),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) } as Site));
        setSites(list);
      }
    );

    const unsubHeartbeats = onSnapshot(
      collection(db, 'artifacts', appId, 'public', 'data', 'heartbeats'),
      (snap) => {
        const map: Record<string, any> = {};
        snap.docs.forEach((d) => {
          map[d.id] = { id: d.id, ...(d.data() as any) };
        });
        setHeartbeats(map);
      }
    );

    // Pull a larger window so we can compute today's IN/OUT for each employee.
    const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'attendance'), orderBy('timestamp', 'desc'), limit(300)); 
    const unsub = onSnapshot(q, (snapshot) => {
      const records = snapshot.docs.map(d => ({id: d.id, ...d.data()})) as AttendanceRecord[];
      setRecentActivity(records); 
      const uniqueUsers = new Set(records.map(r => r.userId)).size;
      const checkedIn = records.filter(r => r.type === 'in').length;
      setStats(s => ({...s, active: uniqueUsers, checkedIn}));
    }, (err) => console.error("Dash err", err));
    return () => {
      unsubUsers();
      unsubSites();
      unsubHeartbeats();
      unsub();
    };
  }, []);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200"><h3 className="text-slate-500 text-sm font-medium">Active Employees Today</h3><p className="text-3xl font-bold text-slate-900 mt-2">{stats.active}</p></div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200"><h3 className="text-slate-500 text-sm font-medium">Last Punch In</h3><p className="text-3xl font-bold text-slate-900 mt-2 text-green-600">{stats.checkedIn}</p></div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200"><h3 className="text-slate-500 text-sm font-medium">System Status</h3><div className="flex items-center gap-2 mt-2"><span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span><p className="text-lg font-medium text-slate-900">Live</p></div></div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold text-slate-800">Geo-fence Test (Admin)</h2>
            <p className="text-xs text-slate-500 mt-1">Simulates employee moving &gt;100m from allocated site and triggers notifications to Manager + HR + Admin.</p>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">EmpID</label>
              <input
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={geoFenceTestEmpId}
                onChange={(e) => setGeoFenceTestEmpId(e.target.value)}
                placeholder="AI1877"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Distance (m)</label>
              <input
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                type="number"
                min={150}
                max={5000}
                value={geoFenceTestDistanceM}
                onChange={(e) => setGeoFenceTestDistanceM(Number(e.target.value || 250))}
              />
              <div className="text-[11px] text-slate-400 mt-1">Uses ~111km per latitude degree approximation.</div>
            </div>
            <div className="flex items-end">
              <button
                className={`w-full px-4 py-2 rounded-lg text-sm font-medium ${geoFenceTestBusy ? 'bg-slate-200 text-slate-500' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                disabled={geoFenceTestBusy}
                onClick={runGeoFenceTest}
              >
                {geoFenceTestBusy ? 'Sending…' : 'Send Test'}
              </button>
            </div>
          </div>

          {geoFenceTestError ? (
            <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-3">{geoFenceTestError}</div>
          ) : null}

          {geoFenceTestResult ? (
            <div className="mt-4 text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-4 py-3 overflow-x-auto">
              <div className="font-semibold text-slate-700 mb-2">Result</div>
              <pre className="whitespace-pre-wrap">{JSON.stringify(geoFenceTestResult, null, 2)}</pre>
            </div>
          ) : null}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold text-slate-800">Tracking Compliance (Heartbeat)</h2>
            <p className="text-xs text-slate-500 mt-1">Green: ≤10m • Yellow: 10–30m • Red: &gt;30m or Tracking Disabled</p>
          </div>
          <div className="text-xs text-slate-500">Total: {trackingComplianceRows.length}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Employee</th>
                <th className="px-6 py-3 font-medium">Dept</th>
                <th className="px-6 py-3 font-medium">Last Seen</th>
                <th className="px-6 py-3 font-medium">Age</th>
                <th className="px-6 py-3 font-medium">Platform</th>
                <th className="px-6 py-3 font-medium">Last Location</th>
                <th className="px-6 py-3 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {trackingComplianceRows.map((r) => (
                <tr key={r.uid} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        r.status === 'GREEN'
                          ? 'bg-green-100 text-green-700'
                          : r.status === 'YELLOW'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-900">{r.name}</div>
                    <div className="text-xs text-slate-500 font-mono">{r.empId}</div>
                  </td>
                  <td className="px-6 py-4 text-slate-500">{r.department || '—'}</td>
                  <td className="px-6 py-4 text-slate-500">{r.lastSeenText}</td>
                  <td className="px-6 py-4 text-slate-500">{r.ageMin === null ? '—' : `${r.ageMin} min`}</td>
                  <td className="px-6 py-4 text-slate-500">
                    {(r.platform || '—') + (r.appState ? ` • ${r.appState}` : '')}
                  </td>
                  <td className="px-6 py-4 text-slate-500">
                    {typeof r.locationLat === 'number' && typeof r.locationLng === 'number' ? (
                      <div className="flex items-center gap-2">
                        <button
                          className="text-indigo-600 hover:text-indigo-700 font-medium"
                          onClick={() =>
                            setSelectedHbLocation({
                              uid: r.uid,
                              name: r.name,
                              empId: r.empId,
                              lat: r.locationLat as number,
                              lng: r.locationLng as number,
                              accuracyM: typeof r.locationAccuracyM === 'number' ? (r.locationAccuracyM as number) : undefined,
                              atText: r.locationAtText || r.lastSeenText,
                            })
                          }
                        >
                          View
                        </button>
                        <a
                          className="text-slate-400 hover:text-slate-600"
                          href={`https://www.google.com/maps?q=${r.locationLat},${r.locationLng}`}
                          target="_blank"
                          rel="noreferrer"
                          title="Open in Google Maps"
                        >
                          <ExternalLink size={16} />
                        </a>
                      </div>
                    ) : (
                      <span>—</span>
                    )}
                    {typeof r.locationAccuracyM === 'number' && (
                      <div className="text-[11px] text-slate-400 mt-1">acc: ~{Math.round(r.locationAccuracyM)}m</div>
                    )}
                    {r.locationAtText && <div className="text-[11px] text-slate-400">at: {r.locationAtText}</div>}
                  </td>
                  <td className="px-6 py-4 text-slate-500">
                    <div>{r.reason}</div>
                    {(r.permission || r.gpsEnabled === false) && (
                      <div className="text-[11px] text-slate-400 mt-1">
                        {r.permission ? `perm: ${r.permission}` : ''}
                        {r.gpsEnabled === false ? `${r.permission ? ' • ' : ''}gps: off` : ''}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {trackingComplianceRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-10 text-center text-slate-400">No employees found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedHbLocation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-3xl overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900">Last Known Location</div>
                <div className="text-xs text-slate-500 mt-1">
                  {selectedHbLocation.name} • {selectedHbLocation.empId}
                  {selectedHbLocation.atText ? ` • ${selectedHbLocation.atText}` : ''}
                </div>
              </div>
              <button
                className="text-slate-500 hover:text-slate-700"
                onClick={() => setSelectedHbLocation(null)}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="h-[420px]">
              <MapContainer
                center={[selectedHbLocation.lat, selectedHbLocation.lng] as LatLngTuple}
                zoom={16}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <CircleMarker
                  center={[selectedHbLocation.lat, selectedHbLocation.lng] as LatLngTuple}
                  radius={10}
                  pathOptions={{ color: '#4f46e5', fillColor: '#4f46e5', fillOpacity: 0.35 }}
                >
                  <Popup>
                    <div className="text-sm">
                      <div className="font-semibold">{selectedHbLocation.name}</div>
                      <div className="text-xs">{selectedHbLocation.empId}</div>
                      <div className="text-xs mt-1">{selectedHbLocation.lat.toFixed(6)}, {selectedHbLocation.lng.toFixed(6)}</div>
                      {typeof selectedHbLocation.accuracyM === 'number' && (
                        <div className="text-xs">Accuracy: ~{Math.round(selectedHbLocation.accuracyM)} m</div>
                      )}
                    </div>
                  </Popup>
                </CircleMarker>
              </MapContainer>
            </div>
            <div className="p-4 border-t border-slate-100 flex items-center justify-between">
              <a
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                href={`https://www.google.com/maps?q=${selectedHbLocation.lat},${selectedHbLocation.lng}`}
                target="_blank"
                rel="noreferrer"
              >
                Open in Google Maps
              </a>
              <button
                className="px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() => setSelectedHbLocation(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100"><h2 className="font-semibold text-slate-800">Live Attendance Feed</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-6 py-3 font-medium">Date</th>
                <th className="px-6 py-3 font-medium">IN Time</th>
                <th className="px-6 py-3 font-medium">OUT Time</th>
                <th className="px-6 py-3 font-medium">Employee ID</th>
                <th className="px-6 py-3 font-medium">Employee Name</th>
                <th className="px-6 py-3 font-medium">Action</th>
                <th className="px-6 py-3 font-medium">Location</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {todaysSummary.map((row) => {
                const ts = row.lastPunchAt;
                const dateText = ts ? ts.toLocaleDateString() : 'Today';
                const inText = row.firstInAt ? row.firstInAt.toLocaleTimeString() : '-';
                const outText = row.lastOutAt ? row.lastOutAt.toLocaleTimeString() : '-';

                const resolved = resolveSiteForPoint(row.lastLatitude, row.lastLongitude, sites);
                const resolvedLocationLabel =
                  resolved?.site?.name || row.lastSiteName || row.lastLocationText || 'View on Map';

                const latForLink = row.lastLatitude ?? 0;
                const lngForLink = row.lastLongitude ?? 0;

                return (
                  <tr key={row.userId} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-slate-500">{dateText}</td>
                    <td className="px-6 py-4 text-slate-500">{inText}</td>
                    <td className="px-6 py-4 text-slate-500">{outText}</td>
                    <td className="px-6 py-4 font-mono text-slate-700">{row.empId}</td>
                    <td className="px-6 py-4 font-medium text-slate-900">{row.userName}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          row.lastPunchType === 'in'
                            ? 'bg-green-100 text-green-700'
                            : row.lastPunchType === 'out'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {String(row.lastPunchType || '').toUpperCase() || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-500 truncate max-w-xs">
                      <a
                        href={`https://www.google.com/maps?q=${latForLink},${lngForLink}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 text-indigo-600 hover:underline"
                      >
                        <MapPin className="w-3 h-3" />
                        {resolvedLocationLabel}
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AdminLiveMap() {
    const [latestLocations, setLatestLocations] = useState<AttendanceRecord[]>([]);
  const [userProfiles, setUserProfiles] = useState<Record<string, UserProfile>>({});
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<{ lat: number; lng: number } | null>(null);
  const [searchText, setSearchText] = useState('');

    useEffect(() => {
        const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'attendance'), orderBy('timestamp', 'desc'), limit(300));
        const unsub = onSnapshot(q, (snap) => {
            const rawRecords = snap.docs.map(d => ({id: d.id, ...d.data()})) as AttendanceRecord[];
            const uniqueMap = new Map();
            // Keep only the latest punch for each user
            rawRecords.forEach(r => { 
                if(!uniqueMap.has(r.userId)) { uniqueMap.set(r.userId, r); } 
            });
            setLatestLocations(Array.from(uniqueMap.values()));
        });
    return () => { unsub(); };
    }, []);

    useEffect(() => {
      const unsubUsers = onSnapshot(
        collection(db, 'artifacts', appId, 'public', 'data', 'users'),
        (snap) => {
          const map: Record<string, UserProfile> = {};
          snap.docs.forEach((d) => {
            map[d.id] = { id: d.id, ...(d.data() as any) } as UserProfile;
          });
          setUserProfiles(map);
        }
      );
      return () => {
        unsubUsers();
      };
    }, []);

    useEffect(() => {
      const unsubSites = onSnapshot(
        collection(db, 'artifacts', appId, 'public', 'data', 'sites'),
        (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) } as Site));
          setSites(list);
        }
      );
      return () => {
        unsubSites();
      };
    }, []);

    // Filter: REMOVED dutyType === 'Field' restriction to show ALL employees
    const activeLocations = latestLocations;

    // NOTE: Many employees can share the exact same lat/lng (e.g., Head Office).
    // Leaflet will then draw markers on top of each other, making it look like only 1 employee exists.
    // To solve this without extra dependencies, we apply a tiny, deterministic "jitter" to overlapping points.
    const parsedLocations = useMemo(() => {
      type Parsed = AttendanceRecord & {
        rawLatitude: number;
        rawLongitude: number;
      };

      const toNum = (v: any) => (typeof v === 'number' ? v : Number(v));
      const base: Parsed[] = activeLocations
        .map((loc) => {
          const rawLatitude = toNum((loc as any).latitude);
          const rawLongitude = toNum((loc as any).longitude);
          return {
            ...(loc as any),
            rawLatitude,
            rawLongitude,
            latitude: rawLatitude,
            longitude: rawLongitude,
          } as Parsed;
        })
        .filter(
          (loc) =>
            Number.isFinite(loc.rawLatitude) &&
            Number.isFinite(loc.rawLongitude) &&
            (loc.rawLatitude !== 0 || loc.rawLongitude !== 0)
        );

      // Group by rounded coordinate (avoid float noise)
      const keyOf = (lat: number, lng: number) => `${lat.toFixed(6)}:${lng.toFixed(6)}`;
      const groups = new Map<string, Parsed[]>();
      base.forEach((loc) => {
        const k = keyOf(loc.rawLatitude, loc.rawLongitude);
        const arr = groups.get(k);
        if (arr) arr.push(loc);
        else groups.set(k, [loc]);
      });

      const jittered: Parsed[] = [];
      groups.forEach((arr) => {
        if (arr.length === 1) {
          jittered.push(arr[0]);
          return;
        }

        // Distribute markers around a small circle (~10–30m) so they are all clickable.
        // Adjust longitude delta by latitude so the circle looks round on the map.
        const baseLat = arr[0].rawLatitude;
        const latRad = (baseLat * Math.PI) / 180;
        const cosLat = Math.max(0.15, Math.cos(latRad));
        const radiusDeg = Math.min(0.00025, 0.0001 + arr.length * 0.00001);
        arr.forEach((loc, idx) => {
          const angle = (2 * Math.PI * idx) / arr.length;
          const dLat = radiusDeg * Math.cos(angle);
          const dLng = (radiusDeg * Math.sin(angle)) / cosLat;
          jittered.push({
            ...loc,
            latitude: loc.rawLatitude + dLat,
            longitude: loc.rawLongitude + dLng,
          });
        });
      });

      return jittered;
    }, [activeLocations]);

    const FitBounds = ({ points }: { points: Array<{ latitude: number; longitude: number }> }) => {
      const map = useMap();
      useEffect(() => {
        if (!points || points.length === 0) return;
        try {
          const bounds = points.map((p) => [p.latitude, p.longitude]) as any;
          map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
        } catch {
          // ignore
        }
      }, [map, points]);
      return null;
    };

    const FlyToSelected = ({ point }: { point: { lat: number; lng: number } | null }) => {
      const map = useMap();
      useEffect(() => {
        if (!point) return;
        try {
          map.flyTo([point.lat, point.lng] as any, Math.max(map.getZoom?.() ?? 15, 16), { duration: 0.6 });
        } catch {
          // ignore
        }
      }, [map, point]);
      return null;
    };

    const liveListGroups = useMemo(() => {
      const normalize = (v: any) => String(v ?? '').trim();
      const toUpper = (v: string) => v.trim().toUpperCase();
      const locKey = (rawLat: number, rawLng: number) => `${rawLat.toFixed(5)},${rawLng.toFixed(5)}`;

      const groups = new Map<
        string,
        {
          key: string;
          title: string;
          rawLat: number;
          rawLng: number;
          employees: Array<
            AttendanceRecord & {
              rawLatitude: number;
              rawLongitude: number;
              resolvedEmpId: string;
            }
          >;
        }
      >();

      (parsedLocations as any[]).forEach((loc) => {
        const rawLat = Number(loc.rawLatitude ?? loc.latitude);
        const rawLng = Number(loc.rawLongitude ?? loc.longitude);
        if (!Number.isFinite(rawLat) || !Number.isFinite(rawLng)) return;

        const resolved = resolveSiteForPoint(rawLat, rawLng, sites);
        const siteOrText = normalize(resolved?.site?.name || loc.siteName || loc.locationText);
        const title = siteOrText ? siteOrText : locKey(rawLat, rawLng);
        const key = `${toUpper(title)}|${locKey(rawLat, rawLng)}`;

        const resolvedEmpId =
          normalize(loc.empId) || normalize(userProfiles[loc.userId]?.empId) || '-';

        const entry = {
          ...(loc as any),
          rawLatitude: rawLat,
          rawLongitude: rawLng,
          resolvedEmpId,
        };

        const existing = groups.get(key);
        if (existing) {
          existing.employees.push(entry);
        } else {
          groups.set(key, {
            key,
            title,
            rawLat,
            rawLng,
            employees: [entry],
          });
        }
      });

      const search = toUpper(searchText);
      const list = Array.from(groups.values())
        .map((g) => {
          const employees = g.employees
            .slice()
            .sort((a, b) => {
              const ta = a.timestamp?.toDate?.()?.getTime?.() ?? 0;
              const tb = b.timestamp?.toDate?.()?.getTime?.() ?? 0;
              return tb - ta;
            });
          return { ...g, employees };
        })
        .filter((g) => {
          if (!search) return true;
          if (toUpper(g.title).includes(search)) return true;
          return g.employees.some((e) => {
            const name = toUpper(String(e.userName ?? ''));
            const empId = toUpper(String((e as any).resolvedEmpId ?? ''));
            return name.includes(search) || empId.includes(search);
          });
        })
        .sort((a, b) => b.employees.length - a.employees.length || a.title.localeCompare(b.title));

      return list;
    }, [parsedLocations, searchText, userProfiles, sites]);

    return (
      <div className="h-full w-full bg-slate-100">
        <div className="flex h-full min-h-[520px]">
          {/* MAP */}
          <div className="flex-1 relative">
            <div className="absolute top-4 right-4 z-20 bg-white p-2 rounded-lg shadow-lg">
              <p className="text-xs font-bold text-slate-600 mb-1">Live Workforce</p>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                <span className="text-xs">Active (In)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                <span className="text-xs">Inactive (Out)</span>
              </div>
            </div>

            <MapContainer
              bounds={
                parsedLocations.length > 0
                  ? (parsedLocations.map((p: any) => [p.latitude, p.longitude]) as any)
                  : ([[6, 68], [36, 98]] as any)
              }
              boundsOptions={{ padding: [40, 40] }}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FitBounds points={parsedLocations as any} />
              <FlyToSelected point={selectedPoint} />

              {parsedLocations.map((loc: any) => {
                const color = loc.type === 'in' ? '#22c55e' : '#ef4444';
                const isSelected = selectedUserId && loc.userId === selectedUserId;
                const rawLat = Number(loc.rawLatitude ?? loc.latitude);
                const rawLng = Number(loc.rawLongitude ?? loc.longitude);
                const resolvedEmpId = String(loc.empId || userProfiles[loc.userId]?.empId || '').trim() || '-';
                const resolvedSite = resolveSiteForPoint(rawLat, rawLng, sites);

                return (
                  <CircleMarker
                    key={loc.id}
                    center={[loc.latitude as any, loc.longitude as any]}
                    radius={isSelected ? 11 : 8}
                    pathOptions={{
                      color,
                      fillColor: color,
                      fillOpacity: isSelected ? 0.95 : 0.85,
                      weight: isSelected ? 3 : 2,
                    }}
                    eventHandlers={{
                      click: () => {
                        setSelectedUserId(loc.userId);
                        if (Number.isFinite(rawLat) && Number.isFinite(rawLng)) {
                          setSelectedPoint({ lat: rawLat, lng: rawLng });
                        }
                      },
                    }}
                  >
                    <Popup>
                      <div style={{ minWidth: 200 }}>
                        <div style={{ fontWeight: 700 }}>{loc.userName}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>EmpID: {resolvedEmpId}</div>
                        {(resolvedSite?.site?.name || loc.siteName) && (
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            Site: {resolvedSite?.site?.name || loc.siteName}
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          {loc.timestamp?.toDate?.()?.toLocaleString?.() ?? ''}
                        </div>
                        <div style={{ marginTop: 6, fontSize: 12 }}>
                          Status: <b>{String(loc.type).toUpperCase()}</b>
                        </div>
                        <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
                          {Number(rawLat).toFixed(5)}, {Number(rawLng).toFixed(5)}
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <a
                            href={`https://www.google.com/maps?q=${rawLat},${rawLng}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#4f46e5', fontSize: 12, textDecoration: 'underline' }}
                          >
                            Open in Google Maps
                          </a>
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </div>

          {/* LIVE LIST */}
          <div className="w-full md:w-[380px] bg-white border-l border-slate-200 flex flex-col">
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <MapIcon className="w-5 h-5 text-indigo-600" /> Live List
                </h3>
                <div className="text-xs text-slate-500">{activeLocations.length} employees</div>
              </div>
              <div className="mt-3">
                <input
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  placeholder="Search name / EmpID / site"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              <div className="mt-2 text-[11px] text-slate-500">
                Tip: Click a location group to jump the map.
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {liveListGroups.length === 0 && (
                <div className="p-6 text-sm text-slate-500">No location data found.</div>
              )}

              {liveListGroups.map((g) => {
                const inCount = g.employees.filter((e) => e.type === 'in').length;
                const outCount = g.employees.filter((e) => e.type === 'out').length;
                return (
                  <div key={g.key} className="border-b border-slate-100">
                    <button
                      className="w-full text-left px-4 py-3 hover:bg-slate-50"
                      onClick={() => {
                        setSelectedUserId(null);
                        setSelectedPoint({ lat: g.rawLat, lng: g.rawLng });
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex items-center gap-2">
                          <div className="min-w-0 truncate font-semibold text-sm text-slate-800">{g.title}</div>
                          <div className="shrink-0 text-[11px] text-slate-500 font-mono">
                            {g.rawLat.toFixed(5)}, {g.rawLng.toFixed(5)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-100">
                            IN {inCount}
                          </span>
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100">
                            OUT {outCount}
                          </span>
                        </div>
                      </div>
                    </button>

                    <div className="px-4 pb-4">
                      <div className="space-y-2">
                        {g.employees.map((e) => {
                          const ts = e.timestamp?.toDate?.();
                          const timeText = ts ? ts.toLocaleTimeString() : '';
                          const isSelected = selectedUserId && e.userId === selectedUserId;
                          return (
                            <button
                              key={e.id}
                              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-left hover:bg-slate-50 ${
                                isSelected ? 'border-indigo-300 bg-indigo-50' : 'border-slate-100'
                              }`}
                              onClick={() => {
                                setSelectedUserId(e.userId);
                                setSelectedPoint({ lat: e.rawLatitude, lng: e.rawLongitude });
                              }}
                            >
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                                  e.type === 'in' ? 'bg-green-500' : 'bg-red-500'
                                }`}
                              >
                                {String(e.userName || '?')[0]}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="min-w-0 truncate text-sm font-semibold text-slate-800">
                                    {e.userName}{' '}
                                    <span className="font-normal text-slate-500">({(e as any).resolvedEmpId ?? '-'})</span>
                                  </div>
                                  <div className="shrink-0 flex items-center gap-2">
                                    <span
                                      className={`text-[11px] px-2 py-0.5 rounded-full border ${
                                        e.type === 'in'
                                          ? 'bg-green-50 text-green-700 border-green-100'
                                          : 'bg-red-50 text-red-700 border-red-100'
                                      }`}
                                    >
                                      {String(e.type).toUpperCase()}
                                    </span>
                                    <span className="text-xs text-slate-500">{timeText}</span>
                                  </div>
                                </div>
                              </div>
                              <a
                                href={`https://www.google.com/maps?q=${e.rawLatitude},${e.rawLongitude}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-slate-400 hover:text-slate-600"
                                onClick={(ev) => ev.stopPropagation()}
                                title="Open in Google Maps"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
}

function AdminEmployees({ profile }: any) {

  const canManageEmployees = profile?.role === 'admin';

  const [employees, setEmployees] = useState<any[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showDedupe, setShowDedupe] = useState(false);
  const [dedupeMode, setDedupeMode] = useState<'empId' | 'email'>('empId');
  const [dedupeBusy, setDedupeBusy] = useState(false);
  const [dedupeError, setDedupeError] = useState('');
  const [keepByKey, setKeepByKey] = useState<Record<string, string>>({});
  const [bulkData, setBulkData] = useState('');
  const [showBulk, setShowBulk] = useState(false);
  const [showAddSingle, setShowAddSingle] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState('');
  const [existingClients, setExistingClients] = useState<string[]>([]);
  const [formMode, setFormMode] = useState<'Employee' | 'Vendor'>('Employee'); // Toggle between Employee and Vendor
  const [resetDeviceBusyId, setResetDeviceBusyId] = useState('');

  const toDateSafe = (value: any): Date | null => {
    try {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value?.toDate === 'function') return value.toDate();
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  const employeeDuplicateGroups = useMemo(() => {
    const byKey = new Map<string, any[]>();
    for (const emp of employees || []) {
      if ((emp?.role || '').toString() !== 'employee') continue;
      const key =
        dedupeMode === 'empId'
          ? String(emp?.empId || '').trim().toUpperCase()
          : String(emp?.email || '').trim().toLowerCase();
      if (!key) continue;
      const bucket = byKey.get(key) || [];
      bucket.push(emp);
      byKey.set(key, bucket);
    }

    const groups = Array.from(byKey.entries())
      .filter(([, list]) => (list?.length || 0) > 1)
      .map(([groupKey, list]) => {
        const sorted = [...list].sort((a, b) => {
          const ad = toDateSafe(a?.createdAt);
          const bd = toDateSafe(b?.createdAt);
          const at = ad ? ad.getTime() : 0;
          const bt = bd ? bd.getTime() : 0;
          return bt - at;
        });
        return { key: groupKey, list: sorted };
      })
      .sort((a, b) => a.key.localeCompare(b.key));

    return groups;
  }, [employees, dedupeMode]);

  useEffect(() => {
    if (!showDedupe) return;
    setDedupeError('');
    setKeepByKey((prev) => {
      const next = { ...prev };
      for (const g of employeeDuplicateGroups) {
        if (!next[g.key]) next[g.key] = g.list[0]?.id;
      }
      return next;
    });
  }, [showDedupe, employeeDuplicateGroups]);

  const mergeEmployeeRecords = (keep: any, others: any[]) => {
    const merged: any = { ...keep };

    const mergeScalar = (key: string, value: any) => {
      const cur = merged[key];
      const curEmpty = cur === undefined || cur === null || (typeof cur === 'string' && cur.trim() === '');
      if (curEmpty && value !== undefined && value !== null) merged[key] = value;
    };

    for (const o of others) {
      if (!o) continue;
      for (const k of Object.keys(o)) {
        if (k === 'id') continue;
        if (k === 'allocatedSiteIds') continue;
        if (k === 'createdAt') continue;
        mergeScalar(k, o[k]);
      }

      // Union allocated sites
      const keepSites = Array.isArray(merged.allocatedSiteIds) ? merged.allocatedSiteIds : [];
      const otherSites = Array.isArray(o.allocatedSiteIds) ? o.allocatedSiteIds : [];
      merged.allocatedSiteIds = Array.from(new Set([...keepSites, ...otherSites].filter(Boolean)));

      // Keep earliest createdAt (if present)
      const kd = toDateSafe(merged.createdAt);
      const od = toDateSafe(o.createdAt);
      if (kd && od && od.getTime() < kd.getTime()) merged.createdAt = o.createdAt;
      if (!kd && od) merged.createdAt = o.createdAt;
    }

    // Normalize
    const empIdUpper = String(merged.empId || '').trim().toUpperCase();
    if (empIdUpper) {
      merged.empId = empIdUpper;
      merged.empIdUpper = empIdUpper;
    }
    if (merged.email) merged.email = String(merged.email).trim().toLowerCase();
    return merged;
  };

  const handleMergeDuplicateGroup = async (groupKey: string) => {
    setDedupeError('');
    const group = employeeDuplicateGroups.find((g) => g.key === groupKey);
    if (!group) return;
    const keepId = keepByKey[groupKey] || group.list[0]?.id;
    const keep = group.list.find((e) => e.id === keepId) || group.list[0];
    const others = group.list.filter((e) => e.id !== keep?.id);
    if (!keep?.id || others.length === 0) return;

    const label = dedupeMode === 'empId' ? 'EmpID' : 'Email';
    const ok = window.confirm(
      `Merge duplicates for ${label} ${groupKey}?\n\nThis will UPDATE 1 Firestore document and DELETE ${others.length} duplicate Firestore document(s).\n(Authentication accounts are NOT deleted.)`
    );
    if (!ok) return;

    setDedupeBusy(true);
    try {
      const merged = mergeEmployeeRecords(keep, others);

      const batch = writeBatch(db);
      batch.update(doc(db, 'artifacts', appId, 'public', 'data', 'users', keep.id), {
        ...merged,
        updatedAt: serverTimestamp(),
      });
      for (const o of others) {
        batch.delete(doc(db, 'artifacts', appId, 'public', 'data', 'users', o.id));
      }
      await batch.commit();
    } catch (e: any) {
      console.error(e);
      setDedupeError(e?.message || 'Failed to merge duplicates.');
    } finally {
      setDedupeBusy(false);
    }
  };

  const handleMergeAllDuplicates = async () => {
    if (employeeDuplicateGroups.length === 0) return;
    const label = dedupeMode === 'empId' ? 'EmpIDs' : 'Emails';
    const ok = window.confirm(
      `Merge ALL duplicate ${label}?\n\nGroups: ${employeeDuplicateGroups.length}\nThis will delete extra Firestore docs. (Authentication accounts are NOT deleted.)`
    );
    if (!ok) return;
    setDedupeBusy(true);
    setDedupeError('');
    try {
      for (const g of employeeDuplicateGroups) {
        // eslint-disable-next-line no-await-in-loop
        await handleMergeDuplicateGroup(g.key);
      }
    } finally {
      setDedupeBusy(false);
    }
  };

  const filteredEmployeesForCards = useMemo(() => {
    const byKey = new Map<string, any>();
    for (const emp of employees || []) {
      const empIdKey = String(emp?.empId || '').trim().toUpperCase();
      const emailKey = String(emp?.email || '').trim().toLowerCase();
      const key = empIdKey || emailKey || emp?.id;
      if (!key) continue;
      if (!byKey.has(key)) byKey.set(key, emp);
    }
    const unique = Array.from(byKey.values());

    const q = employeeSearch.trim().toLowerCase();
    if (!q) return unique;
    return unique.filter((emp) => {
      const hay = [
        emp?.name,
        emp?.empId,
        emp?.email,
        emp?.phone,
        emp?.department,
        emp?.designation,
        emp?.employeeCategory,
        emp?.dutyType,
        emp?.clientName,
        emp?.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [employees, employeeSearch]);

  const departmentNameOptions = useMemo(() => {
    const names = (departments || [])
      .filter((d) => (d?.isActive ?? true) !== false)
      .map((d) => String(d?.name || '').trim())
      .filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [departments]);

  const [newEmp, setNewEmp] = useState<Partial<UserProfile>>({
    empId: '',
    name: '',
    email: '',
    phone: '',
    department: '',
    designation: '',
    reportingTo: '',
    reportingToEmpId: '',
    hrManager: '',
    hrManagerEmpId: '',
    employeeCategory: 'Direct',
    dutyType: 'Field',
    clientName: '',
    status: 'Active',
    // Vendor specific
    ownerName: '',
    address: '',
    gstNo: '',
    panNo: '',
    bankName: '',
    bankAccount: '',
    ifscCode: '',
  });

  useEffect(() => {
    const q = collection(db, 'artifacts', appId, 'public', 'data', 'users');
    const unsub = onSnapshot(q, (snap) => {
      let allEmps = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as UserProfile[];
      if (profile.role === 'manager') {
        allEmps = allEmps.filter(
          (e) => e.reportingTo === profile.name || e.reportingToEmpId === profile.empId
        );
      }
      setEmployees(allEmps);
    });

    const qSites = collection(db, 'artifacts', appId, 'public', 'data', 'sites');
    const unsubSites = onSnapshot(qSites, (snap) => {
      const sites = snap.docs.map((d) => d.data() as Site);
      setExistingClients(Array.from(new Set(sites.map((s) => s.clientName).filter((c) => c))));
    });

    return () => {
      unsub();
      unsubSites();
    };
  }, [profile]);

  useEffect(() => {
    const unsubDepts = onSnapshot(
      query(collection(db, 'artifacts', appId, 'public', 'data', 'departments'), orderBy('name', 'asc')),
      (snap) => {
        setDepartments(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Department[]);
      }
    );
    return () => unsubDepts();
  }, []);

  const formatReportingTo = (managerName?: string, managerEmpId?: string) => {
    const name = (managerName || '').trim();
    if (!name) return '-';
    if ((managerEmpId || '').trim()) return `${name} (${managerEmpId})`;
    const manager = employees.find((u) => (u.name || '').trim() === name);
    if (!manager?.empId) return name;
    return `${name} (${manager.empId})`;
  };

  const handleResetDevice = async (targetUserId: string, empName?: string, empId?: string) => {
    if (!canManageEmployees) return;
    const label = `${String(empName || '').trim() || 'this user'}${String(empId || '').trim() ? ` (${String(empId).trim()})` : ''}`;
    const ok = window.confirm(
      `Reset device access for ${label}?\n\nThis will allow login on a new phone/device and will kick out the old device automatically.`
    );
    if (!ok) return;
    setResetDeviceBusyId(targetUserId);
    try {
      await adminResetDeviceCallable({ appId, targetUserId });
      alert('Device reset successful. User can login on a new device now.');
    } catch (e: any) {
      console.error(e);
      alert(callErrorToUserMessage(e));
    } finally {
      setResetDeviceBusyId('');
    }
  };

const handleSaveEmployee = async (e: React.FormEvent) => {
  e.preventDefault();

  // Name is mandatory
  if (!newEmp.name) return;

  // Auto-set category if in Vendor Mode
  const category = formMode === 'Vendor' ? 'Vendor' : newEmp.employeeCategory;

  // Decide final employee ID:
  let finalEmpId = (newEmp.empId || '').toString().trim().toUpperCase();

  // MANUAL EMPLOYEE CODE MODEL (as agreed):
  // - Admin must type the employee/vendor code exactly as per your payroll/ERP.
  // - Examples: AI1984, CI0031, WI0102, VND0007 etc.
  if (!finalEmpId) {
    alert(formMode === 'Vendor' ? 'Please enter Vendor Code / ID (manual).' : 'Please enter Employee Code / Emp ID (manual).');
    return;
  }

  if (isEditing && editId) {
    // UPDATE existing employee
    await updateDoc(
      doc(db, 'artifacts', appId, 'public', 'data', 'users', editId),
      {
        ...newEmp,
        empId: finalEmpId,
        empIdUpper: finalEmpId,
        employeeCategory: category,
      }
    );
    alert(`${formMode} Updated Successfully`);
  } else {
    // CREATE new employee / vendor (prevent duplicates by EmpID/email)
    const normalizedEmail = (newEmp.email || '').toString().trim().toLowerCase();
    if (!normalizedEmail) {
      alert('Email is required.');
      return;
    }

    // If an employee with same EmpID already exists, update that record instead of creating a new one.
    const existingByEmpIdSnap = await getDocs(
      query(
        collection(db, 'artifacts', appId, 'public', 'data', 'users'),
        where('empId', '==', finalEmpId),
        limit(1)
      )
    );

    if (!existingByEmpIdSnap.empty) {
      const existingId = existingByEmpIdSnap.docs[0].id;
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', existingId), {
        ...newEmp,
        email: normalizedEmail,
        empId: finalEmpId,
        empIdUpper: finalEmpId,
        employeeCategory: category,
        role: 'employee',
      });
      alert(`${formMode} already exists for EmpID ${finalEmpId}. Updated existing record.`);
    } else {
      // Create Auth user (so there is a single canonical Firestore doc keyed by UID)
      const tempPassword = `${finalEmpId || 'AlertIntell'}@123`;
      try {
        const userCredential = await createUserWithEmailAndPassword(adminAuth, normalizedEmail, tempPassword);
        try {
          await sendPasswordResetEmail(adminAuth, normalizedEmail);
        } catch (err) {
          console.warn(`Could not send reset email for ${normalizedEmail}:`, err);
        }

        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', userCredential.user.uid), {
          ...newEmp,
          email: normalizedEmail,
          empId: finalEmpId,
          empIdUpper: finalEmpId,
          employeeCategory: category,
          role: 'employee',
          createdAt: serverTimestamp(),
          allocatedSiteIds: [],
        });

        alert(`${formMode} Added Successfully (ID: ${finalEmpId})`);
      } catch (err: any) {
        if (err?.code === 'auth/email-already-in-use') {
          // If Auth already has this email, update existing Firestore profile by email (prevents duplicate docs)
          const existingByEmailSnap = await getDocs(
            query(
              collection(db, 'artifacts', appId, 'public', 'data', 'users'),
              where('email', '==', normalizedEmail),
              limit(1)
            )
          );
          if (!existingByEmailSnap.empty) {
            const existingId = existingByEmailSnap.docs[0].id;
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', existingId), {
              ...newEmp,
              email: normalizedEmail,
              empId: finalEmpId,
              empIdUpper: finalEmpId,
              employeeCategory: category,
              role: 'employee',
            });
            alert(`${formMode} already exists for ${normalizedEmail}. Updated existing record.`);
          } else {
            alert(
              `Auth user already exists for ${normalizedEmail}, but no Firestore profile was found. Please create the profile once in Firestore or contact support.`
            );
          }
        } else {
          console.error(err);
          alert(err?.message || 'Failed to add employee/vendor.');
        }
      }
    }
  }

  // Reset the form to defaults
  setNewEmp({
    empId: '',
    name: '',
    email: '',
    phone: '',
    department: '',
    designation: '',
    reportingTo: '',
    reportingToEmpId: '',
    hrManager: '',
    hrManagerEmpId: '',
    employeeCategory: 'Direct',
    dutyType: 'Field',
    clientName: '',
    status: 'Active',
    ownerName: '',
    address: '',
    gstNo: '',
    panNo: '',
    bankName: '',
    bankAccount: '',
    ifscCode: '',
  });
  setShowAddSingle(false);
  setIsEditing(false);
  setEditId('');
};

  const handleEditClick = (emp: UserProfile) => {
    const existingManager = (emp.reportingTo || '').trim()
      ? employees.find((u) => (u.name || '').trim() === (emp.reportingTo || '').trim())
      : undefined;

    const existingHrManager = (emp.hrManager || '').trim()
      ? employees.find((u) => (u.name || '').trim() === (emp.hrManager || '').trim())
      : undefined;

    setNewEmp({
      ...emp,
      reportingToEmpId: emp.reportingToEmpId || existingManager?.empId || '',
      hrManagerEmpId: emp.hrManagerEmpId || existingHrManager?.empId || '',
    });
    setEditId(emp.id);
    setFormMode(emp.employeeCategory === 'Vendor' ? 'Vendor' : 'Employee');
    setIsEditing(true);
    setShowAddSingle(true);
  };

  // Download SAMPLE CSV for bulk upload
  const handleDownloadSample = () => {
    const headers = [
      'empId',
      'name',
      'email',
      'phone',
      'department',
      'designation',
      'reportingTo',
      'hrManager',
      'category',
      'dutyType',
      'clientName',
    ];
    const rows = [
      [
        'AI1001',
        'Amit Sharma',
        'amit.sharma@alertintell.com',
        '9876543210',
        'Sales',
        'Sales Executive',
        'MGR0001',
        'HR0001',
        'Direct',
        'Field',
        'Client A',
      ],
      [
        'VN2001',
        'ABC Vendor',
        'vendor.abc@alertintell.com',
        '9000000000',
        'Vendors',
        'Vendor',
        'MGR0001',
        'HR0001',
        'Vendor',
        'Static',
        '',
      ],
    ];
    downloadCSV('employees_sample.csv', headers, rows);
  };

  // BULK UPLOAD – creates Auth + Firestore users
  const handleBulkUpload = async () => {
    const employeesByEmpId = new Map(
      (employees || [])
        .filter((e) => (e.empId || '').toString().trim())
        .map((e) => [(e.empId || '').toString().trim().toUpperCase(), e])
    );
    const employeesByEmail = new Map(
      (employees || [])
        .filter((e) => (e.email || '').toString().trim())
        .map((e) => [(e.email || '').toString().trim().toLowerCase(), e])
    );
    const employeesByName = new Map(
      (employees || [])
        .filter((e) => (e.name || '').toString().trim())
        .map((e) => [(e.name || '').toString().trim(), e])
    );

    const lines = bulkData
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length === 0) {
      alert('No data to upload.');
      return;
    }

    let count = 0;
    const errors: string[] = [];

    const tasks = lines.map(async (line, index) => {
      const parts = line.split(',').map((s) => s.trim());
      // Expected CSV: empId,name,email,phone,department,designation,reportingTo,hrManager,category,dutyType,clientName
      if (parts.length < 3) {
        errors.push(`Line ${index + 1}: Not enough columns, skipped.`);
        return;
      }

      const [
        empId,
        name,
        email,
        phone,
        department,
        designation,
        reportingTo,
        hrManager,
        category,
        dutyType,
        clientName,
      ] = parts;

      const empIdUpper = (empId || '').toString().trim().toUpperCase();

      const reportingToRaw = (reportingTo || '').trim();
      const manager = reportingToRaw
        ? employeesByEmpId.get(reportingToRaw) || employeesByName.get(reportingToRaw)
        : undefined;
      const reportingToNameFinal = manager?.name || reportingToRaw;
      const reportingToEmpIdFinal = manager?.empId || '';

      const hrManagerRaw = (hrManager || '').trim();
      const hr = hrManagerRaw
        ? employeesByEmpId.get(hrManagerRaw) || employeesByName.get(hrManagerRaw)
        : undefined;
      const hrManagerNameFinal = hr?.name || hrManagerRaw;
      const hrManagerEmpIdFinal = hr?.empId || '';

      if (!email) {
        errors.push(`Line ${index + 1}: Missing email, skipped.`);
        return;
      }

      try {
        const normalizedEmail = email.trim().toLowerCase();
        const tempPassword = `${empId || 'AlertIntell'}@123`;

        // If this EmpID or Email already exists in Firestore, update that single record and skip creating another Auth/Firestore doc.
        const existing = (empIdUpper ? employeesByEmpId.get(empIdUpper) : undefined) || employeesByEmail.get(normalizedEmail);
        if (existing?.id) {
          await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', existing.id), {
            empId: empIdUpper,
            empIdUpper,
            name: name || existing.name || '',
            email: normalizedEmail,
            phone: phone || existing.phone || '',
            department: department || existing.department || '',
            designation: designation || existing.designation || '',
            reportingTo: reportingToNameFinal || existing.reportingTo || '',
            reportingToEmpId: reportingToEmpIdFinal || existing.reportingToEmpId || '',
            hrManager: hrManagerNameFinal || existing.hrManager || '',
            hrManagerEmpId: hrManagerEmpIdFinal || existing.hrManagerEmpId || '',
            employeeCategory: category || existing.employeeCategory || 'Direct',
            dutyType: dutyType || existing.dutyType || 'Field',
            clientName: clientName || existing.clientName || '',
            role: 'employee',
          });
          count++;
          return;
        }

        // Create Auth user using secondary admin app (does not log out the Admin)
        const userCredential = await createUserWithEmailAndPassword(adminAuth, normalizedEmail, tempPassword);

        // Try sending password reset email so employee sets their own password
        try {
          await sendPasswordResetEmail(adminAuth, normalizedEmail);
        } catch (err) {
          console.warn(`Could not send reset email for ${normalizedEmail}:`, err);
        }

        const profileData = {
          empId: empIdUpper,
          empIdUpper,
          name: name || '',
          email: normalizedEmail,
          phone: phone || '',
          department: department || '',
          designation: designation || '',
          reportingTo: reportingToNameFinal || '',
          reportingToEmpId: reportingToEmpIdFinal || '',
          hrManager: hrManagerNameFinal || '',
          hrManagerEmpId: hrManagerEmpIdFinal || '',
          employeeCategory: category || 'Direct',
          dutyType: dutyType || 'Field',
          clientName: clientName || '',
          role: 'employee', // Vendors also treated as employees for login
          createdAt: serverTimestamp(),
          allocatedSiteIds: [] as string[],
        };

        await setDoc(
          doc(db, 'artifacts', appId, 'public', 'data', 'users', userCredential.user.uid),
          profileData
        );

        count++;
      } catch (err: any) {
        console.error(`Line ${index + 1} failed`, err);
        if (err?.code === 'auth/email-already-in-use') {
          errors.push(`Line ${index + 1}: ${email} already exists in Authentication, skipped.`);
        } else {
          errors.push(`Line ${index + 1}: ${email} error: ${err?.message || 'Unknown error'}`);
        }
      }
    });

    await Promise.all(tasks);

    setBulkData('');
    setShowBulk(false);

    let message = `${count} employees uploaded!`;
    if (errors.length) {
      message += `\n\nSome rows were skipped:\n- ` + errors.join('\n- ');
    }
    alert(message);
  };

  return (
    <div className="space-y-6">
      {/* HEADER + ACTION BUTTONS */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h2 className="text-2xl font-bold text-slate-800">Team Management</h2>
        {canManageEmployees ? (
          <div className="flex gap-2">
            {/* Add Employee */}
            <button
              onClick={() => {
                setShowAddSingle(true);
                setIsEditing(false);
                setFormMode('Employee');
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-medium"
            >
              <UserPlus className="w-4 h-4" /> Add Employee
            </button>

            {/* Add Vendor */}
            <button
              onClick={() => {
                setShowAddSingle(true);
                setIsEditing(false);
                setFormMode('Vendor');
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-700 text-sm font-medium"
            >
              <Truck className="w-4 h-4" /> Add Vendor
            </button>

            {/* Bulk Upload */}
            <button
              onClick={() => setShowBulk(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium"
            >
              <Upload className="w-4 h-4" /> Bulk Upload
            </button>

            <button
              onClick={() => setShowDedupe(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded bg-amber-600 text-white hover:bg-amber-700 text-sm font-medium"
              title="Find and clean duplicate employee records"
            >
              <AlertTriangle className="w-4 h-4" /> Duplicates
            </button>
          </div>
        ) : (
          <div className="text-sm text-slate-500">Only Admin/HR can add employees/vendors.</div>
        )}
      </div>

      {/* DUPLICATE CLEANUP */}
      {showDedupe && (
        <div className="bg-white p-6 rounded-xl border border-amber-200 shadow-md">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" /> Duplicate Cleanup
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Finds duplicates by EmpID or Email. Merging updates 1 Firestore profile and deletes extra Firestore profiles.
                Authentication users are not deleted.
              </p>
            </div>
            <button
              onClick={() => setShowDedupe(false)}
              className="text-slate-400 hover:text-slate-600"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {dedupeError && (
            <div className="mb-3 bg-red-50 border border-red-100 text-red-700 text-sm p-3 rounded-lg">
              {dedupeError}
            </div>
          )}

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div className="text-sm text-slate-700">
              Duplicate {dedupeMode === 'empId' ? 'EmpIDs' : 'Emails'} found:{' '}
              <span className="font-bold">{employeeDuplicateGroups.length}</span>
            </div>
            <div className="flex gap-2">
              <div className="flex items-center gap-2 mr-2">
                <button
                  type="button"
                  disabled={dedupeBusy}
                  onClick={() => setDedupeMode('empId')}
                  className={`px-3 py-2 rounded text-xs font-semibold border ${
                    dedupeMode === 'empId'
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  By EmpID
                </button>
                <button
                  type="button"
                  disabled={dedupeBusy}
                  onClick={() => setDedupeMode('email')}
                  className={`px-3 py-2 rounded text-xs font-semibold border ${
                    dedupeMode === 'email'
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  By Email
                </button>
              </div>
              <button
                disabled={dedupeBusy || employeeDuplicateGroups.length === 0}
                onClick={handleMergeAllDuplicates}
                className="px-4 py-2 rounded bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
              >
                {dedupeBusy ? 'Working…' : 'Merge All'}
              </button>
              <button
                onClick={() => {
                  setKeepByKey({});
                  setDedupeError('');
                }}
                className="px-4 py-2 rounded border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
              >
                Reset Selection
              </button>
            </div>
          </div>

          {employeeDuplicateGroups.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              No duplicate {dedupeMode === 'empId' ? 'EmpIDs' : 'Emails'} found.
            </div>
          ) : (
            <div className="space-y-4">
              {employeeDuplicateGroups.map((g) => {
                const selectedKeep = keepByKey[g.key] || g.list[0]?.id;
                return (
                  <div key={g.key} className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="bg-slate-50 px-4 py-3 flex items-center justify-between gap-3">
                      <div className="text-sm">
                        <span className="font-bold text-slate-800">{dedupeMode === 'empId' ? 'EmpID' : 'Email'}:</span>{' '}
                        <span className="font-mono text-indigo-700">{g.key}</span>{' '}
                        <span className="text-slate-500">({g.list.length} records)</span>
                      </div>
                      <button
                        disabled={dedupeBusy}
                        onClick={() => handleMergeDuplicateGroup(g.key)}
                        className="px-3 py-1.5 rounded bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50"
                      >
                        Merge & Delete
                      </button>
                    </div>
                    <div className="p-4 space-y-2">
                      {g.list.map((emp) => {
                        const created = toDateSafe(emp?.createdAt);
                        const createdText = created ? created.toLocaleString() : '—';
                        return (
                          <label
                            key={emp.id}
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                              selectedKeep === emp.id
                                ? 'border-indigo-300 bg-indigo-50'
                                : 'border-slate-200 hover:bg-slate-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`keep_${g.key}`}
                              checked={selectedKeep === emp.id}
                              onChange={() => setKeepByKey((prev) => ({ ...prev, [g.key]: emp.id }))}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <div className="font-semibold text-slate-800 truncate">
                                  {emp?.name || '(No name)'}
                                </div>
                                <div className="text-[11px] font-mono text-slate-500 whitespace-nowrap">
                                  doc: {emp.id}
                                </div>
                              </div>
                              <div className="text-xs text-slate-500 truncate">
                                {String(emp?.email || '').trim().toLowerCase() || '—'}
                                {String(emp?.empId || '').trim()
                                  ? ` • EmpID: ${String(emp.empId).trim().toUpperCase()}`
                                  : ''}
                              </div>
                              <div className="text-[11px] text-slate-400 mt-1">
                                createdAt: {createdText}
                                {Array.isArray(emp?.allocatedSiteIds) && emp.allocatedSiteIds.length > 0
                                  ? ` • allocatedSites: ${emp.allocatedSiteIds.length}`
                                  : ''}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* SINGLE EMP / VENDOR FORM */}
      {showAddSingle && (
        <div className="bg-white p-6 rounded-xl border border-indigo-200 shadow-md mb-6">
          <div className="flex justify-between mb-4 items-center">
            <div className="flex gap-4">
              <h3
                className={`font-bold text-lg cursor-pointer ${
                  formMode === 'Employee' ? 'text-indigo-600 underline' : 'text-slate-400'
                }`}
                onClick={() => setFormMode('Employee')}
              >
                New Employee
              </h3>
              <h3
                className={`font-bold text-lg cursor-pointer ${
                  formMode === 'Vendor' ? 'text-purple-600 underline' : 'text-slate-400'
                }`}
                onClick={() => setFormMode('Vendor')}
              >
                New Vendor
              </h3>
            </div>
            <button onClick={() => setShowAddSingle(false)}>
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          <form onSubmit={handleSaveEmployee} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Common Fields */}
            <input
              placeholder={formMode === 'Vendor' ? 'Vendor Code *' : 'Employee ID *'}
              className="p-2 border rounded"
              value={newEmp.empId}
              onChange={(e) => setNewEmp({ ...newEmp, empId: e.target.value })}
              required
            />
            <input
              placeholder={formMode === 'Vendor' ? 'Vendor Name *' : 'Full Name *'}
              className="p-2 border rounded"
              value={newEmp.name}
              onChange={(e) => setNewEmp({ ...newEmp, name: e.target.value })}
              required
            />
            <input
              placeholder="Email (Login ID) *"
              className="p-2 border rounded"
              value={newEmp.email}
              onChange={(e) => setNewEmp({ ...newEmp, email: e.target.value })}
              required
            />
            <input
              placeholder="Phone"
              className="p-2 border rounded"
              value={newEmp.phone}
              onChange={(e) => setNewEmp({ ...newEmp, phone: e.target.value })}
            />

            <select
              className="p-2 border rounded bg-white"
              value={newEmp.status}
              onChange={(e) => setNewEmp({ ...newEmp, status: e.target.value as any })}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>

            {/* Employee Specific */}
            {formMode === 'Employee' && (
              <>
                <select
                  className="p-2 border rounded bg-white"
                  value={newEmp.department || ''}
                  onChange={(e) => setNewEmp({ ...newEmp, department: e.target.value })}
                >
                  <option value="">Select Department</option>
                  {!!String(newEmp.department || '').trim() &&
                    !departmentNameOptions.some(
                      (n) => n.toLowerCase() === String(newEmp.department || '').trim().toLowerCase()
                    ) && (
                      <option value={String(newEmp.department || '').trim()}>
                        {String(newEmp.department || '').trim()} (legacy)
                      </option>
                    )}
                  {departmentNameOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <input
                  placeholder="Designation"
                  className="p-2 border rounded"
                  value={newEmp.designation}
                  onChange={(e) => setNewEmp({ ...newEmp, designation: e.target.value })}
                />

                <select
                  className="p-2 border rounded"
                  value={newEmp.reportingToEmpId || ''}
                  onChange={(e) => {
                    const selectedManagerEmpId = e.target.value;
                    if (!selectedManagerEmpId) {
                      setNewEmp({ ...newEmp, reportingTo: '', reportingToEmpId: '' });
                      return;
                    }
                    const manager = employees.find((u) => u.empId === selectedManagerEmpId);
                    setNewEmp({
                      ...newEmp,
                      reportingTo: manager?.name || '',
                      reportingToEmpId: selectedManagerEmpId,
                    });
                  }}
                >
                  <option value="">Select Reporting Manager (by EmpID)</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.empId}>
                      {e.name} ({e.empId})
                    </option>
                  ))}
                </select>

                <select
                  className="p-2 border rounded"
                  value={newEmp.hrManagerEmpId || ''}
                  onChange={(e) => {
                    const selectedHrEmpId = e.target.value;
                    if (!selectedHrEmpId) {
                      setNewEmp({ ...newEmp, hrManager: '', hrManagerEmpId: '' });
                      return;
                    }
                    const hr = employees.find((u) => u.empId === selectedHrEmpId);
                    setNewEmp({
                      ...newEmp,
                      hrManager: hr?.name || '',
                      hrManagerEmpId: selectedHrEmpId,
                    });
                  }}
                >
                  <option value="">Select HR Manager (by EmpID)</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.empId}>
                      {e.name} ({e.empId})
                    </option>
                  ))}
                </select>

                <select
                  className="p-2 border rounded bg-white"
                  value={newEmp.employeeCategory}
                  onChange={(e) => setNewEmp({ ...newEmp, employeeCategory: e.target.value as any })}
                >
                  <option value="Direct">Direct</option>
                  <option value="Indirect">Indirect</option>
                  <option value="Contract">Contract</option>
                </select>
                <select
                  className="p-2 border rounded bg-white"
                  value={newEmp.dutyType}
                  onChange={(e) => setNewEmp({ ...newEmp, dutyType: e.target.value as any })}
                >
                  <option value="Field">Field</option>
                  <option value="Static">Static</option>
                </select>
                {newEmp.employeeCategory === 'Direct' && (
                  <select
                    className="p-2 border rounded bg-white"
                    value={newEmp.clientName}
                    onChange={(e) => setNewEmp({ ...newEmp, clientName: e.target.value })}
                  >
                    <option value="">Select Client *</option>
                    {existingClients.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                )}
              </>
            )}

            {/* Vendor Specific */}
            {formMode === 'Vendor' && (
              <>
                <input
                  placeholder="Owner Name"
                  className="p-2 border rounded"
                  value={newEmp.ownerName}
                  onChange={(e) => setNewEmp({ ...newEmp, ownerName: e.target.value })}
                />
                <input
                  placeholder="Address"
                  className="p-2 border rounded md:col-span-2"
                  value={newEmp.address}
                  onChange={(e) => setNewEmp({ ...newEmp, address: e.target.value })}
                />
                <input
                  placeholder="GST No"
                  className="p-2 border rounded"
                  value={newEmp.gstNo}
                  onChange={(e) => setNewEmp({ ...newEmp, gstNo: e.target.value })}
                />
                <input
                  placeholder="PAN Card No"
                  className="p-2 border rounded"
                  value={newEmp.panNo}
                  onChange={(e) => setNewEmp({ ...newEmp, panNo: e.target.value })}
                />
                <input
                  placeholder="Bank Name"
                  className="p-2 border rounded"
                  value={newEmp.bankName}
                  onChange={(e) => setNewEmp({ ...newEmp, bankName: e.target.value })}
                />
                <input
                  placeholder="Bank A/c No"
                  className="p-2 border rounded"
                  value={newEmp.bankAccount}
                  onChange={(e) => setNewEmp({ ...newEmp, bankAccount: e.target.value })}
                />
                <input
                  placeholder="IFSC Code"
                  className="p-2 border rounded"
                  value={newEmp.ifscCode}
                  onChange={(e) => setNewEmp({ ...newEmp, ifscCode: e.target.value })}
                />

                {/* Manager Assignment for Vendor Work Approval */}
                <select
                  className="p-2 border rounded"
                  value={newEmp.reportingToEmpId || ''}
                  onChange={(e) => {
                    const selectedManagerEmpId = e.target.value;
                    if (!selectedManagerEmpId) {
                      setNewEmp({ ...newEmp, reportingTo: '', reportingToEmpId: '' });
                      return;
                    }
                    const manager = employees.find((u) => u.empId === selectedManagerEmpId);
                    setNewEmp({
                      ...newEmp,
                      reportingTo: manager?.name || '',
                      reportingToEmpId: selectedManagerEmpId,
                    });
                  }}
                >
                  <option value="">Assign Manager (For Approval) (by EmpID)</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.empId}>
                      {e.name} ({e.empId})
                    </option>
                  ))}
                </select>
              </>
            )}

            <div className="md:col-span-3 flex justify-end pt-4 border-t mt-2">
              <button
                type="submit"
                className={`text-white px-6 py-2 rounded shadow-lg transition hover:-translate-y-0.5 ${
                  formMode === 'Vendor' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {isEditing ? 'Update' : 'Save'} {formMode}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* BULK UPLOAD PANEL */}
      {showBulk && (
        <div className="bg-white p-6 rounded-xl border border-emerald-200 shadow-md mb-6">
          <div className="flex justify-between items-center mb-3">
            <div>
              <h3 className="font-bold text-slate-800">Bulk Upload – Employees / Vendors</h3>
              <p className="text-xs text-slate-500">
                Paste CSV lines: empId,name,email,phone,department,designation,reportingTo,hrManager,category,dutyType,clientName (reportingTo/hrManager = EmpID recommended)
              </p>
            </div>
            <button onClick={() => setShowBulk(false)}>
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          <div className="flex justify-between items-center mb-2">
            <button
              onClick={handleDownloadSample}
              className="text-indigo-600 text-xs flex items-center gap-1 hover:underline"
            >
              <Download className="w-3 h-3" /> Download Sample CSV
            </button>
            <span className="text-[11px] text-slate-400">
              A temporary password like <code className="font-mono bg-slate-50 px-1">EMPID@123</code> is auto-set, and a reset email is sent.
            </span>
          </div>

          <textarea
            className="w-full h-32 p-3 border border-slate-300 rounded-lg font-mono text-xs"
            placeholder="Example:&#10;AI1001,Amit Sharma,amit.sharma@alertintell.com,9876543210,Sales,Sales Executive,MGR0001,HR0001,Direct,Field,Client A&#10;VN2001,ABC Vendor,vendor.abc@alertintell.com,9000000000,Vendors,Vendor,MGR0001,HR0001,Vendor,Static,"
            value={bulkData}
            onChange={(e) => setBulkData(e.target.value)}
          />

          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setShowBulk(false)}
              className="px-4 py-2 rounded border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkUpload}
              disabled={!bulkData.trim()}
              className="px-4 py-2 rounded bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
            >
              Process Upload
            </button>
          </div>
        </div>
      )}

      {/* EMPLOYEE / VENDOR CARDS */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="relative w-full md:max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="w-full pl-9 pr-9 py-2 border border-slate-300 rounded-lg text-sm"
            placeholder="Search employees/vendors (name, EmpID, email, dept...)"
            value={employeeSearch}
            onChange={(e) => setEmployeeSearch(e.target.value)}
          />
          {employeeSearch.trim() && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              onClick={() => setEmployeeSearch('')}
              aria-label="Clear search"
              title="Clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="text-xs text-slate-500">
          Showing {filteredEmployeesForCards.length} of {employees.length}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEmployeesForCards.map((emp) => (
          <div
            key={emp.id}
            className={`relative bg-white p-4 rounded-xl border shadow-sm hover:shadow-md transition group ${
              emp.employeeCategory === 'Vendor' ? 'border-purple-100' : 'border-slate-200'
            }`}
          >
            <button
              onClick={() => handleEditClick(emp)}
              className="absolute top-4 right-4 text-slate-400 hover:text-indigo-600"
            >
              <Pencil className="w-4 h-4" />
            </button>

            {canManageEmployees && emp.employeeCategory !== 'Vendor' && (
              <button
                type="button"
                disabled={resetDeviceBusyId === emp.id}
                onClick={() => handleResetDevice(emp.id, emp.name, emp.empId)}
                className="absolute top-4 right-11 text-slate-400 hover:text-rose-600 disabled:opacity-50"
                title="Reset Device"
                aria-label="Reset Device"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            )}
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold text-slate-800">{emp.name}</h3>
                <div className="flex gap-2 mb-1">
                  <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-1 rounded">{emp.empId}</span>
                  <span
                    className={`text-[10px] px-1 rounded border ${
                      emp.status === 'Inactive'
                        ? 'bg-red-50 text-red-600 border-red-100'
                        : 'bg-green-50 text-green-600 border-green-100'
                    }`}
                  >
                    {emp.status || 'Active'}
                  </span>
                </div>

                {emp.employeeCategory === 'Vendor' ? (
                  <div className="text-xs text-slate-500 mt-2 space-y-1">
                    <p>Owner: {emp.ownerName}</p>
                    <p>GST: {emp.gstNo}</p>
                    <p>Manager: {formatReportingTo(emp.reportingTo, emp.reportingToEmpId)}</p>
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 mt-2 space-y-1">
                    <p>
                      {emp.designation} • {emp.department}
                    </p>
                    <p>Manager: {formatReportingTo(emp.reportingTo, emp.reportingToEmpId)}</p>
                  </div>
                )}

                <div className="flex gap-2 mt-3">
                  <span
                    className={`text-xs px-2 py-0.5 rounded border ${
                      emp.employeeCategory === 'Vendor'
                        ? 'bg-purple-50 border-purple-200 text-purple-700'
                        : 'bg-slate-100 border-slate-200'
                    }`}
                  >
                    {emp.employeeCategory}
                  </span>
                  {emp.dutyType && (
                    <span className="text-xs px-2 py-0.5 rounded border bg-blue-50 border-blue-200 text-blue-700">
                      {emp.dutyType}
                    </span>
                  )}
                </div>
              </div>
              <div
                className={`p-2 rounded-full ${
                  emp.employeeCategory === 'Vendor' ? 'bg-purple-100' : 'bg-slate-100'
                }`}
              >
                {emp.employeeCategory === 'Vendor' ? (
                  <Truck className="w-4 h-4 text-purple-600" />
                ) : (
                  <Users className="w-4 h-4 text-slate-600" />
                )}
              </div>
            </div>
          </div>
        ))}

        {filteredEmployeesForCards.length === 0 && (
          <div className="col-span-full text-center text-slate-400 py-10">
            No employees found.
          </div>
        )}
      </div>
    </div>
  );
}


function AdminSites() {
  const [sites, setSites] = useState<Site[]>([]);

  // Keep form fields separate from the `Site` type (because form inputs are strings)
  type SiteForm = {
    siteCode: string;
    name: string;
    clientName: string;
    lat: string;
    lng: string;
    radius: string;
    address: string;
  };

  const [newSite, setNewSite] = useState<SiteForm>({
    siteCode: '',
    name: '',
    clientName: '',
    lat: '',
    lng: '',
    radius: '100',
    address: ''
  });
  const [showBulk, setShowBulk] = useState(false);
  const [bulkData, setBulkData] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState('');

  useEffect(() => {
    const q = collection(db, 'artifacts', appId, 'public', 'data', 'sites');
    const unsub = onSnapshot(q, (snap) => setSites(snap.docs.map(d => ({id: d.id, ...d.data()})) as Site[]));
    return () => unsub();
  }, []);

  const handleSaveSite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSite.name || !newSite.lat || !newSite.lng) return;
    
    const siteData = {
      siteCode: newSite.siteCode, name: newSite.name, clientName: newSite.clientName,
      latitude: parseFloat(newSite.lat),
      longitude: parseFloat(newSite.lng),
      radius: parseInt(newSite.radius || '100', 10),
      address: newSite.address
    };

    if (isEditing && editId) {
         await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'sites', editId), siteData);
         alert("Site Updated!");
    } else {
         await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'sites'), siteData);
         alert("Site Added!");
    }

    setNewSite({ siteCode: '', name: '', clientName: '', lat: '', lng: '', radius: '100', address: '' });
    setIsEditing(false);
    setEditId('');
  };

  const handleEditClick = (site: Site) => {
      setNewSite({
          siteCode: site.siteCode, name: site.name, clientName: site.clientName, 
          lat: site.latitude.toString(), lng: site.longitude.toString(), radius: site.radius.toString(), address: site.address
      });
      setEditId(site.id);
      setIsEditing(true);
      // Scroll to top logic could go here
  };

  const handleDownloadSample = () => {
      const headers = ['SiteCode', 'SiteName', 'ClientName', 'Latitude', 'Longitude', 'Radius', 'Address'];
      const rows = [['S001', 'HQ', 'Acme Corp', '28.6139', '77.2090', '100', '123 Main St']];
      downloadCSV('site_sample.csv', headers, rows);
  };

  const handleBulkSiteUpload = async () => {
    const lines = bulkData.trim().split('\n');
    let count = 0;
    const batchPromises = lines.map(async (line) => {
      const parts = line.split(',').map(s => s.trim());
      if (parts.length < 5) return;
      const [siteCode, name, clientName, lat, lng, radius, address] = parts;
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'sites'), {
        siteCode, name, clientName, latitude: parseFloat(lat), longitude: parseFloat(lng), radius: parseInt(radius || '100'), address: address || ''
      });
      count++;
    });
    await Promise.all(batchPromises);
    setBulkData('');
    setShowBulk(false);
    alert(`${count} Sites Uploaded!`);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center"><h2 className="text-2xl font-bold text-slate-800">Site Management</h2><button onClick={() => setShowBulk(!showBulk)} className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700 flex items-center gap-2"><Upload className="w-4 h-4" /> Bulk Sites</button></div>
       {showBulk && (
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm mb-6">
          <div className="flex justify-between items-center mb-2"><h3 className="font-semibold">Bulk Add Sites</h3><button onClick={handleDownloadSample} className="text-indigo-600 text-xs flex items-center gap-1 hover:underline"><Download className="w-3 h-3" /> Download Sample CSV</button></div>
          <textarea className="w-full h-32 p-3 border border-slate-300 rounded-lg font-mono text-sm" placeholder="Paste CSV data..." value={bulkData} onChange={(e) => setBulkData(e.target.value)} />
          <div className="flex justify-end mt-4"><button onClick={handleBulkSiteUpload} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm font-medium">Process Sites</button></div>
        </div>
      )}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex justify-between mb-4"><h3 className="font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> {isEditing ? 'Edit Site' : 'Add Single Site'}</h3>{isEditing && <button onClick={() => {setIsEditing(false); setNewSite({ siteCode: '', name: '', clientName: '', lat: '', lng: '', radius: '100', address: '' })}}><X className="w-4 h-4" /></button>}</div>
        <form onSubmit={handleSaveSite} className="grid grid-cols-1 md:grid-cols-3 gap-4"><input type="text" placeholder="Site Code" className="p-2 border rounded" value={newSite.siteCode} onChange={e => setNewSite({...newSite, siteCode: e.target.value})} /><input type="text" placeholder="Site Name" className="p-2 border rounded" value={newSite.name} onChange={e => setNewSite({...newSite, name: e.target.value})} /><input type="text" placeholder="Client Name" className="p-2 border rounded" value={newSite.clientName} onChange={e => setNewSite({...newSite, clientName: e.target.value})} /><input type="number" placeholder="Latitude" step="any" className="p-2 border rounded" value={newSite.lat} onChange={e => setNewSite({...newSite, lat: e.target.value})} /><input type="number" placeholder="Longitude" step="any" className="p-2 border rounded" value={newSite.lng} onChange={e => setNewSite({...newSite, lng: e.target.value})} /><input type="number" placeholder="Radius (m)" className="p-2 border rounded" value={newSite.radius} onChange={e => setNewSite({...newSite, radius: e.target.value})} /><input type="text" placeholder="Address" className="md:col-span-3 p-2 border rounded" value={newSite.address} onChange={e => setNewSite({...newSite, address: e.target.value})} /><div className="md:col-span-3 flex justify-end"><button type="submit" className="bg-indigo-600 text-white px-6 py-2 rounded">{isEditing ? 'Update Site' : 'Save Site'}</button></div></form></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{sites.map(site => (<div key={site.id} className="bg-white p-4 rounded-xl border border-slate-200 flex justify-between items-start relative">
        <button onClick={() => handleEditClick(site)} className="absolute top-4 right-4 text-slate-400 hover:text-indigo-600"><Pencil className="w-4 h-4" /></button>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-slate-800">{site.name}</h3>
            <span className="text-xs bg-slate-100 px-2 rounded text-slate-600">{site.siteCode}</span>
          </div>
          <p className="text-sm font-medium text-indigo-600">{site.clientName}</p>
          <p className="text-xs text-slate-500 font-mono mt-1">
            {typeof site.latitude === 'number' && !isNaN(site.latitude) ? site.latitude.toFixed(4) : 'N/A'}, {typeof site.longitude === 'number' && !isNaN(site.longitude) ? site.longitude.toFixed(4) : 'N/A'}
          </p>
          <p className="text-sm text-slate-600 mt-2">{site.address}</p>
        </div>
      </div>))}
    </div>
    </div>
  );
}

function AdminAllocation() {
  const [employees, setEmployees] = useState<UserProfile[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [selectedSiteIds, setSelectedSiteIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');

  useEffect(() => {
    const qUser = query(collection(db, 'artifacts', appId, 'public', 'data', 'users'), orderBy('name'));
    const unsubUser = onSnapshot(qUser, (snap) => setEmployees(snap.docs.map(d => ({id: d.id, ...d.data()})) as UserProfile[]));
    const qSite = query(collection(db, 'artifacts', appId, 'public', 'data', 'sites'), orderBy('name'));
    const unsubSite = onSnapshot(qSite, (snap) => setSites(snap.docs.map(d => ({id: d.id, ...d.data()})) as Site[]));
    return () => { unsubUser(); unsubSite(); };
  }, []);

  const handleEmpSelect = (empId: string) => {
    setSelectedEmpId(empId);
    const emp = employees.find(e => e.id === empId);
    if(emp && emp.allocatedSiteIds) setSelectedSiteIds(new Set(emp.allocatedSiteIds));
    else setSelectedSiteIds(new Set());
  };

  const toggleSite = (siteId: string) => {
    const newSet = new Set(selectedSiteIds);
    if(newSet.has(siteId)) newSet.delete(siteId); else newSet.add(siteId);
    setSelectedSiteIds(newSet);
  };

  const handleSave = async () => {
    if(!selectedEmpId) return;
    setIsSaving(true);
    try {
        const userRef = doc(db, 'artifacts', appId, 'public', 'data', 'users', selectedEmpId);
        await updateDoc(userRef, { allocatedSiteIds: Array.from(selectedSiteIds) });
        alert("Allocation Saved!");
    } catch(err) { console.error(err); alert("Error Saving"); }
    setIsSaving(false);
  };

  const filteredSites = sites.filter(site => 
    site.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    site.siteCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    site.clientName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredEmployees = useMemo(() => {
    const t = String(employeeSearchTerm || '').trim().toLowerCase();
    const base = employees.filter((e) => e.role === 'employee');
    const byKey = new Map<string, UserProfile>();
    for (const emp of base) {
      const empIdKey = String((emp as any).empId || '').trim().toUpperCase();
      const emailKey = String((emp as any).email || '').trim().toLowerCase();
      const key = empIdKey || emailKey || emp.id;
      if (!key) continue;
      if (!byKey.has(key)) byKey.set(key, emp);
    }
    const unique = Array.from(byKey.values());

    if (!t) return unique;
    return unique.filter((e) => {
      const name = String(e.name || '').toLowerCase();
      const empId = String((e as any).empId || '').toLowerCase();
      const designation = String((e as any).designation || '').toLowerCase();
      const department = String((e as any).department || '').toLowerCase();
      return (
        name.includes(t) ||
        empId.includes(t) ||
        designation.includes(t) ||
        department.includes(t)
      );
    });
  }, [employees, employeeSearchTerm]);

  return (
    <div className="h-[calc(100vh-150px)] flex flex-col md:flex-row gap-6">
        <div className="w-full md:w-1/3 bg-white p-6 rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Users className="w-5 h-5 text-indigo-600" /> Select Employee</h3>

          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search employees..."
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={employeeSearchTerm}
              onChange={(e) => setEmployeeSearchTerm(e.target.value)}
            />
          </div>

          <div className="overflow-y-auto flex-1 space-y-2 pr-2">
            {filteredEmployees.map(emp => (
              <button
                key={emp.id}
                onClick={() => handleEmpSelect(emp.id)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${selectedEmpId === emp.id ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-slate-200 hover:bg-slate-50'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-slate-800 truncate">{emp.name}</p>
                  {!!(emp as any).empId && (
                    <span className="text-[11px] font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded whitespace-nowrap">
                      {(emp as any).empId}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 truncate">{emp.designation}</p>
              </button>
            ))}
            {filteredEmployees.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center">No employees match your search.</div>
            )}
          </div>
        </div>
        <div className="w-full md:w-2/3 bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-slate-800 flex items-center gap-2"><Building className="w-5 h-5 text-indigo-600" /> Allocate Sites</h3>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input 
                  type="text" 
                  placeholder="Search sites..." 
                  className="pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button disabled={!selectedEmpId || isSaving} onClick={handleSave} className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">{isSaving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
          {!selectedEmpId ? (<div className="flex-1 flex items-center justify-center text-slate-400 bg-slate-50 rounded-lg border border-dashed border-slate-300"><p>Select an employee from the left to allocate sites.</p></div>) : (<div className="flex-1 overflow-y-auto"><div className="grid grid-cols-1 md:grid-cols-2 gap-3">{filteredSites.map(site => (<label key={site.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${selectedSiteIds.has(site.id) ? 'bg-green-50 border-green-200' : 'hover:bg-slate-50 border-slate-200'}`}><input type="checkbox" className="mt-1 w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500" checked={selectedSiteIds.has(site.id)} onChange={() => toggleSite(site.id)} /><div><p className="font-medium text-slate-800">{site.name}</p><p className="text-xs text-indigo-600 font-medium">{site.clientName}</p><p className="text-xs text-slate-500">{site.address}</p></div></label>))}</div></div>)}
        </div>
    </div>
  );
}

function AdminReports() {
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchUsers = async () => {
      const q = collection(db, 'artifacts', appId, 'public', 'data', 'users');
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }) as UserProfile);
  };

  const fetchAttendance = async (start: Date, end: Date) => {
      const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'attendance'), 
          where('timestamp', '>=', start),
          where('timestamp', '<=', end)
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => d.data() as AttendanceRecord);
  };

  const generateDailyReport = async () => {
      setIsProcessing(true);
      try {
          const users = await fetchUsers();
          const managerEmpIdByName = new Map(
            users
              .filter((u) => !!u.name)
              .map((u) => [(u.name || '').trim(), (u.empId || '').trim()])
          );
          const start = new Date(reportDate); start.setHours(0,0,0,0);
          const end = new Date(reportDate); end.setHours(23,59,59,999);
          const att = await fetchAttendance(start, end);

          const headers = ['Date', 'Department', 'EmpID', 'Name', 'ReportingTo', 'ReportingToEmpID', 'Designation', 'Category', 'DutyType', 'FirstIn', 'LastOut', 'TotalHrs', 'Status'];
          const rows = users.filter(u => u.role === 'employee').map(user => {
              const userAtt = att.filter(a => a.userId === user.id).sort((a,b) => a.timestamp - b.timestamp);
              const firstIn = userAtt.find(a => a.type === 'in')?.timestamp?.toDate().toLocaleTimeString() || '-';
              const lastOut = userAtt.reverse().find(a => a.type === 'out')?.timestamp?.toDate().toLocaleTimeString() || '-';
              
              let duration = '-';
              if(firstIn !== '-' && lastOut !== '-') duration = '8h 0m'; 

              return [
                  reportDate,
                  user.department,
                  user.empId,
                  user.name,
                  user.reportingTo || '',
                  user.reportingToEmpId || managerEmpIdByName.get((user.reportingTo || '').trim()) || '',
                  user.designation,
                  user.employeeCategory, user.dutyType, 
                  firstIn, lastOut, duration, userAtt.length > 0 ? 'Present' : 'Absent'
              ];
          });

          rows.sort((a,b) => String(a[1]).localeCompare(String(b[1])) || String(a[3]).localeCompare(String(b[3])));
          downloadCSV(`Daily_Payroll_Report_${reportDate}.csv`, headers, rows);

      } catch(e) { console.error(e); alert("Error generating report"); }
      setIsProcessing(false);
  };

  const generateMonthlyReport = async () => {
      setIsProcessing(true);
      try {
          const users = await fetchUsers();
          const managerEmpIdByName = new Map(
            users
              .filter((u) => !!u.name)
              .map((u) => [(u.name || '').trim(), (u.empId || '').trim()])
          );
          const [year, month] = reportMonth.split('-');
          const start = new Date(parseInt(year), parseInt(month)-1, 1);
          const end = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59); // Last day of month
          const att = await fetchAttendance(start, end);

          const headers = ['Month', 'Department', 'EmpID', 'Name', 'ReportingTo', 'ReportingToEmpID', 'Designation', 'Category', 'TotalPresentDays', 'Attendance%'];
          const totalDaysInMonth = end.getDate();

          const rows = users.filter(u => u.role === 'employee').map(user => {
              const userAtt = att.filter(a => a.userId === user.id);
              const uniqueDays = new Set(userAtt.map(a => a.timestamp.toDate().toDateString())).size;
              const percent = ((uniqueDays / totalDaysInMonth) * 100).toFixed(1) + '%';

              return [
                  reportMonth,
                  user.department,
                  user.empId,
                  user.name,
                  user.reportingTo || '',
                  user.reportingToEmpId || managerEmpIdByName.get((user.reportingTo || '').trim()) || '',
                  user.designation,
                  user.employeeCategory, uniqueDays, percent
              ];
          });

          rows.sort((a,b) => String(a[1]).localeCompare(String(b[1])) || String(a[3]).localeCompare(String(b[3])));
          downloadCSV(`Monthly_Payroll_Report_${reportMonth}.csv`, headers, rows);

      } catch(e) { console.error(e); alert("Error generating report"); }
      setIsProcessing(false);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-slate-800">Reports & Payroll</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-indigo-600" /> Daily Attendance Report</h3>
              <p className="text-sm text-slate-500 mb-4">Generates a detailed breakdown of In/Out times for a specific day, grouped by Department.</p>
              <div className="flex gap-2">
                  <input type="date" className="border p-2 rounded flex-1" value={reportDate} onChange={e => setReportDate(e.target.value)} />
                  <button onClick={generateDailyReport} disabled={isProcessing} className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50">{isProcessing ? '...' : 'Download CSV'}</button>
              </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-green-600" /> Monthly Payroll Summary</h3>
              <p className="text-sm text-slate-500 mb-4">Generates total present days and attendance percentage for the selected month.</p>
              <div className="flex gap-2">
                  <input type="month" className="border p-2 rounded flex-1" value={reportMonth} onChange={e => setReportMonth(e.target.value)} />
                  <button onClick={generateMonthlyReport} disabled={isProcessing} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50">{isProcessing ? '...' : 'Download CSV'}</button>
              </div>
          </div>
      </div>
    </div>
  );
}

// --- Tasks Module (Admin/Manager) ---
const AdminTasks = ({ profile }: { profile: UserProfile }) => {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [employees, setEmployees] = useState<UserProfile[]>([]);
    const [newTask, setNewTask] = useState({ title: '', description: '', assignedToIds: [] as string[], dueDate: '', targetSiteIds: [] as string[] });
    const [showForm, setShowForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sites, setSites] = useState<Site[]>([]);
    // New filter state for employee selection
    const [empFilter, setEmpFilter] = useState<'All' | 'Employee' | 'Vendor'>('All');

    useEffect(() => {
        // Fetch tasks
        let q;
        if (profile.role === 'admin') {
            q = query(collection(db, 'artifacts', appId, 'public', 'data', 'tasks'), orderBy('createdAt', 'desc'));
        } else {
            q = query(collection(db, 'artifacts', appId, 'public', 'data', 'tasks'), where('assignedBy', '==', profile.id)); // Managers only see their tasks
        }
        
        const unsub = onSnapshot(q, (snap) => {
            setTasks(snap.docs.map(d => ({id: d.id, ...d.data()})) as Task[]);
        });
        
        // Fetch employees
        const qEmp = collection(db, 'artifacts', appId, 'public', 'data', 'users');
        const unsubEmp = onSnapshot(qEmp, (snap) => {
            let allEmps = snap.docs.map(d => ({id: d.id, ...d.data()})) as UserProfile[];
            if (profile.role === 'manager') {
                allEmps = allEmps.filter(
                  (e) => e.reportingTo === profile.name || e.reportingToEmpId === profile.empId
                ); // Filter for Manager
            }
            setEmployees(allEmps);
        });

        // Fetch sites for dropdown
        const qSites = collection(db, 'artifacts', appId, 'public', 'data', 'sites');
        const unsubSites = onSnapshot(qSites, (snap) => {
            setSites(snap.docs.map(d => ({id: d.id, ...d.data()})) as Site[]);
        });

        return () => { unsub(); unsubEmp(); unsubSites(); };
    }, [profile]);

    const handleCreateTask = async (e: React.FormEvent) => {
        e.preventDefault();
        if(newTask.assignedToIds?.length === 0) return;

        const selectedEmployees = employees.filter(emp => newTask.assignedToIds?.includes(emp.id));
        
        // Find site name if selected
        const targetSiteNames = sites.filter(s => newTask.targetSiteIds?.includes(s.id)).map(s => s.name).join(', ');

        // Create a task for EACH selected employee
        const batchPromises = selectedEmployees.map(async (emp) => {
             await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'tasks'), {
                title: newTask.title,
                description: newTask.description,
                assignedTo: emp.id,
                assignedToName: emp.name,
                assignedBy: profile.id,
                assignedByName: profile.name,
                targetSiteIds: newTask.targetSiteIds, // Save Array
                targetSiteNames: targetSiteNames, // Save String for display
                status: 'Pending',
                dueDate: newTask.dueDate,
                createdAt: serverTimestamp()
            });
            
            // Send Notification to Employee
            await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'notifications'), {
                userId: emp.id,
                message: `New Task Assigned: ${newTask.title} by ${profile.name}`,
                type: 'info',
                read: false,
                timestamp: serverTimestamp()
            });
        });

        await Promise.all(batchPromises);

        setNewTask({ title: '', description: '', assignedToIds: [], dueDate: '', targetSiteIds: [] });
        setShowForm(false);
        alert(`Task assigned to ${selectedEmployees.length} people.`);
    };

    const toggleEmployeeSelection = (empId: string) => {
        const currentIds = newTask.assignedToIds || [];
        if (currentIds.includes(empId)) {
            setNewTask({ ...newTask, assignedToIds: currentIds.filter(id => id !== empId) });
        } else {
            setNewTask({ ...newTask, assignedToIds: [...currentIds, empId] });
        }
    };
    
    const toggleSiteSelection = (siteId: string) => {
        const currentIds = newTask.targetSiteIds || [];
        if (currentIds.includes(siteId)) {
            setNewTask({ ...newTask, targetSiteIds: currentIds.filter(id => id !== siteId) });
        } else {
            setNewTask({ ...newTask, targetSiteIds: [...currentIds, siteId] });
        }
    };

    const handleVerify = async (task: Task) => {
        if(window.confirm("Are you sure you want to CLOSE this task?\n\nThis will mark it as Verified/Closed. Ensure you have checked the proof.")) {
            try {
                // Update DB to Verified
                await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'tasks', task.id), {
                    status: 'Verified'
                });
                alert("Task Closed & Verified!");
            } catch(e) {
                console.error(e);
                alert("Error closing task.");
            }
        }
    };

    // --- NEW: Re-initiate (Re-open) Logic ---
    const handleReopen = async (task: Task) => {
        if(window.confirm("Re-open this task?\n\nIt will be set back to 'Pending' for the employee.")) {
             await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'tasks', task.id), {
                status: 'Pending',
                proofUrl: '', 
                proofUrls: [],
                proofPath: ''
            });
        }
    };

    // --- NEW: Cancel (Delete) Logic ---
    const handleCancel = async (task: Task) => {
        if(window.confirm("Delete this task permanently?")) {
            await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'tasks', task.id));
        }
    };

    // Filter employees based on search and category tabs
    const filteredEmployees = employees.filter(emp => {
        const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                              emp.empId.toLowerCase().includes(searchTerm.toLowerCase());
        
        if (empFilter === 'All') return matchesSearch;
        if (empFilter === 'Vendor') return matchesSearch && emp.employeeCategory === 'Vendor';
        return matchesSearch && emp.employeeCategory !== 'Vendor'; // Employee mode excludes vendors
    });

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-slate-800">Task Management</h2>
                <button onClick={() => setShowForm(!showForm)} className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"><Plus className="w-4 h-4" /> Assign Task</button>
            </div>

            {showForm && (
                <div className="bg-white p-6 rounded-xl border border-indigo-200 shadow-sm">
                    <div className="flex justify-between mb-4">
                        <h3 className="font-bold">Create New Task</h3>
                        <button onClick={() => setShowForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                             <input placeholder="Task Title" className="border p-2 rounded w-full" value={newTask.title} onChange={e=>setNewTask({...newTask, title: e.target.value})} />
                             <textarea placeholder="Description" className="border p-2 rounded w-full h-24" value={newTask.description} onChange={e=>setNewTask({...newTask, description: e.target.value})} />
                             
                             {/* Multi-Select Sites - Replaces Dropdown */}
                             <div className="border rounded p-2 h-40 overflow-y-auto bg-slate-50">
                                 <p className="text-xs font-bold text-slate-500 mb-2 uppercase sticky top-0 bg-slate-50 pb-1 border-b flex items-center gap-2"><MapIcon className="w-3 h-3"/> Select Sites (Optional)</p>
                                 {sites.length === 0 ? <p className="text-xs text-slate-400 p-2">No sites found.</p> : sites.map(s => (
                                     <label key={s.id} className="flex items-center gap-2 text-sm p-1.5 hover:bg-white cursor-pointer border-b border-slate-100 last:border-0 rounded">
                                         <input type="checkbox" checked={newTask.targetSiteIds?.includes(s.id) || false} onChange={() => toggleSiteSelection(s.id)} className="rounded text-indigo-600 focus:ring-indigo-500" />
                                         <span className="flex-1">{s.name} <span className="text-xs text-slate-400">({s.siteCode})</span></span>
                                     </label>
                                 ))}
                             </div>

                            <input type="date" className="border p-2 rounded w-full" value={newTask.dueDate} onChange={e=>setNewTask({...newTask, dueDate: e.target.value})} />
                        </div>

                        <div className="border rounded-lg p-4 flex flex-col h-96 bg-slate-50/50">
                             <div className="flex justify-between items-center mb-2">
                                <h4 className="font-semibold text-sm text-slate-600">Select Assignees</h4>
                                {/* Filter Tabs */}
                                <div className="flex text-xs bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                                    <button onClick={() => setEmpFilter('All')} className={`px-2 py-1 rounded ${empFilter === 'All' ? 'bg-white shadow text-indigo-600 font-medium' : 'text-slate-500 hover:text-indigo-600'}`}>All</button>
                                    <button onClick={() => setEmpFilter('Employee')} className={`px-2 py-1 rounded ${empFilter === 'Employee' ? 'bg-white shadow text-indigo-600 font-medium' : 'text-slate-500 hover:text-indigo-600'}`}>Staff</button>
                                    <button onClick={() => setEmpFilter('Vendor')} className={`px-2 py-1 rounded ${empFilter === 'Vendor' ? 'bg-white shadow text-purple-600 font-medium' : 'text-slate-500 hover:text-purple-600'}`}>Vendors</button>
                                </div>
                             </div>
                             
                             <div className="relative mb-2">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                                <input 
                                  type="text" 
                                  placeholder={`Search ${empFilter}...`}
                                  className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:border-indigo-300 bg-white"
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}
                                />
                             </div>
                             
                             <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                                 {filteredEmployees.length === 0 ? (
                                     <div className="text-center py-8 text-slate-400 text-sm">No matching {empFilter.toLowerCase()}s found.</div>
                                 ) : filteredEmployees.map(emp => (
                                     <label key={emp.id} className={`flex items-center gap-3 p-2 rounded border cursor-pointer transition-all ${newTask.assignedToIds?.includes(emp.id) ? 'bg-indigo-50 border-indigo-200 shadow-sm' : 'bg-white border-slate-100 hover:border-indigo-100'}`}>
                                         <input 
                                            type="checkbox" 
                                            className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                                            checked={newTask.assignedToIds?.includes(emp.id) || false}
                                            onChange={() => toggleEmployeeSelection(emp.id)}
                                         />
                                         <div className="flex-1">
                                             <div className="flex justify-between items-center">
                                                <p className="text-sm font-medium text-slate-800">{emp.name}</p>
                                                {emp.employeeCategory === 'Vendor' && <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1"><Truck className="w-3 h-3"/> Vendor</span>}
                                             </div>
                                             <p className="text-xs text-slate-500">{emp.empId} • {emp.designation || 'External'}</p>
                                         </div>
                                     </label>
                                 ))}
                             </div>
                             <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-indigo-600 font-medium text-right">
                                 {newTask.assignedToIds?.length} people selected
                             </div>
                        </div>
                    </div>
                    
                    <div className="flex justify-end mt-6 border-t pt-4">
                        <button onClick={handleCreateTask} disabled={!newTask.title || newTask.assignedToIds?.length === 0} className="bg-indigo-600 text-white px-6 py-2 rounded-lg shadow hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2">
                           <CheckCircle className="w-4 h-4" /> Assign Tasks
                        </button>
                    </div>
                </div>
            )}

            <div className="grid gap-4">
                {tasks.map(task => (
                    <div key={task.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center relative hover:shadow-md transition-all">
                         {/* Cancel Button (Top Right) */}
                         <button onClick={() => handleCancel(task)} className="absolute top-2 right-2 text-slate-300 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors" title="Cancel Task"><XCircle className="w-5 h-5" /></button>
                        
                        <div className="mb-4 md:mb-0 max-w-2xl pr-8">
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-bold text-lg text-slate-800">{task.title}</h3>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-bold tracking-wide ${
                                    task.status === 'Completed' ? 'bg-blue-100 text-blue-700' : 
                                    task.status === 'Verified' ? 'bg-green-100 text-green-700' : 
                                    task.status === 'In Progress' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-50 text-red-600'
                                }`}>{task.status}</span>
                            </div>
                            <p className="text-slate-600 text-sm mb-3 leading-relaxed">{task.description}</p>
                            
                            <div className="flex flex-wrap gap-4 text-xs text-slate-500 bg-slate-50 p-2 rounded-lg inline-flex border border-slate-100">
                                <span className="flex items-center gap-1"><UserCheck className="w-3 h-3 text-indigo-400"/> For: <strong>{task.assignedToName}</strong></span>
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-orange-400"/> Due: {task.dueDate}</span>
                            </div>
                            
                            {task.targetSiteNames && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {task.targetSiteNames.split(',').map((site, idx) => (
                                        <span key={idx} className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                                            <MapPin className="w-3 h-3"/> {site.trim()}
                                        </span>
                                    ))}
                                </div>
                            )}
                             
                             {task.proofRemarks && (
                                <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-100 text-xs text-slate-700">
                                    <strong className="text-yellow-700 block mb-1">📢 Employee Remarks:</strong> {task.proofRemarks}
                                </div>
                            )}
                        </div>
                        
                        <div className="flex gap-2 items-center self-start md:self-center">
                             {/* Re-open Button (for Completed/Verified tasks) */}
                             {(task.status === 'Completed' || task.status === 'Verified') && (
                                <button onClick={() => handleReopen(task)} className="text-orange-500 hover:bg-orange-50 hover:text-orange-600 p-2 rounded-lg border border-transparent hover:border-orange-200 transition-all" title="Re-open Task">
                                    <RotateCcw className="w-5 h-5" />
                                </button>
                             )}

                            {task.status === 'Completed' && (
                                <div className="flex flex-col items-end gap-2 bg-indigo-50/50 p-3 rounded-xl border border-indigo-100">
                                    <p className="text-xs font-bold text-indigo-800 mb-1 uppercase tracking-wider">Evidence</p>
                                    
                                    {/* Multiple Proof Links */}
                                    <div className="flex gap-1 flex-wrap justify-end max-w-[200px]">
                                        {task.proofUrls && task.proofUrls.length > 0 ? (
                                            task.proofUrls.map((url, idx) => (
                                                <a key={idx} href={url} target="_blank" rel="noreferrer" className="text-indigo-600 text-xs bg-white border border-indigo-200 px-2 py-1 rounded hover:bg-indigo-600 hover:text-white transition-colors">
                                                    Media {idx + 1}
                                                </a>
                                            ))
                                        ) : (
                                            // Fallback for legacy single proof
                                            task.proofUrl && <a href={task.proofUrl} target="_blank" rel="noreferrer" className="text-indigo-600 text-xs bg-white border px-2 py-1 rounded">View Media</a>
                                        )}
                                    </div>

                                    <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-1 font-mono bg-white px-1 rounded border"><MapIcon className="w-3 h-3"/> {task.proofLocation}</div>
                                    <button onClick={() => handleVerify(task)} className="bg-green-600 text-white px-4 py-1.5 rounded-lg text-xs font-bold hover:bg-green-700 shadow-sm mt-2 w-full flex items-center justify-center gap-2">
                                        <FileCheck className="w-3 h-3"/> Approve & Close
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {tasks.length === 0 && (
                    <div className="text-center py-12 bg-white rounded-xl border border-dashed border-slate-300">
                        <CheckSquare className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                        <p className="text-slate-400">No tasks assigned yet.</p>
                        <p className="text-xs text-slate-300 mt-1">Create a new task to get started</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const EmployeeMobileApp = ({ profile, onLogout }: { profile: UserProfile, onLogout: () => void }) => {
  const isManager = profile.role === 'manager';
  const [currentView, setCurrentView] = useState<'home' | 'tasks' | 'history' | 'requests' | 'conveyance' | 'salary' | 'manager'>('home');
  const [managerView, setManagerView] = useState<'approvals' | 'team_tasks'>('approvals');
  const [status, setStatus] = useState<'in' | 'out'>('out');
  const [loadingLoc, setLoadingLoc] = useState(false);
  const [offlineQueue, setOfflineQueue] = useState<any[]>([]);
  const [isSimulatedOffline, setIsSimulatedOffline] = useState(false);
  const [todaysPunches, setTodaysPunches] = useState<AttendanceRecord[]>([]);
  const [myTasks, setMyTasks] = useState<Task[]>([]);
  const [uploading, setUploading] = useState(false);

  // Temp state for task uploading
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [tempProofs, setTempProofs] = useState<File[]>([]); // Store multiple files

  // Fetch Data
  useEffect(() => {
    const saved = localStorage.getItem('offlineQueue');
    if (saved) setOfflineQueue(JSON.parse(saved));

    // Punches
    const qPunch = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'attendance'),
      where('userId', '==', profile.id),
      where('timestamp', '>=', new Date(new Date().setHours(0, 0, 0, 0))),
      where('timestamp', '<=', new Date(new Date().setHours(23, 59, 59, 999))),
      orderBy('timestamp', 'asc')
    );

    const unsubPunch = onSnapshot(qPunch, (snap) => {
      const recs = snap.docs.map(d => ({ id: d.id, ...d.data() } as any)) as AttendanceRecord[];
      setTodaysPunches(recs);
      if (recs.length > 0) {
        setStatus(recs[recs.length - 1].type === 'in' ? 'in' : 'out');
      }
    });

    // Tasks
    const qTask = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'tasks'),
      where('assignedToIds', 'array-contains', profile.id)
    );

    const unsubTask = onSnapshot(qTask, (snap) => {
      setMyTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })) as Task[]);
    });

    return () => { unsubPunch(); unsubTask(); };
  }, [profile.id]);

  // Add a photo to temp list
  const handleAddPhoto = (e: any) => {
    if (e.target.files && e.target.files[0]) {
      setTempProofs([...tempProofs, e.target.files[0]]);
    }
  };

  // Submit all photos for the task
  const handleSubmitTask = async () => {
    if (!selectedTask || tempProofs.length === 0) return;

    const remarks = prompt("Enter remarks for this task update (Optional):");
    if (remarks === null) return; // User cancelled

    setUploading(true);

    // 1. Get GPS
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      const locationText = `Lat: ${latitude.toFixed(5)}, Lng: ${longitude.toFixed(5)}`;

      try {
        // 2. Upload files one by one
        const uploadedUrls: string[] = [];
        for (let i = 0; i < tempProofs.length; i++) {
          const file = tempProofs[i];
          const refPath = `task-proofs/${selectedTask.id}/${profile.id}/${Date.now()}_${file.name}`;
          const fileRef = ref(storage, refPath);
          await uploadBytes(fileRef, file);
          const url = await getDownloadURL(fileRef);
          uploadedUrls.push(url);
        }

        // 3. Save task proof document
        await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'taskProofs'), {
          taskId: selectedTask.id,
          userId: profile.id,
          userName: profile.name,
          empId: profile.empId,
          remarks: remarks || '',
          locationText,
          proofUrls: uploadedUrls,
          createdAt: serverTimestamp(),
          createdBy: profile.id,
        });

        // 4. Update task status
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'tasks', selectedTask.id), {
          status: 'Completed',
          lastUpdateAt: serverTimestamp(),
        });

        alert('Task proof uploaded successfully!');
        setSelectedTask(null);
        setTempProofs([]);
      } catch (err) {
        console.error(err);
        alert('Failed to upload task proof.');
      } finally {
        setUploading(false);
      }
    }, (err) => {
      console.error(err);
      alert('Unable to fetch GPS location.');
      setUploading(false);
    });
  };

  const addToOfflineQueue = (record: any) => {
    const updatedQueue = [...offlineQueue, record];
    setOfflineQueue(updatedQueue);
    localStorage.setItem('offlineQueue', JSON.stringify(updatedQueue));
  };

  const syncOfflineQueue = async () => {
    if (offlineQueue.length === 0) return;
    const queue = [...offlineQueue];
    setOfflineQueue([]);
    localStorage.removeItem('offlineQueue');
    for (const rec of queue) {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'attendance'), rec);
    }
    alert(`Synced ${queue.length} offline punches!`);
  };

  const handlePunch = () => {
    if (isSimulatedOffline) {
  const rec: any = {
    userId: profile.id,
    empId: profile.empId,
    userName: profile.name,   // 👈 use userName, not name
    type: status === 'in' ? 'out' : 'in',
    timestamp: new Date(),
    latitude: 0,              // no real GPS, keep 0
    longitude: 0,
    locationText: 'Offline Mode',
    deviceType: 'mobile',
  };
  addToOfflineQueue(rec);
  setStatus(status === 'in' ? 'out' : 'in');
  return;
}


    setLoadingLoc(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      const locationText = `Lat: ${latitude.toFixed(5)}, Lng: ${longitude.toFixed(5)}`;
        const rec: any = {
    userId: profile.id,
    empId: profile.empId,
    userName: profile.name,   // 👈 match AttendanceRecord field
    type: status === 'in' ? 'out' : 'in',
    timestamp: new Date(),
    latitude,                 // 👈 store numeric GPS
    longitude,
    locationText,             // nice readable string
    deviceType: 'mobile',
  };

      try {
        await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'attendance'), rec);
        setStatus(status === 'in' ? 'out' : 'in');
      } catch (err) {
        console.error(err);
        alert('Failed to punch. Stored offline.');
        addToOfflineQueue(rec);
      } finally {
        setLoadingLoc(false);
      }
    }, (err) => {
      console.error(err);
      alert('Unable to fetch location');
      setLoadingLoc(false);
    });
  };

  const getButtonText = () => {
    if (loadingLoc) return 'Getting Location...';
    return status === 'in' ? 'Punch Out' : 'Punch In';
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col pb-24">
      <header className="bg-indigo-600 text-white">
        <div className="max-w-6xl mx-auto w-full p-4 flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2">
              <img
                src={`${process.env.PUBLIC_URL}/Company_logo.png`}
                alt="Alert Intell"
                className="w-6 h-6 object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
              <div className="text-xs text-indigo-100">Alert Intell</div>
            </div>
            <div className="font-semibold flex items-center gap-2">
              {profile.name}
              <span className="text-[10px] bg-indigo-500 px-2 py-0.5 rounded-full">{profile.empId}</span>
            </div>
            <div className="text-[11px] text-indigo-100 mt-1">
              {profile.designation || 'Employee'} · {profile.department || 'Department'}
            </div>
          </div>
          <div className="text-right">
            <button
              onClick={onLogout}
              className="text-[11px] text-indigo-100 underline mb-1"
            >
              Logout
            </button>
            <div className="flex items-center gap-2 text-xs justify-end">
              <span className={`w-2 h-2 rounded-full ${isSimulatedOffline ? 'bg-orange-400' : 'bg-emerald-300'}`}></span>
              <span>{isSimulatedOffline ? 'Offline (Simulated)' : 'Online'}</span>
            </div>
            {offlineQueue.length > 0 && (
              <button
                onClick={syncOfflineQueue}
                className="mt-1 text-[11px] px-2 py-0.5 bg-amber-400 text-slate-800 rounded-full"
              >
                Sync {offlineQueue.length} punches
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 px-4 py-3 max-w-6xl mx-auto w-full">
        {currentView === 'home' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-200">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <div className="text-xs text-slate-500">Current Status</div>
                  <div className="text-lg font-bold text-slate-800">
                    {status === 'in' ? 'On Duty' : 'Off Duty'}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <button
                    onClick={() => setIsSimulatedOffline(!isSimulatedOffline)}
                    className="text-[10px] px-2 py-0.5 rounded-full border border-slate-200 text-slate-500"
                  >
                    {isSimulatedOffline ? 'Disable Offline Mode' : 'Simulate Offline'}
                  </button>
                </div>
              </div>

              <button
                onClick={handlePunch}
                disabled={loadingLoc}
                className={`w-full py-3 rounded-full text-white font-semibold flex items-center justify-center gap-2 ${
                  status === 'in' ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-500 hover:bg-emerald-600'
                } ${loadingLoc && 'opacity-75 cursor-not-allowed'}`}
              >
                {loadingLoc ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                {getButtonText()}
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-200">
              <div className="flex justify-between items-center mb-2">
                <div className="font-semibold text-slate-800 text-sm">Today&apos;s Punches</div>
                <div className="text-[10px] text-slate-400">
                  {new Date().toLocaleDateString()}
                </div>
              </div>
              {todaysPunches.length === 0 && (
                <div className="text-xs text-slate-400">No punches recorded today.</div>
              )}
              <div className="space-y-2 mt-2">
                {todaysPunches.map((rec) => (
                  <div key={rec.id} className="flex justify-between items-center text-xs">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          rec.type === 'in' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {rec.type === 'in' ? 'IN' : 'OUT'}
                      </span>
                      <span className="text-slate-600">
                        {rec.timestamp.toDate().toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-400 max-w-[140px] text-right truncate">
                      {rec.locationText}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {currentView === 'history' && (
          <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-200">
            <div className="font-semibold text-slate-800 text-sm mb-2">Attendance History (Today)</div>
            {todaysPunches.length === 0 && (
              <div className="text-xs text-slate-400">No punches recorded today.</div>
            )}
            <div className="space-y-2 mt-2 max-h-[300px] overflow-y-auto">
              {todaysPunches.map((rec) => (
                <div key={rec.id} className="flex justify-between items-start text-xs border-b border-slate-100 pb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          rec.type === 'in' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                        }`}
                      >
                        {rec.type === 'in' ? 'Punched In' : 'Punched Out'}
                      </span>
                      <span className="text-slate-500">
                        {rec.timestamp.toDate().toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      {rec.locationText}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {currentView === 'tasks' && (
          <div className="space-y-4">
            {!selectedTask && (
              <div className="flex justify-between items-center">
                <div className="font-semibold text-slate-800 text-sm">My Tasks</div>
                <div className="text-[11px] text-slate-400">
                  Pending: {myTasks.filter(t => t.status === 'Pending').length}
                </div>
              </div>
            )}

            {/* Task Proof Upload View */}
            {selectedTask && (
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-bold text-slate-800 text-sm">Update Task</h4>
                  <button
                    onClick={() => { setSelectedTask(null); setTempProofs([]); }}
                    className="text-xs text-slate-400 hover:text-slate-600"
                  >
                    Cancel
                  </button>
                </div>
                <div className="mb-2 text-sm text-slate-700">{selectedTask.title}</div>
                <div className="mb-3 text-xs text-slate-500">{selectedTask.description}</div>
                {selectedTask.targetSiteNames && (
                  <div className="mb-3 text-xs text-indigo-600 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> At: {selectedTask.targetSiteNames}
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-slate-600 font-medium mb-1 block">
                      Attach Proof Photos (you can select multiple one by one)
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple={false}
                      onChange={handleAddPhoto}
                      className="text-xs"
                    />
                    {tempProofs.length > 0 && (
                      <div className="mt-2 text-[11px] text-slate-500">
                        {tempProofs.length} file(s) selected.
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleSubmitTask}
                    disabled={uploading || tempProofs.length === 0}
                    className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-bold disabled:opacity-50 flex justify-center gap-2"
                  >
                    {uploading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                    {uploading ? 'Uploading...' : `Submit ${tempProofs.length} Files`}
                  </button>
                </div>
              </div>
            )}

            {/* Tasks List */}
            {!selectedTask && (
              <div className="space-y-3">
                {myTasks.map(task => (
                  <div key={task.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-bold text-slate-800">{task.title}</h4>
                      <span
                        className={`text-[10px] px-2 py-1 rounded ${
                          task.status === 'Pending'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {task.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mb-3">{task.description}</p>
                    {task.targetSiteNames && (
                      <p className="text-xs text-indigo-600 mb-2 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> At: {task.targetSiteNames}
                      </p>
                    )}
                    <div className="text-xs text-slate-400 mb-3">Due: {task.dueDate}</div>

                    {task.status !== 'Completed' && task.status !== 'Verified' && !selectedTask && (
                      <button
                        onClick={() => setSelectedTask(task)}
                        className="w-full bg-indigo-600 text-white py-2 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-indigo-700"
                      >
                        <Camera className="w-4 h-4" /> Update Task
                      </button>
                    )}
                    {(task.status === 'Completed' || task.status === 'Verified') && (
                      <div className="text-xs text-center text-green-600 font-medium bg-green-50 p-2 rounded mt-2">
                        Proof Submitted
                      </div>
                    )}
                  </div>
                ))}
                {myTasks.length === 0 && (
                  <div className="text-center text-slate-400 mt-10">No tasks assigned.</div>
                )}
                <button
                  onClick={() => setCurrentView('home')}
                  className="w-full text-slate-500 py-4"
                >
                  Back to Home
                </button>
              </div>
            )}
          </div>
        )}

        {currentView === 'requests' && (
          <div className="space-y-6">
            <EmployeeLeaveRequests profile={profile} />
            <EmployeeExpenses profile={profile} />
            <EmployeeFundRequests profile={profile} />
            <EmployeeTravelRequests profile={profile} />
          </div>
        )}

        {currentView === 'conveyance' && (
          <EmployeeLocalConveyance profile={profile} />
        )}

        {currentView === 'salary' && (
          <EmployeeSalarySlips profile={profile} />
        )}

        {currentView === 'manager' && isManager && (
          <div className="space-y-6">
            <div className="bg-white border rounded-xl p-3">
              <div className="flex gap-2">
                <button
                  onClick={() => setManagerView('approvals')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold ${managerView === 'approvals' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700'}`}
                >
                  Approvals
                </button>
                <button
                  onClick={() => setManagerView('team_tasks')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold ${managerView === 'team_tasks' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700'}`}
                >
                  Team Tasks
                </button>
              </div>
            </div>

            {managerView === 'approvals' && (
              <div className="space-y-8">
                <AdminApprovals profile={profile} />
                <AdminLeaveApprovals profile={profile} />
                <AdminExpenseApprovals profile={profile} />
                <AdminTravelApprovals profile={profile} />
                <AdminFundApprovals profile={profile} />
                <AdminLocalConveyanceApprovals profile={profile} />
              </div>
            )}

            {managerView === 'team_tasks' && (
              <AdminTasks profile={profile} />
            )}
          </div>
        )}
      </div>

      {/* Bottom nav (sticky) */}
      <div className="bg-white border-t border-slate-200 fixed bottom-0 left-0 right-0 w-full z-30">
        <div className={`max-w-6xl mx-auto w-full px-2 py-3 grid ${isManager ? 'grid-cols-7' : 'grid-cols-6'}`}>
          <button
            onClick={() => setCurrentView('home')}
            className={`flex flex-col items-center ${
              currentView === 'home' ? 'text-indigo-600' : 'text-slate-400'
            }`}
          >
            <Clock className="w-6 h-6" />
            <span className="text-[10px] font-medium mt-1">Punch</span>
          </button>
          <button
            onClick={() => setCurrentView('tasks')}
            className={`flex flex-col items-center ${
              currentView === 'tasks' ? 'text-indigo-600' : 'text-slate-400'
            }`}
          >
            <CheckSquare className="w-6 h-6" />
            <span className="text-[10px] font-medium mt-1">Tasks</span>
          </button>
          <button
            onClick={() => setCurrentView('history')}
            className={`flex flex-col items-center ${
              currentView === 'history' ? 'text-indigo-600' : 'text-slate-400'
            }`}
          >
            <FileText className="w-6 h-6" />
            <span className="text-[10px] font-medium mt-1">History</span>
          </button>
          <button
            onClick={() => setCurrentView('requests')}
            className={`flex flex-col items-center ${
              currentView === 'requests' ? 'text-indigo-600' : 'text-slate-400'
            }`}
          >
            <FileCheck className="w-6 h-6" />
            <span className="text-[10px] font-medium mt-1">Requests</span>
          </button>
          <button
            onClick={() => setCurrentView('conveyance')}
            className={`flex flex-col items-center ${
              currentView === 'conveyance' ? 'text-indigo-600' : 'text-slate-400'
            }`}
          >
            <Truck className="w-6 h-6" />
            <span className="text-[10px] font-medium mt-1">Conv</span>
          </button>
          <button
            onClick={() => setCurrentView('salary')}
            className={`flex flex-col items-center ${
              currentView === 'salary' ? 'text-indigo-600' : 'text-slate-400'
            }`}
          >
            <Download className="w-6 h-6" />
            <span className="text-[10px] font-medium mt-1">Salary</span>
          </button>

          {isManager && (
            <button
              onClick={() => setCurrentView('manager')}
              className={`flex flex-col items-center ${
                currentView === 'manager' ? 'text-indigo-600' : 'text-slate-400'
              }`}
            >
              <UserCheck className="w-6 h-6" />
              <span className="text-[10px] font-medium mt-1">Manager</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
